/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// Tocca must dispatch native swipe events because theme components use addEventListener.
window.tocca?.({ useJquery: false });

/*
 * ------------------------------------------------------------------------------------------------------------
 * Custom GTAG tracking events
 * ------------------------------------------------------------------------------------------------------------
 */

// GTAG custom tracking for various user interactions
document.addEventListener("DOMContentLoaded", () => {
  const selectors = {
    sortingContainerDesktop: ".product-facet__meta-bar-item.product-facet__meta-bar-item--sort",
    sortingContainerMobile: "#mobile-facet-toolbar",
    toggleButton: "button[is='toggle-button']",
    sortContent: "#sort-by-popover",
    sortContentItem: ".popover__choice-item",
    sortContentItemLabel: ".popover__choice-label",
    filterContent: "#facet-filters-form",
    filterContentItemCheckbox: ".checkbox-container",
    filterContentItemBlock: ".block-swatch",
    filterContentItemColorSwatch: ".color-swatch__item",
    filterContentItemLabel: "label",
    filterContentItemColorSwatchLabel: "label span",
    productRecommendationsContainer: ".shopify-section--product-recommendations",
    productRecommendationsItem: ".product-item",
    quickBuyDrawer: "quick-buy-drawer",
    mainProductContainer: ".shopify-section--main-product",
    mainProductZoomButton: ".product__zoom-button",
    sizeGuideButton: ".product-form__option-link",
    productTabs: ".product-tabs",
    productTabItem: ".collapsible-toggle",
    headerNavigation: ".header__inline-navigation",
    burgerMenuCloseButton: ".drawer__close-button",
    mobileMenuDrawer: "#mobile-menu-drawer",
    mobileMenuDrawerLink: ".mobile-nav__link",
    headerLink: ".header-link",
    languageSwitch: ".language-switch span"
  };

  document.addEventListener("click", (event) => {
    const target = event.target;

    if (target.closest(selectors.headerLink)) {
      window.tfcanalytics.trackEvent("navigation_click", {
        label: target.closest(selectors.headerLink).innerText
      });
    }

    if (target.closest(selectors.sortingContainerDesktop)) {
      if (target.closest(selectors.toggleButton)) {
        window.tfcanalytics.trackEvent("open_sorting");
      }
    }

    if (target.closest(selectors.sortingContainerMobile)) {
      if (target.closest(selectors.toggleButton).classList.contains("mobile-toolbar__item--sort")) {
        window.tfcanalytics.trackEvent("open_sorting");
      } else if (target.closest(selectors.toggleButton).classList.contains("mobile-toolbar__item--filters")) {
        window.tfcanalytics.trackEvent("open_filter");
      }
    }

    if (target.closest(selectors.sortContent)) {
      if (target.closest(selectors.sortContentItem) && !target.closest(selectors.sortContentItem).hasAttribute('data-handled')) {
        const item = target.closest(selectors.sortContentItem);

        if (!item) return null;

        const value = item.querySelector(selectors.sortContentItemLabel);

        window.tfcanalytics.trackEvent("select_sorting", {
          sort_by: value.innerText
        });
        item.setAttribute('data-handled', 'true');
      }
    }

    if (target.closest(selectors.filterContent)) {
      if (target.closest(selectors.filterContentItemCheckbox) && !target.closest(selectors.filterContentItemCheckbox).hasAttribute('data-handled')) {
        const item = target.closest(selectors.filterContentItemCheckbox);

        if (!item) return null;

        const value = item.querySelector(selectors.filterContentItemLabel);

        window.tfcanalytics.trackEvent("select_filter", {
          filter_by: value.innerText
        });
        item.setAttribute('data-handled', 'true');
      } else if (target.closest(selectors.filterContentItemBlock) && !target.closest(selectors.filterContentItemBlock).hasAttribute('data-handled')) {
        const item = target.closest(selectors.filterContentItemBlock);

        if (!item) return null;

        const value = item.querySelector(selectors.filterContentItemLabel);

        window.tfcanalytics.trackEvent("select_filter", {
          filter_by: value.innerText
        });
        item.setAttribute('data-handled', 'true');
      } else if (target.closest(selectors.filterContentItemColorSwatch) && !target.closest(selectors.filterContentItemColorSwatch).hasAttribute('data-handled')) {
        const item = target.closest(selectors.filterContentItemColorSwatch);

        if (!item) return null;

        const value = item.querySelector(selectors.filterContentItemColorSwatchLabel);

        window.tfcanalytics.trackEvent("select_filter", {
          filter_by: value.innerText
        });
        item.setAttribute('data-handled', 'true');
      }
    }

    if (target.closest(selectors.productRecommendationsContainer)) {
      const recommendationsItem = target.closest(selectors.productRecommendationsItem);
      const quickViewButton = target.closest(selectors.toggleButton);
      const quickBuyDrawer = target.closest(selectors.quickBuyDrawer);

      if (recommendationsItem && !quickViewButton && !quickBuyDrawer) {
        const productId = recommendationsItem.dataset.id;

        window.tfcanalytics.trackEvent("click_pdp_reco", {
          productId: productId
        });
      }

      if (quickViewButton) {
        event.stopPropagation();
        event.stopImmediatePropagation();
        const productId = quickViewButton.closest(selectors.productRecommendationsItem).dataset.id;

        window.tfcanalytics.trackEvent("click_pdp_reco_quickview", {
          productId: productId
        });
      }
    }

    if (target.closest(selectors.mainProductContainer)) {
      if (target.closest(selectors.mainProductZoomButton)) {
        window.tfcanalytics.trackEvent("view_zoom");
      }

      if (target.closest(selectors.sizeGuideButton)) {
        window.tfcanalytics.trackEvent("view_sizeguide");
      }

      if (target.closest(selectors.productTabs)) {
        const productTabItem = target.closest(selectors.productTabItem);

        if (!productTabItem) return null;

        const check = productTabItem.getAttribute("aria-expanded") === "true";

        if (productTabItem && check) {
          window.tfcanalytics.trackEvent("accordeon_interaction", {
            label: productTabItem.innerText
          });
        }
      }
    }

    if (target.closest(selectors.languageSwitch)) {
      window.tfcanalytics.trackEvent("language_switch", {
        to: target.closest(selectors.languageSwitch).innerText
      });
    }

    if (target.closest(selectors.headerNavigation)) {
      const toggleButton = target.closest(selectors.toggleButton);

      if (!toggleButton) return null;

      const toggleButtonAriaControls = toggleButton.getAttribute("aria-controls") === "mobile-menu-drawer";

      if (toggleButton && toggleButtonAriaControls) {
        window.tfcanalytics.trackEvent("burger_click");
      }
    }

    if (target.closest(selectors.mobileMenuDrawer)) {
      if (target.closest(selectors.burgerMenuCloseButton)) {
        window.tfcanalytics.trackEvent("burger_close");
      }

      if (target.closest(selectors.mobileMenuDrawerLink)) {
        window.tfcanalytics.trackEvent("navigation_click", {
          label: target.closest(selectors.mobileMenuDrawerLink).innerText
        });
      }
    }
  });

  let check = false;

  function isElementInView(element) {
    if (!element) return null;

    const bounding = element.getBoundingClientRect();

    return (
      bounding.top < window.innerHeight
    );
  }

  const recommendedProducts = document.querySelector(selectors.productRecommendationsContainer);
  function checkIfInView() {
    if (isElementInView(recommendedProducts) && !check) {
      check = true;
      window.tfcanalytics.trackEvent("view_pdp_reco");
      window.removeEventListener("scroll", checkIfInView);
    }
  }

  window.addEventListener("scroll", checkIfInView);

});


// GTAG custom tracking for product-item visibility
const productItems = document.querySelectorAll('product-item[data-id]');
const trackedItems = new Set(); // product IDs, that have already been tracked; prevents double-tracking

const observerOptions = {
  root: null,              // viewport
  threshold: 1.0           // 1.0 = fully visible
};

// tracking shortly paused during AJAX pagination to avoid tracking invisible items
let trackingPaused = false;
let trackingResumeTimeout = null;
let trackingIgnoreUntil = 0; // timestamp until which observer entries are ignored

function handleProductInView(entries, observer) {
  if (trackingPaused) return;
  if (Date.now() < trackingIgnoreUntil) return;
  entries.forEach(entry => {
    if (entry.isIntersecting && entry.intersectionRatio === 1) {
      const item = entry.target;
      const id = item.getAttribute('data-id');

      if (!trackedItems.has(id)) {
        window.tfcanalytics.trackEvent("view_productitem", {
          productId: id
        });
        trackedItems.add(id);
      }

      observer.unobserve(item);
    }
  });
}

const observer = new IntersectionObserver(handleProductInView, observerOptions);

// watch all product-item elements
productItems.forEach(item => observer.observe(item));

// NOTE: we rely only on `theme:loading:end` to re-init observation after AJAX pagination.
document.addEventListener('theme:loading:end', () => {
  trackingPaused = true;
  if (trackingResumeTimeout) clearTimeout(trackingResumeTimeout);
  try { observer.disconnect(); } catch { /* ignore */ }
  const newItems = document.querySelectorAll('product-item[data-id]');
  newItems.forEach(item => {
    const id = item.getAttribute('data-id');
    if (!id) return;
    if (trackedItems.has(id)) return;
    observer.observe(item);
  });
  // Ignore observer entries for a short window to avoid tracking during automatic scroll.
  const IGNORE_MS = 700;
  trackingIgnoreUntil = Date.now() + IGNORE_MS;
  // Ensure we eventually re-enable tracking
  trackingResumeTimeout = setTimeout(() => {
    trackingPaused = false;
    trackingIgnoreUntil = 0;
  }, IGNORE_MS + 50);
});


/* oss alternative products */
class OssAlternative extends HTMLElement {
  constructor() {
    super();
    console.log("OssAlternative constructor");
    setTimeout(() => {
      document.addEventListener("variant:changed", (e) => {
        const variant = e.detail.variant;
        console.log(variant, variant.available === true);
        if (variant.available) {
          this.classList.remove("oss-alternative--show");
        } else {
          this.classList.add("oss-alternative--show");
        }
      });
    }, 1000);
  }
}

if (!customElements.get("oss-alternative")) {
  window.customElements.define("oss-alternative", OssAlternative);
}
/* oss alternative products END */

/* SC Start */
function scParseBoolean(scValue) {
  return scValue === true || scValue === 'true' || scValue === '1';
}

function scParseInteger(scValue, scFallbackValue = 0) {
  const scParsedValue = parseInt(scValue, 10);
  return Number.isFinite(scParsedValue) ? scParsedValue : scFallbackValue;
}

function scCloneFiltersConfiguration(scFiltersConfiguration) {
  if (!scFiltersConfiguration || typeof scFiltersConfiguration !== 'object') {
    return { options: {} };
  }

  try {
    return JSON.parse(JSON.stringify(scFiltersConfiguration));
  } catch {
    return { options: {} };
  }
}

function scToggleHidden(scElement, scHidden) {
  if (!scElement) return;

  if (scHidden) {
    scElement.setAttribute('hidden', '');
    scElement.style.display = 'none';
  } else {
    scElement.removeAttribute('hidden');
    scElement.style.removeProperty('display');
  }
}

function scRefreshDiscountShowcases() {
  if (window.discountShowcaseInstance && typeof window.discountShowcaseInstance.updateAllShowcases === 'function') {
    Promise.resolve(window.discountShowcaseInstance.updateAllShowcases()).catch((scDiscountShowcaseError) => {
      console.error('[SC] Discount showcase refresh failed.', scDiscountShowcaseError);
    });
    return;
  }

  // Fallback to theme event hook if instance is not ready yet.
  document.documentElement.dispatchEvent(new CustomEvent('product-facet:rerender', { bubbles: true }));
}

async function scRefreshFeaturedCollectionProductListState(scProductListElement) {
  if (!scProductListElement) return;

  const scSavedOptionFilters = scProductListElement.filterable
    ? scCloneFiltersConfiguration(scProductListElement.activeFilters)
    : null;

  scProductListElement.productListInner = scProductListElement.querySelector('[data-sc-products-inner]');
  scProductListElement.productItems = Array.from(scProductListElement.querySelectorAll('product-item'));
  scProductListElement.productDataMounted = false;

  if (scProductListElement.filterable && typeof scProductListElement.renderFilterControls === 'function') {
    scProductListElement.availableOptionFilters = {};
    if (scProductListElement.filterContainer) {
      scProductListElement.filterContainer.innerHTML = '';
    }
    await scProductListElement.renderFilterControls();
    if (scSavedOptionFilters && typeof scProductListElement.filterProducts === 'function') {
      await scProductListElement.filterProducts(scSavedOptionFilters);
    }
  }

  const scProductsInnerElement = scProductListElement.querySelector('[data-sc-products-inner]');
  const scProductsScrollerElement = scProductListElement.querySelector('[data-sc-products-scroller]');
  if (scProductsInnerElement) {
    scProductsInnerElement.scrollLeft = 0;
  }
  if (scProductsScrollerElement) {
    scProductsScrollerElement.scrollLeft = 0;
  }

  const scPreviousButtonElement = scProductListElement.querySelector('[data-sc-products-prev-button]');
  const scNextButtonElement = scProductListElement.querySelector('[data-sc-products-next-button]');
  if (scPreviousButtonElement) {
    scPreviousButtonElement.setAttribute('disabled', '');
  }
  if (scNextButtonElement && scProductListElement.productListInner) {
    const scDisableNextButton = scProductListElement.productListInner.scrollWidth <= scProductListElement.productListInner.clientWidth;
    scNextButtonElement.toggleAttribute('disabled', scDisableNextButton);
  }

scRefreshDiscountShowcases();
}

const SC_FEATURED_COLLECTION_FILTER_ROOT_SELECTOR = 'sc-featured-collection-absorbancy-filter[data-sc-filter-root], sc-featured-collection-tabs-filter[data-sc-filter-root]';

function scGetFeaturedCollectionFilterRootElements(scParentElement) {
  return Array.from(scParentElement?.querySelectorAll(SC_FEATURED_COLLECTION_FILTER_ROOT_SELECTOR) || []);
}

function scGetFeaturedCollectionFilterRootKey(scFilterRootElement) {
  if (!scFilterRootElement) return '';

  const scTagName = scFilterRootElement.tagName.toLowerCase();
  const scFilterParam = scFilterRootElement.dataset.scFilterParam || '';

  return scFilterParam ? `${scTagName}::${scFilterParam}` : scTagName;
}

function scGetFeaturedCollectionFilterRootElementByTag(scParentElement, scTagName, scFilterParam = '') {
  return scGetFeaturedCollectionFilterRootElements(scParentElement).find((scFilterRootElement) => {
    if (scFilterRootElement.tagName.toLowerCase() !== scTagName) return false;
    if (!scFilterParam) return true;

    return (scFilterRootElement.dataset.scFilterParam || '') === scFilterParam;
  }) || null;
}

function scGetFeaturedCollectionFilterRequestState(scProductListElement) {
  if (!scProductListElement) return null;

  if (!scProductListElement.scFeaturedCollectionFilterRequestState) {
    scProductListElement.scFeaturedCollectionFilterRequestState = {
      requestId: 0,
      controller: null,
    };
  }

  return scProductListElement.scFeaturedCollectionFilterRequestState;
}

function scCreateFeaturedCollectionFilterRequest(scProductListElement) {
  const scRequestState = scGetFeaturedCollectionFilterRequestState(scProductListElement);
  if (!scRequestState) return null;

  if (scRequestState.controller) {
    scRequestState.controller.abort();
  }

  scRequestState.requestId += 1;
  scRequestState.controller = new AbortController();

  return {
    scRequestId: scRequestState.requestId,
    scRequestController: scRequestState.controller,
  };
}

function scIsFeaturedCollectionFilterRequestCurrent(scProductListElement, scRequestId, scRequestController) {
  const scRequestState = scGetFeaturedCollectionFilterRequestState(scProductListElement);
  if (!scRequestState) return false;

  return scRequestState.requestId === scRequestId && scRequestState.controller === scRequestController;
}

function scFinalizeFeaturedCollectionFilterRequest(scProductListElement, scRequestId, scRequestController) {
  const scRequestState = scGetFeaturedCollectionFilterRequestState(scProductListElement);
  if (!scRequestState) return;
  if (scRequestState.requestId !== scRequestId || scRequestState.controller !== scRequestController) return;

  scRequestState.controller = null;
}

function scReplaceFeaturedCollectionFilterRoots(scProductListElement, scNewFilterRootElements = []) {
  if (!scProductListElement || !scNewFilterRootElements.length) return;

  const scCurrentFilterRootElements = scGetFeaturedCollectionFilterRootElements(scProductListElement);
  if (!scCurrentFilterRootElements.length) return;

  scCurrentFilterRootElements.forEach((scCurrentFilterRootElement) => {
    if (typeof scCurrentFilterRootElement.scReplaceFilterContent !== 'function') return;

    const scMatchingFilterRootElement = scNewFilterRootElements.find((scNewFilterRootElement) => {
      return scGetFeaturedCollectionFilterRootKey(scNewFilterRootElement)
        === scGetFeaturedCollectionFilterRootKey(scCurrentFilterRootElement);
    });

    if (!scMatchingFilterRootElement) return;

    scCurrentFilterRootElement.scReplaceFilterContent(scMatchingFilterRootElement);
    if (typeof scCurrentFilterRootElement.scSetAjaxFilterReadyState === 'function') {
      const scHasUsableValues = typeof scCurrentFilterRootElement.scHasUsableFilterValues === 'function'
        ? scCurrentFilterRootElement.scHasUsableFilterValues(scMatchingFilterRootElement)
        : true;
      scCurrentFilterRootElement.scSetAjaxFilterReadyState(scHasUsableValues);
    }
  });

  if (scProductListElement.hasAttribute('data-sc-loading')) {
    scSetFeaturedCollectionFilterInputsLocked(scProductListElement, true);
  }
}

function scSetFeaturedCollectionFilterInputsLocked(scProductListElement, scIsLocked) {
  if (!scProductListElement) return;

  scGetFeaturedCollectionFilterRootElements(scProductListElement).forEach((scFilterRootElement) => {
    scFilterRootElement.toggleAttribute('data-sc-loading-locked', scIsLocked);

    scFilterRootElement.querySelectorAll('[data-sc-filter-input]').forEach((scFilterInputElement) => {
      if (scIsLocked) {
        if (scFilterInputElement.disabled) return;
        scFilterInputElement.dataset.scLoadingLocked = 'true';
        scFilterInputElement.disabled = true;
        return;
      }

      if (scFilterInputElement.dataset.scLoadingLocked !== 'true') return;
      scFilterInputElement.disabled = false;
      delete scFilterInputElement.dataset.scLoadingLocked;
    });
  });
}

// Keep facet filter drawer above header/app banners when moved to body via append-body
(function scPatchProductFacetForAppendBodyFacetFilters() {
  const scProductFacetClass = customElements.get('product-facet');
  if (!scProductFacetClass || scProductFacetClass.prototype.scAppendBodyFacetFiltersPatchApplied) return;

  // Drawer filters use append-body, so facet:criteria-changed never bubbles through product-facet.
  document.addEventListener('facet:criteria-changed', (event) => {
    const scFacetFiltersElement = event.target;
    if (!(scFacetFiltersElement instanceof Element) || scFacetFiltersElement.id !== 'facet-filters') return;
    if (scFacetFiltersElement.hasAttribute('always-visible')) return;

    const scProductFacetElement = document.querySelector('product-facet');
    if (!scProductFacetElement || scProductFacetElement.contains(scFacetFiltersElement)) return;

    scProductFacetElement.dispatchEvent(new CustomEvent('facet:criteria-changed', {
      bubbles: false,
      detail: event.detail,
    }));
  }, true);

  const scOriginalRerender = scProductFacetClass.prototype._rerender;

  scProductFacetClass.prototype._rerender = async function scRerenderWithAppendBodyFacetFilters(event) {
    const scFacetFiltersElement = document.getElementById('facet-filters');
    const scShouldRestoreToBody = scFacetFiltersElement && !this.contains(scFacetFiltersElement);

    if (scShouldRestoreToBody) {
      this.appendChild(scFacetFiltersElement);
    }

    try {
      await scOriginalRerender.call(this, event);
    } finally {
      if (scShouldRestoreToBody && scFacetFiltersElement.parentElement === this) {
        document.body.appendChild(scFacetFiltersElement);
      }
    }
  };

  scProductFacetClass.prototype.scAppendBodyFacetFiltersPatchApplied = true;
})();

// After filtering, the re-render replaces the open filter drawer with a new, closed one. The old drawer is
// removed while open, so its scroll lock ("lock-all") is never released and the page can't scroll anymore.
// Keep a single drawer, restore its open state and release a leftover lock.
(function scFixFacetDrawerScrollLock() {
  let scFacetDrawerWasOpen = false;

  document.addEventListener('facet:criteria-changed', () => {
    scFacetDrawerWasOpen = !!document.querySelector('#facet-filters[open]');
  }, true);

  document.addEventListener('product-facet:rerender', () => {
    // Wait for the append-body patch above to move the drawer back to <body>
    setTimeout(() => {
      const scFacetDrawers = Array.from(document.querySelectorAll('#facet-filters'));
      if (scFacetDrawers.length === 0) return;

      const scFacetDrawer = scFacetDrawers.find((scDrawer) => scDrawer.parentElement === document.body)
        || scFacetDrawers[scFacetDrawers.length - 1];
      scFacetDrawers.forEach((scDrawer) => {
        if (scDrawer !== scFacetDrawer) scDrawer.remove();
      });

      if (scFacetDrawerWasOpen && !scFacetDrawer.hasAttribute('open') && !scFacetDrawer.hasAttribute('always-visible')) {
        scFacetDrawer.setAttribute('open', '');
      }

      const scDrawerClass = customElements.get('drawer-content');
      const scModalClass = customElements.get('modal-content');
      const scHasOpenOverlay = Array.from(document.querySelectorAll('[open]')).some((scElement) => {
        return (scDrawerClass && scElement instanceof scDrawerClass) || (scModalClass && scElement instanceof scModalClass);
      });
      if (!scHasOpenOverlay) {
        document.documentElement.classList.remove('lock-all');
      }
    }, 0);
  });
})();

function scGetFeaturedCollectionSelectedFilterInputs(scProductListElement) {
  return scGetFeaturedCollectionFilterRootElements(scProductListElement).flatMap((scFilterRootElement) => {
    return Array.from(scFilterRootElement.querySelectorAll('[data-sc-filter-input]:checked'));
  });
}

class ScMainCollectionFilterElement extends HTMLElement {
  scIsFeaturedCollectionFilter() {
    return !!this.closest('[data-sc-filter-root]');
  }

  scGetMainCollectionFacetElement() {
    if (this.scIsFeaturedCollectionFilter()) return null;
    return this.closest('product-facet');
  }

  scGetAllMainCollectionFilterInputElements() {
    const scFacetElement = this.scGetMainCollectionFacetElement();
    if (!scFacetElement) return [];

    return Array.from(scFacetElement.querySelectorAll('[data-sc-filter-input]'));
  }

  scUnbindMainCollectionFilterEvents() {
    if (!this.scMainCollectionFilterInputElements?.length || !this.scBoundMainCollectionFilterChangedHandler) return;

    this.scMainCollectionFilterInputElements.forEach((scFilterInputElement) => {
      scFilterInputElement.removeEventListener('change', this.scBoundMainCollectionFilterChangedHandler);
    });
    this.scMainCollectionFilterInputElements = [];
  }

  scBindMainCollectionFilterEvents() {
    this.scUnbindMainCollectionFilterEvents();

    if (!this.scGetMainCollectionFacetElement()) return;

    this.scMainCollectionFilterInputElements = Array.from(this.querySelectorAll('[data-sc-filter-input]'));
    this.scMainCollectionFilterInputElements.forEach((scFilterInputElement) => {
      scFilterInputElement.addEventListener('change', this.scBoundMainCollectionFilterChangedHandler);
    });
  }

  scSyncCheckedFilterItems() {
    this.querySelectorAll('[data-sc-filter-input]').forEach((scFilterInputElement) => {
      if (!scFilterInputElement.id) return;

      const scFilterItemLabelElement = this.querySelector(`label[for="${scFilterInputElement.id}"] [data-sc-filter-item]`);
      if (!scFilterItemLabelElement) return;

      scFilterItemLabelElement.classList.toggle('checked', scFilterInputElement.checked);
    });
  }

  scBuildMainCollectionFilterUrl() {
    const scFacetElement = this.scGetMainCollectionFacetElement();
    const scFacetFiltersFormElement = document.getElementById('facet-filters-form');
    if (!scFacetElement || !scFacetFiltersFormElement) return null;

    const scFormData = new FormData(scFacetFiltersFormElement);
    const scSearchParams = new URLSearchParams();

    scFormData.forEach((scValue, scKey) => {
      if (scValue === '' || scValue == null) return;
      scSearchParams.append(scKey, scValue);
    });

    const scAllMainCollectionFilterInputElements = this.scGetAllMainCollectionFilterInputElements();
    const scAllMainCollectionFilterParamNames = Array.from(new Set(
      scAllMainCollectionFilterInputElements.map((scFilterInputElement) => scFilterInputElement.name).filter(Boolean)
    ));

    scAllMainCollectionFilterParamNames.forEach((scFilterParamName) => {
      scSearchParams.delete(scFilterParamName);
    });

    scAllMainCollectionFilterInputElements.forEach((scFilterInputElement) => {
      if (!scFilterInputElement.name || !scFilterInputElement.checked || scFilterInputElement.disabled) return;
      scSearchParams.append(scFilterInputElement.name, scFilterInputElement.value);
    });

    const scCurrentUrl = new URL(window.location.href);
    if (!scSearchParams.has('sort_by') && scCurrentUrl.searchParams.has('sort_by')) {
      scSearchParams.set('sort_by', scCurrentUrl.searchParams.get('sort_by'));
    }
    scSearchParams.delete('page');

    const scSearchParamsAsString = scSearchParams.toString();
    return scSearchParamsAsString ? `${window.location.pathname}?${scSearchParamsAsString}` : window.location.pathname;
  }

  scHasSwiperOverflow(scSwiperContainer) {
    const scSwiperWrapperElement = scSwiperContainer?.querySelector('.swiper-wrapper');
    if (!scSwiperContainer || !scSwiperWrapperElement) return false;

    const scOverflowThreshold = 2;
    return scSwiperWrapperElement.scrollWidth > scSwiperContainer.clientWidth + scOverflowThreshold;
  }

  scSyncSwiperInteractivity(scSwiperContainer) {
    const scHasOverflow = this.scHasSwiperOverflow(scSwiperContainer);
    if (!this.scSwiperInstance) return scHasOverflow;

    this.scSwiperInstance.allowTouchMove = scHasOverflow;
    this.scSwiperInstance.allowSlideNext = scHasOverflow;
    this.scSwiperInstance.allowSlidePrev = scHasOverflow;

    if (!scHasOverflow && this.scSwiperInstance.activeIndex !== 0 && typeof this.scSwiperInstance.slideTo === 'function') {
      this.scSwiperInstance.slideTo(0, 0);
    }

    return scHasOverflow;
  }

  scOnMainCollectionFilterChanged() {
    const scFacetElement = this.scGetMainCollectionFacetElement();
    if (!scFacetElement) return;

    this.scSyncCheckedFilterItems();

    const scFilterUrl = this.scBuildMainCollectionFilterUrl();
    if (!scFilterUrl) return;

    this.dispatchEvent(new CustomEvent('facet:criteria-changed', {
      bubbles: true,
      detail: {
        url: scFilterUrl,
      },
    }));
  }
}

if (!customElements.get('sc-collection-filter-absorbancy')) {
  class ScCollectionFilterAbsorbancy extends ScMainCollectionFilterElement {
    connectedCallback() {
      if (this.scIsInitialized) return;
      this.scIsInitialized = true;
      this.scBoundMainCollectionFilterChangedHandler = this.scOnMainCollectionFilterChanged.bind(this);
      this.scInitializeSwiper();
      this.scBindMainCollectionFilterEvents();
      this.scSyncCheckedFilterItems();
    }

    disconnectedCallback() {
      this.scUnbindMainCollectionFilterEvents();
      if (this.scSwiperInstance && typeof this.scSwiperInstance.destroy === 'function') {
        this.scSwiperInstance.destroy(true, true);
      }
      this.scSwiperInstance = null;
    }

    scUpdateNavigationButtonsVisibility() {
      const scNavigationButtonsElement = this.querySelector('.sc-collection-filter-absorbancy-buttons');
      if (!scNavigationButtonsElement) return;

      const scSwiperContainer = this.querySelector('[data-sc-absorbancy-swiper]');
      const scHasOverflow = this.scSyncSwiperInteractivity(scSwiperContainer);
      if (!scHasOverflow) {
        scNavigationButtonsElement.hidden = true;
        return;
      }

      const scNavigationButtonElements = Array.from(this.querySelectorAll('.sc-collection-filter-absorbancy-nav-button'));
      if (!scNavigationButtonElements.length) {
        scNavigationButtonsElement.hidden = true;
        return;
      }

      const scHasUsableNavigationButton = scNavigationButtonElements.some((scNavigationButtonElement) => {
        return !scNavigationButtonElement.classList.contains('swiper-button-lock')
          && !scNavigationButtonElement.classList.contains('swiper-button-disabled');
      });

      scNavigationButtonsElement.hidden = !scHasUsableNavigationButton;
    }

    scInitializeSwiper() {
      this.scEnsureSwiperReady().then(() => {
        if (!window.Swiper) return;
        const scSwiperContainer = this.querySelector('[data-sc-absorbancy-swiper]');
        if (!scSwiperContainer) return;

        const scNextNavigationElement = this.querySelector('[data-sc-absorbancy-nav-next]');
        const scPreviousNavigationElement = this.querySelector('[data-sc-absorbancy-nav-prev]');

        this.scSwiperInstance = new window.Swiper(scSwiperContainer, {
          slidesPerView: "auto",
          spaceBetween: 10,
          allowTouchMove: true,
          watchOverflow: false,
          observer: true,
          observeParents: true,
          observeSlideChildren: true,
          on: {
            afterInit: () => this.scUpdateNavigationButtonsVisibility(),
            fromEdge: () => this.scUpdateNavigationButtonsVisibility(),
            lock: () => this.scUpdateNavigationButtonsVisibility(),
            observerUpdate: () => this.scUpdateNavigationButtonsVisibility(),
            reachBeginning: () => this.scUpdateNavigationButtonsVisibility(),
            reachEnd: () => this.scUpdateNavigationButtonsVisibility(),
            resize: () => this.scUpdateNavigationButtonsVisibility(),
            slideChange: () => this.scUpdateNavigationButtonsVisibility(),
            unlock: () => this.scUpdateNavigationButtonsVisibility(),
            update: () => this.scUpdateNavigationButtonsVisibility(),
          },
          navigation: {
            nextEl: scNextNavigationElement,
            prevEl: scPreviousNavigationElement,
          },
        });

        this.scUpdateNavigationButtonsVisibility();
      });
    }

    scEnsureSwiperReady() {
      if (window.Swiper) return Promise.resolve();
      if (ScCollectionFilterAbsorbancy.scSwiperReadyPromise) return ScCollectionFilterAbsorbancy.scSwiperReadyPromise;

      ScCollectionFilterAbsorbancy.scSwiperReadyPromise = new Promise((resolve) => {
        const scReadyCheckStartAt = Date.now();
        const scMaximumWaitInMilliseconds = 5000;

        const scWaitForSwiper = () => {
          if (window.Swiper) return resolve();
          if (Date.now() - scReadyCheckStartAt > scMaximumWaitInMilliseconds) return resolve();
          requestAnimationFrame(scWaitForSwiper);
        };

        scWaitForSwiper();
      });

      return ScCollectionFilterAbsorbancy.scSwiperReadyPromise;
    }
  }

  customElements.define('sc-collection-filter-absorbancy', ScCollectionFilterAbsorbancy);
}

if (!customElements.get('sc-collection-filter-tabs')) {
  class ScCollectionFilterTabs extends ScMainCollectionFilterElement {
    connectedCallback() {
      if (this.scIsInitialized) return;
      this.scIsInitialized = true;
      this.scBoundMainCollectionFilterChangedHandler = this.scOnMainCollectionFilterChanged.bind(this);
      this.scInitializeSwiper();
      this.scBindMainCollectionFilterEvents();
      this.scSyncCheckedFilterItems();
    }

    disconnectedCallback() {
      this.scUnbindMainCollectionFilterEvents();
      if (this.scSwiperInstance && typeof this.scSwiperInstance.destroy === 'function') {
        this.scSwiperInstance.destroy(true, true);
      }
      this.scSwiperInstance = null;
    }

    scUpdateNavigationButtonsVisibility() {
      const scNavigationButtonsElement = this.querySelector('.sc-collection-filter-tabs-buttons');
      if (!scNavigationButtonsElement) return;

      const scSwiperContainer = this.querySelector('[data-sc-tabs-swiper]');
      const scHasOverflow = this.scSyncSwiperInteractivity(scSwiperContainer);
      if (!scHasOverflow) {
        scNavigationButtonsElement.hidden = true;
        return;
      }

      const scNavigationButtonElements = Array.from(this.querySelectorAll('.sc-collection-filter-tabs-nav-button'));
      if (!scNavigationButtonElements.length) {
        scNavigationButtonsElement.hidden = true;
        return;
      }

      const scHasUsableNavigationButton = scNavigationButtonElements.some((scNavigationButtonElement) => {
        return !scNavigationButtonElement.classList.contains('swiper-button-lock')
          && !scNavigationButtonElement.classList.contains('swiper-button-disabled');
      });

      scNavigationButtonsElement.hidden = !scHasUsableNavigationButton;
    }

    scInitializeSwiper() {
      this.scEnsureSwiperReady().then(() => {
        if (!window.Swiper) return;
        const scSwiperContainer = this.querySelector('[data-sc-tabs-swiper]');
        if (!scSwiperContainer) return;

        const scNextNavigationElement = this.querySelector('[data-sc-tabs-nav-next]');
        const scPreviousNavigationElement = this.querySelector('[data-sc-tabs-nav-prev]');

        this.scSwiperInstance = new window.Swiper(scSwiperContainer, {
          slidesPerView: "auto",
          spaceBetween: 10,
          allowTouchMove: true,
          watchOverflow: false,
          observer: true,
          observeParents: true,
          observeSlideChildren: true,
          on: {
            afterInit: () => this.scUpdateNavigationButtonsVisibility(),
            fromEdge: () => this.scUpdateNavigationButtonsVisibility(),
            lock: () => this.scUpdateNavigationButtonsVisibility(),
            observerUpdate: () => this.scUpdateNavigationButtonsVisibility(),
            reachBeginning: () => this.scUpdateNavigationButtonsVisibility(),
            reachEnd: () => this.scUpdateNavigationButtonsVisibility(),
            resize: () => this.scUpdateNavigationButtonsVisibility(),
            slideChange: () => this.scUpdateNavigationButtonsVisibility(),
            unlock: () => this.scUpdateNavigationButtonsVisibility(),
            update: () => this.scUpdateNavigationButtonsVisibility(),
          },
          navigation: {
            nextEl: scNextNavigationElement,
            prevEl: scPreviousNavigationElement,
          },
        });

        this.scUpdateNavigationButtonsVisibility();
      });
    }

    scEnsureSwiperReady() {
      if (window.Swiper) return Promise.resolve();
      if (ScCollectionFilterTabs.scSwiperReadyPromise) return ScCollectionFilterTabs.scSwiperReadyPromise;

      ScCollectionFilterTabs.scSwiperReadyPromise = new Promise((resolve) => {
        const scReadyCheckStartAt = Date.now();
        const scMaximumWaitInMilliseconds = 5000;

        const scWaitForSwiper = () => {
          if (window.Swiper) return resolve();
          if (Date.now() - scReadyCheckStartAt > scMaximumWaitInMilliseconds) return resolve();
          requestAnimationFrame(scWaitForSwiper);
        };

        scWaitForSwiper();
      });

      return ScCollectionFilterTabs.scSwiperReadyPromise;
    }
  }

  customElements.define('sc-collection-filter-tabs', ScCollectionFilterTabs);
}

if (!customElements.get('sc-featured-collection-controller')) {
  class ScFeaturedCollectionController extends HTMLElement {
    connectedCallback() {
      if (this.scIsInitialized) return;
      this.scIsInitialized = true;
      this.scSectionRequestController = null;
      this.scTabsNavigationElement = null;
      this.scBoundLoadMoreClickHandler = this.scOnLoadMoreButtonClick.bind(this);
      this.scBoundTabsChangedHandler = this.scOnTabsChanged.bind(this);
      this.scProductListElement = this.closest('product-list[data-sc-featured-collection-block]');
      if (!this.scProductListElement) return;

      this.scFooterElement = this.scProductListElement.querySelector('[data-sc-footer]');
      this.scLoadMoreButtonElement = this.scProductListElement.querySelector('[data-sc-load-more-button]');
      this.scFinalLinkElement = this.scProductListElement.querySelector('[data-sc-final-link]');

      this.scIsStackProducts = scParseBoolean(this.dataset.scStackProducts);
      this.scIsLoadMoreModeEnabled = this.scIsStackProducts && scParseBoolean(this.dataset.scLoadMoreEnabled);
      this.scIsExclusiveActionMode = scParseBoolean(this.dataset.scLoadMoreExclusiveMode);
      this.scIsLoadMoreModeActive = false;

      this.scBatchSize = Math.max(scParseInteger(this.dataset.scInitialProductsCount, 0), 1);
      this.scInitialProductsCount = Math.max(scParseInteger(this.dataset.scInitialProductsCount, 0), 0);
      this.scVisibleProductsCount = Math.max(scParseInteger(this.dataset.scVisibleProductsCount, this.scInitialProductsCount), 0);
      this.scMaxIterations = Math.max(scParseInteger(this.dataset.scLoadMoreMaxIterations, 0), 0);
      this.scCurrentIteration = Math.max(
        scParseInteger(this.dataset.scCurrentIteration, scParseInteger(this.scProductListElement.dataset.scCurrentIteration, 0)),
        0
      );
      this.scCollectionProductsCount = Math.max(scParseInteger(this.dataset.scCollectionProductsCount, 0), 0);
      this.scHasMoreSourcePages = this.scCollectionProductsCount > this.scVisibleProductsCount;

      if (this.scLoadMoreButtonElement) {
        this.scLoadMoreButtonElement.addEventListener('click', this.scBoundLoadMoreClickHandler);
      }

      this.scBindTabsNavigationEvents();
      this.scSyncProductListRuntimeState();
      this.scSyncFooterState();
      requestAnimationFrame(() => {
        this.scActivateLoadMoreModeIfVisible();
      });
    }

    disconnectedCallback() {
      if (this.scSectionRequestController) {
        this.scSectionRequestController.abort();
        this.scSectionRequestController = null;
      }
      if (this.scLoadMoreButtonElement && this.scBoundLoadMoreClickHandler) {
        this.scLoadMoreButtonElement.removeEventListener('click', this.scBoundLoadMoreClickHandler);
      }
      if (this.scTabsNavigationElement && this.scBoundTabsChangedHandler) {
        this.scTabsNavigationElement.removeEventListener('tabs-nav:changed', this.scBoundTabsChangedHandler);
      }
    }

    scBindTabsNavigationEvents() {
      const scParentSectionElement = this.closest('[id^="shopify-section-"]');
      if (!scParentSectionElement) return;

      this.scTabsNavigationElement = scParentSectionElement.querySelector('tabs-nav');
      if (!this.scTabsNavigationElement) return;

      this.scTabsNavigationElement.addEventListener('tabs-nav:changed', this.scBoundTabsChangedHandler);
    }

    scOnTabsChanged() {
      requestAnimationFrame(() => {
        this.scActivateLoadMoreModeIfVisible();
      });
    }

    scIsLoadMoreEnabled() {
      return this.scIsLoadMoreModeEnabled;
    }

    scCreateExpectedStateError(scMessage) {
      const scExpectedStateError = new Error(scMessage);
      scExpectedStateError.scIsExpectedState = true;
      return scExpectedStateError;
    }

    scIsCriticalError(scError) {
      if (!scError) return false;
      if (scError.name === 'AbortError') return false;
      if (scError.scIsExpectedState) return false;

      if (typeof scError.scHttpStatus === 'number') {
        return scError.scHttpStatus >= 500;
      }

      return true;
    }

    scLogCriticalError(scContextLabel, scError) {
      if (!this.scIsCriticalError(scError)) return;
      console.error(`[SC] Featured collection load more critical error (${scContextLabel}).`, scError);
    }

    scSyncProductListRuntimeState() {
      if (!this.scProductListElement) return;
      this.scProductListElement.dataset.scCurrentIteration = this.scCurrentIteration.toString();
      this.scProductListElement.dataset.scVisibleProductsCount = this.scVisibleProductsCount.toString();
      this.dataset.scCurrentIteration = this.scCurrentIteration.toString();
      this.dataset.scVisibleProductsCount = this.scVisibleProductsCount.toString();
    }

    scSetLoadingState(scIsLoading) {
      if (!this.scProductListElement) return;

      if (scIsLoading) {
        this.scProductListElement.setAttribute('data-sc-loading', 'true');
      } else {
        this.scProductListElement.removeAttribute('data-sc-loading');
      }

      scSetFeaturedCollectionFilterInputsLocked(this.scProductListElement, scIsLoading);

      if (this.scLoadMoreButtonElement) {
        this.scLoadMoreButtonElement.toggleAttribute('disabled', scIsLoading);
      }
    }

    scActivateLoadMoreModeIfVisible() {
      if (!this.scIsLoadMoreModeEnabled) return false;
      if (!this.scProductListElement || this.scProductListElement.hidden) return false;

      this.scIsLoadMoreModeActive = true;
      this.scProductListElement.dataset.scLoadMoreActive = 'true';
      this.scSyncFooterState();
      return true;
    }

    scSyncFooterState() {
      if (!this.scFooterElement) return;

      if (!this.scIsLoadMoreModeEnabled || !this.scIsLoadMoreModeActive || !this.scLoadMoreButtonElement) {
        scToggleHidden(this.scLoadMoreButtonElement, true);
        scToggleHidden(this.scFinalLinkElement, false);
        this.scProductListElement.dataset.scLoadMoreActive = 'false';
        this.scProductListElement.dataset.scLoadMoreFinished = 'false';
        return;
      }

      const scCanLoadMore = this.scHasMoreSourcePages && this.scCurrentIteration < this.scMaxIterations;
      scToggleHidden(this.scLoadMoreButtonElement, !scCanLoadMore);

      if (this.scFinalLinkElement) {
        if (this.scIsExclusiveActionMode) {
          scToggleHidden(this.scFinalLinkElement, scCanLoadMore);
        } else {
          scToggleHidden(this.scFinalLinkElement, false);
        }
      }

      this.scProductListElement.dataset.scLoadMoreFinished = scCanLoadMore ? 'false' : 'true';
    }

    scGetCollectionBasePath() {
      return this.dataset.scCollectionUrl || this.scProductListElement?.dataset.scCollectionUrl || window.location.pathname;
    }

    scGetFilterRootElements() {
      return scGetFeaturedCollectionFilterRootElements(this.scProductListElement);
    }

    scGetFilterRootElement() {
      return this.scGetFilterRootElements()[0] || null;
    }

    scGetSelectedFilterInputs() {
      return scGetFeaturedCollectionSelectedFilterInputs(this.scProductListElement);
    }

    scAppendRequestContextParameters(scSectionRenderingUrl) {
      const scCurrentRequestUrl = new URL(window.location.href);
      const scAllowedRequestParameterNames = ['preview_theme_id', 'pb', '_fd', 'sort_by'];

      scAllowedRequestParameterNames.forEach((scAllowedRequestParameterName) => {
        if (!scCurrentRequestUrl.searchParams.has(scAllowedRequestParameterName)) return;
        if (scSectionRenderingUrl.searchParams.has(scAllowedRequestParameterName)) return;

        scSectionRenderingUrl.searchParams.set(
          scAllowedRequestParameterName,
          scCurrentRequestUrl.searchParams.get(scAllowedRequestParameterName)
        );
      });
    }

    scBuildSectionRenderingUrl(scPage = 1, scIncludeSelectedFilters = true) {
      const scSectionId = this.dataset.scSectionId;
      if (!scSectionId) return null;

      const scCollectionBasePath = this.scGetCollectionBasePath();
      const scSectionRenderingUrl = new URL(scCollectionBasePath, window.location.origin);
      scSectionRenderingUrl.searchParams.set('sections', scSectionId);
      if (scPage > 1) {
        scSectionRenderingUrl.searchParams.set('page', scPage.toString());
      }
      this.scAppendRequestContextParameters(scSectionRenderingUrl);

      if (scIncludeSelectedFilters) {
        this.scGetSelectedFilterInputs().forEach((scCheckedFilterInput) => {
          if (!scCheckedFilterInput.name) return;
          scSectionRenderingUrl.searchParams.append(scCheckedFilterInput.name, scCheckedFilterInput.value);
        });
      }

      return scSectionRenderingUrl;
    }

    scCreateSectionRequestController() {
      if (this.scSectionRequestController) {
        this.scSectionRequestController.abort();
      }

      const scSectionRequestController = new AbortController();
      this.scSectionRequestController = scSectionRequestController;
      return scSectionRequestController;
    }

    async scFetchSectionHtml(scSectionRenderingUrl, scAbortSignal) {
      const scFetchResponse = await fetch(scSectionRenderingUrl.toString(), {
        signal: scAbortSignal,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!scFetchResponse.ok) {
        const scResponseError = new Error(`Section rendering request failed with status ${scFetchResponse.status}.`);
        scResponseError.scHttpStatus = scFetchResponse.status;
        throw scResponseError;
      }

      const scResponseText = await scFetchResponse.text();
      const scSectionId = this.dataset.scSectionId;
      let scSectionHtml = '';

      try {
        const scResponseJson = JSON.parse(scResponseText);
        scSectionHtml = scResponseJson[scSectionId] || '';
      } catch {
        scSectionHtml = scResponseText;
      }

      if (!scSectionHtml) {
        throw this.scCreateExpectedStateError('No section HTML returned for requested section.');
      }

      return scSectionHtml;
    }

    scExtractSectionState(scSectionHtml) {
      const scInstanceId = this.dataset.scInstanceId;
      if (!scInstanceId) return null;

      const scHtmlParser = new DOMParser();
      const scSectionDocument = scHtmlParser.parseFromString(scSectionHtml, 'text/html');
      const scCandidateProductListElements = Array.from(scSectionDocument.querySelectorAll('product-list[data-sc-instance-id]'));
      const scTargetProductListElement = scCandidateProductListElements.find((scProductListElement) => {
        return scProductListElement.dataset.scInstanceId === scInstanceId;
      });

      if (!scTargetProductListElement) return null;

      const scProductsInnerElement = scTargetProductListElement.querySelector('[data-sc-products-inner]');
      const scFilterRootElements = scGetFeaturedCollectionFilterRootElements(scTargetProductListElement);
      const scProductsSourceTemplate = scTargetProductListElement.querySelector('template[data-sc-products-source-template]');
      const scSourceProductItems = scProductsSourceTemplate
        ? Array.from(scProductsSourceTemplate.content.querySelectorAll('product-item'))
        : [];

      return {
        scProductsInnerElement,
        scFilterRootElements,
        scProductsSourceTemplate,
        scSourceProductItems,
        scSourceHasNext: scProductsSourceTemplate?.dataset.scSourceHasNext === 'true',
      };
    }

    scHasUsableFilterValues(scFilterElement) {
      if (!scFilterElement) return false;
      return !!scFilterElement.querySelector('[data-sc-filter-input]:not([disabled])');
    }

    async scCollectProductsForVisibleCount(scTargetVisibleCount, scSectionRequestController) {
      let scCurrentPage = 1;
      let scHasNextPage = true;
      let scLastKnownFilterRootElements = [];
      const scCollectedProductItems = [];
      const scMaximumPages = 50;

      while (scCurrentPage <= scMaximumPages && scHasNextPage && scCollectedProductItems.length < scTargetVisibleCount) {
        const scSectionRenderingUrl = this.scBuildSectionRenderingUrl(scCurrentPage, true);
        if (!scSectionRenderingUrl) {
          throw this.scCreateExpectedStateError('Cannot build section rendering URL for load more request.');
        }

        const scSectionHtml = await this.scFetchSectionHtml(scSectionRenderingUrl, scSectionRequestController.signal);
        if (this.scSectionRequestController !== scSectionRequestController) return null;

        const scSectionState = this.scExtractSectionState(scSectionHtml);
        if (!scSectionState) {
          throw this.scCreateExpectedStateError('Missing section state in load more response.');
        }

        if (scSectionState.scFilterRootElements?.length) {
          scLastKnownFilterRootElements = scSectionState.scFilterRootElements;
        }

        const scPageItems = scSectionState.scSourceProductItems.length > 0
          ? scSectionState.scSourceProductItems
          : Array.from(scSectionState.scProductsInnerElement?.querySelectorAll('product-item') || []);

        scPageItems.forEach((scProductItemElement) => {
          if (scCollectedProductItems.length >= scTargetVisibleCount) return;
          scCollectedProductItems.push(scProductItemElement.cloneNode(true));
        });

        scHasNextPage = scSectionState.scProductsSourceTemplate ? scSectionState.scSourceHasNext : false;
        scCurrentPage += 1;
      }

      return {
        scProductItems: scCollectedProductItems,
        scHasNextPage,
        scFilterRootElements: scLastKnownFilterRootElements,
      };
    }

    async scReplaceProductsInnerFromItems(scProductItems) {
      if (!this.scProductListElement) return;

      const scCurrentProductsInnerElement = this.scProductListElement.querySelector('[data-sc-products-inner]');
      if (!scCurrentProductsInnerElement) return;

      const scStoererElements = Array.from(
        scCurrentProductsInnerElement.querySelectorAll('[data-sc-featured-collection-stoerer]')
      ).map((scStoererElement) => {
        return {
          scNode: scStoererElement.cloneNode(true),
          scPosition: Number.parseInt(scStoererElement.dataset.scPosition || '0', 10) || 0,
        };
      });
      const scPlacedStoererIndexes = new Set();
      const scNewProductsInnerElement = scCurrentProductsInnerElement.cloneNode(false);

      scProductItems.forEach((scProductItemElement, scProductIndex) => {
        const scProductPosition = scProductIndex + 1;

        scStoererElements.forEach((scStoerer, scStoererIndex) => {
          if (scPlacedStoererIndexes.has(scStoererIndex) || scStoerer.scPosition !== scProductPosition) return;
          scNewProductsInnerElement.appendChild(scStoerer.scNode);
          scPlacedStoererIndexes.add(scStoererIndex);
        });

        scNewProductsInnerElement.appendChild(scProductItemElement);
      });

      scStoererElements.forEach((scStoerer, scStoererIndex) => {
        if (scPlacedStoererIndexes.has(scStoererIndex) || scStoerer.scPosition > 0) return;
        scNewProductsInnerElement.appendChild(scStoerer.scNode);
      });

      scCurrentProductsInnerElement.replaceWith(scNewProductsInnerElement);
      await scRefreshFeaturedCollectionProductListState(this.scProductListElement);
    }

    async scRenderProductsForVisibleCount(scTargetVisibleCount, scContextLabel) {
      if (!this.scIsLoadMoreModeEnabled) return false;
      if (!this.scProductListElement) return false;

      const scSectionRequestController = this.scCreateSectionRequestController();

      this.scSetLoadingState(true);
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      try {
        const scCollectResult = await this.scCollectProductsForVisibleCount(scTargetVisibleCount, scSectionRequestController);
        if (!scCollectResult || this.scSectionRequestController !== scSectionRequestController) return false;

        await this.scReplaceProductsInnerFromItems(scCollectResult.scProductItems);

        scReplaceFeaturedCollectionFilterRoots(this.scProductListElement, scCollectResult.scFilterRootElements);

        const scLatestProductItemsCount = this.scProductListElement.querySelectorAll('[data-sc-products-inner] product-item').length;
        this.scVisibleProductsCount = scLatestProductItemsCount;
        this.scHasMoreSourcePages = !!scCollectResult.scHasNextPage;
        this.scSyncProductListRuntimeState();

        return true;
      } catch (scRenderError) {
        if (scRenderError.name !== 'AbortError') {
          this.scDeactivateToDefaultMode(scContextLabel, scRenderError);
        }
        return false;
      } finally {
        if (this.scSectionRequestController === scSectionRequestController) {
          this.scSectionRequestController = null;
          this.scSetLoadingState(false);
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        }
      }
    }

    async scOnLoadMoreButtonClick(scEvent) {
      scEvent.preventDefault();
      if (!this.scIsLoadMoreModeEnabled) return;
      if (!this.scActivateLoadMoreModeIfVisible()) return;
      if (this.scCurrentIteration >= this.scMaxIterations) {
        this.scHasMoreSourcePages = false;
        this.scSyncFooterState();
        return;
      }

      const scPreviousVisibleProductsCount = this.scVisibleProductsCount;
      const scTargetVisibleCount = this.scVisibleProductsCount + this.scBatchSize;
      this.scCurrentIteration += 1;
      this.scSyncProductListRuntimeState();

      const scRenderCompleted = await this.scRenderProductsForVisibleCount(scTargetVisibleCount, 'load-more-request');
      if (!scRenderCompleted) {
        this.scCurrentIteration = Math.max(this.scCurrentIteration - 1, 0);
        this.scSyncProductListRuntimeState();
        this.scSyncFooterState();
        return;
      }

      if (this.scVisibleProductsCount <= scPreviousVisibleProductsCount) {
        this.scHasMoreSourcePages = false;
      }

      this.scSyncFooterState();
    }

    async scOnAbsorbancyFilterChanged() {
      if (!this.scIsLoadMoreModeEnabled) return false;
      if (!this.scActivateLoadMoreModeIfVisible()) return false;

      const scTargetVisibleCount = Math.max(this.scVisibleProductsCount, this.scInitialProductsCount);
      const scRenderCompleted = await this.scRenderProductsForVisibleCount(scTargetVisibleCount, 'load-more-filter-request');
      this.scSyncFooterState();
      return scRenderCompleted;
    }

    async scOnTabsFilterChanged() {
      return this.scOnAbsorbancyFilterChanged();
    }

    scDeactivateToDefaultMode(scContextLabel = 'load-more-fallback', scError = null) {
      if (this.scSectionRequestController) {
        this.scSectionRequestController.abort();
        this.scSectionRequestController = null;
      }

      this.scIsLoadMoreModeEnabled = false;
      this.scIsLoadMoreModeActive = false;
      this.scSetLoadingState(false);

      if (this.scProductListElement) {
        this.scProductListElement.dataset.scLoadMoreActive = 'false';
        this.scProductListElement.dataset.scLoadMoreFinished = 'false';
      }

      scToggleHidden(this.scLoadMoreButtonElement, true);
      scToggleHidden(this.scFinalLinkElement, false);
      this.scLogCriticalError(scContextLabel, scError);
    }
  }

  customElements.define('sc-featured-collection-controller', ScFeaturedCollectionController);
}

if (!customElements.get('sc-featured-collection-absorbancy-filter')) {
  class ScFeaturedCollectionAbsorbancyFilter extends HTMLElement {
    connectedCallback() {
      if (this.scIsInitialized) return;
      this.scIsInitialized = true;
      this.scHasAjaxFilterData = false;
      this.scSectionRequestController = null;
      this.scFilterInputElements = [];
      this.scInitialProductsInnerElement = this.scCloneCurrentProductsInnerElement();
      this.scBoundFilterInputChangedHandler = this.scOnFilterInputChanged.bind(this);
      this.scBoundTabsChangedHandler = this.scOnTabsChanged.bind(this);
      this.scSetAjaxFilterReadyState(false);
      this.scBindTabsNavigationEvents();
      this.scBootstrapAjaxFilterIfNeeded();
    }

    disconnectedCallback() {
      if (this.scSectionRequestController) {
        this.scSectionRequestController.abort();
      }

      if (this.scTabsNavigationElement && this.scBoundTabsChangedHandler) {
        this.scTabsNavigationElement.removeEventListener('tabs-nav:changed', this.scBoundTabsChangedHandler);
      }

      this.scUnbindFilterInputEvents();
    }

    scOnTabsChanged() {
      this.scUpdateCollectionSwiper();
      requestAnimationFrame(() => {
        this.scBootstrapAjaxFilterIfNeeded();
      });
    }

    scSetAjaxFilterReadyState(scIsReady) {
      this.scHasAjaxFilterData = scIsReady;

      const scProductListElement = this.scGetProductListElement();
      if (scIsReady) {
        this.removeAttribute('hidden');
      } else {
        this.setAttribute('hidden', '');
      }

      if (scProductListElement) {
        const scHasAnyReadyFilter = scGetFeaturedCollectionFilterRootElements(scProductListElement).some((scFilterRootElement) => {
          return scFilterRootElement === this ? scIsReady : !scFilterRootElement.hasAttribute('hidden');
        });
        scProductListElement.dataset.scAjaxFilterReady = scHasAnyReadyFilter ? 'true' : 'false';
      }
    }

    scUnbindFilterInputEvents() {
      this.scFilterInputElements.forEach((scFilterInputElement) => {
        scFilterInputElement.removeEventListener('change', this.scBoundFilterInputChangedHandler);
      });
      this.scFilterInputElements = [];
    }

    scBindFilterInputEvents() {
      this.scUnbindFilterInputEvents();

      this.scFilterInputElements = Array.from(this.querySelectorAll('[data-sc-filter-input]'));
      this.scFilterInputElements.forEach((scFilterInputElement) => {
        scFilterInputElement.addEventListener('change', this.scBoundFilterInputChangedHandler);
      });
    }

    scBindTabsNavigationEvents() {
      const scParentSectionElement = this.closest('[id^="shopify-section-"]');
      if (!scParentSectionElement) return;

      this.scTabsNavigationElement = scParentSectionElement.querySelector('tabs-nav');
      if (!this.scTabsNavigationElement) return;

      this.scTabsNavigationElement.addEventListener('tabs-nav:changed', this.scBoundTabsChangedHandler);
    }

    async scOnFilterInputChanged() {
      if (!this.scHasAjaxFilterData) return;
      this.scSyncCheckedFilterItems();

      const scControllerElement = this.scGetCollectionControllerElement();
      if (scControllerElement && typeof scControllerElement.scIsLoadMoreEnabled === 'function' && scControllerElement.scIsLoadMoreEnabled()) {
        const scControllerHandledRequest = await scControllerElement.scOnAbsorbancyFilterChanged();
        if (scControllerHandledRequest) return;
      }

      this.scRenderProductsByFilter();
    }

    scSyncCheckedFilterItems() {
      this.querySelectorAll('[data-sc-filter-input]').forEach((scFilterInputElement) => {
        if (!scFilterInputElement.id) return;

        const scFilterItemLabelElement = this.querySelector(`label[for="${scFilterInputElement.id}"] [data-sc-filter-item]`);
        if (!scFilterItemLabelElement) return;

        scFilterItemLabelElement.classList.toggle('checked', scFilterInputElement.checked);
      });
    }

    scGetProductListElement() {
      return this.closest('product-list[data-sc-featured-collection-block]');
    }

    scGetCollectionControllerElement() {
      return this.scGetProductListElement()?.querySelector('sc-featured-collection-controller');
    }

    scCloneCurrentProductsInnerElement() {
      const scProductListElement = this.scGetProductListElement();
      const scProductsInnerElement = scProductListElement?.querySelector('[data-sc-products-inner]');
      if (!scProductsInnerElement) return null;
      return scProductsInnerElement.cloneNode(true);
    }

    scGetCollectionBasePath() {
      const scProductListElement = this.scGetProductListElement();
      return this.dataset.scCollectionUrl || scProductListElement?.dataset.scCollectionUrl || window.location.pathname;
    }

    scSetLoadingState(scIsLoading) {
      const scProductListElement = this.scGetProductListElement();
      if (!scProductListElement) return;

      if (scIsLoading) {
        scProductListElement.setAttribute('data-sc-loading', 'true');
      } else {
        scProductListElement.removeAttribute('data-sc-loading');
      }

      scSetFeaturedCollectionFilterInputsLocked(scProductListElement, scIsLoading);
    }

    scBuildSectionRenderingUrl(scIncludeSelectedFilters = true) {
      const scSectionId = this.dataset.scSectionId;
      if (!scSectionId) return null;

      const scCollectionBasePath = this.scGetCollectionBasePath();
      const scSectionRenderingUrl = new URL(scCollectionBasePath, window.location.origin);
      scSectionRenderingUrl.searchParams.set('sections', scSectionId);
      this.scAppendRequestContextParameters(scSectionRenderingUrl);

      if (scIncludeSelectedFilters) {
        scGetFeaturedCollectionSelectedFilterInputs(this.scGetProductListElement()).forEach((scCheckedFilterInput) => {
          if (!scCheckedFilterInput.name) return;
          scSectionRenderingUrl.searchParams.append(scCheckedFilterInput.name, scCheckedFilterInput.value);
        });
      }

      return scSectionRenderingUrl;
    }

    scAppendRequestContextParameters(scSectionRenderingUrl) {
      const scCurrentRequestUrl = new URL(window.location.href);
      const scAllowedRequestParameterNames = ['preview_theme_id', 'pb', '_fd', 'sort_by'];

      scAllowedRequestParameterNames.forEach((scAllowedRequestParameterName) => {
        if (!scCurrentRequestUrl.searchParams.has(scAllowedRequestParameterName)) return;
        if (scSectionRenderingUrl.searchParams.has(scAllowedRequestParameterName)) return;

        scSectionRenderingUrl.searchParams.set(
          scAllowedRequestParameterName,
          scCurrentRequestUrl.searchParams.get(scAllowedRequestParameterName)
        );
      });
    }

    scCreateExpectedStateError(scMessage) {
      const scExpectedStateError = new Error(scMessage);
      scExpectedStateError.scIsExpectedState = true;
      return scExpectedStateError;
    }

    scIsCriticalError(scError) {
      if (!scError) return false;
      if (scError.name === 'AbortError') return false;
      if (scError.scIsExpectedState) return false;

      if (typeof scError.scHttpStatus === 'number') {
        return scError.scHttpStatus >= 500;
      }

      return true;
    }

    scLogCriticalError(scContextLabel, scError) {
      if (!this.scIsCriticalError(scError)) return;
      console.error(`[SC] Featured collection absorbancy filter critical error (${scContextLabel}).`, scError);
    }

    async scFetchSectionHtml(scSectionRenderingUrl, scAbortSignal) {
      const scFetchResponse = await fetch(scSectionRenderingUrl.toString(), {
        signal: scAbortSignal,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!scFetchResponse.ok) {
        const scResponseError = new Error(`Section rendering request failed with status ${scFetchResponse.status}.`);
        scResponseError.scHttpStatus = scFetchResponse.status;
        throw scResponseError;
      }

      const scResponseText = await scFetchResponse.text();
      const scSectionId = this.dataset.scSectionId;
      let scSectionHtml = '';

      try {
        const scResponseJson = JSON.parse(scResponseText);
        scSectionHtml = scResponseJson[scSectionId] || '';
      } catch {
        scSectionHtml = scResponseText;
      }

      if (!scSectionHtml) {
        throw this.scCreateExpectedStateError('No section HTML returned for requested section.');
      }

      return scSectionHtml;
    }

    scExtractSectionState(scSectionHtml) {
      const scInstanceId = this.dataset.scInstanceId;
      if (!scInstanceId) return null;

      const scHtmlParser = new DOMParser();
      const scSectionDocument = scHtmlParser.parseFromString(scSectionHtml, 'text/html');
      const scCandidateProductListElements = Array.from(scSectionDocument.querySelectorAll('product-list[data-sc-instance-id]'));
      const scTargetProductListElement = scCandidateProductListElements.find((scProductListElement) => {
        return scProductListElement.dataset.scInstanceId === scInstanceId;
      });

      if (!scTargetProductListElement) return null;

      const scProductsInnerElement = scTargetProductListElement.querySelector('[data-sc-products-inner]');
      const scFilterRootElement = scGetFeaturedCollectionFilterRootElementByTag(
        scTargetProductListElement,
        this.tagName.toLowerCase(),
        this.dataset.scFilterParam || ''
      );
      return {
        scProductsInnerElement,
        scFilterElement: scFilterRootElement,
        scFilterRootElements: scGetFeaturedCollectionFilterRootElements(scTargetProductListElement),
      };
    }

    scHasUsableFilterValues(scFilterElement) {
      if (!scFilterElement) return false;
      return !!scFilterElement.querySelector('[data-sc-filter-input]:not([disabled])');
    }

    scReplaceFilterContent(scNewFilterElement) {
      if (!scNewFilterElement) return;

      const scNextChildNodes = Array.from(scNewFilterElement.childNodes).map((scChildNode) => scChildNode.cloneNode(true));
      this.replaceChildren(...scNextChildNodes);

      this.scBindFilterInputEvents();
      this.scSyncCheckedFilterItems();
      this.scUpdateCollectionSwiper();
    }

    async scRestoreInitialProductsState() {
      if (!this.scInitialProductsInnerElement) return;

      const scProductListElement = this.scGetProductListElement();
      const scCurrentProductsInnerElement = scProductListElement?.querySelector('[data-sc-products-inner]');
      if (!scProductListElement || !scCurrentProductsInnerElement) return;

      scCurrentProductsInnerElement.replaceWith(this.scInitialProductsInnerElement.cloneNode(true));
      await this.scRefreshProductListState(scProductListElement);
    }

    async scActivateFallbackMode(scContextLabel, scError = null) {
      const scProductListElement = this.scGetProductListElement();
      const scWasLoading = !!scProductListElement?.hasAttribute('data-sc-loading');

      if (this.scSectionRequestController) {
        this.scSectionRequestController.abort();
        this.scSectionRequestController = null;
      }

      this.scSetAjaxFilterReadyState(false);
      this.scSetLoadingState(false);
      this.scUnbindFilterInputEvents();
      await this.scRestoreInitialProductsState();
      const scControllerElement = this.scGetCollectionControllerElement();
      if (scControllerElement && typeof scControllerElement.scDeactivateToDefaultMode === 'function') {
        scControllerElement.scDeactivateToDefaultMode('load-more-fallback-after-filter-error', scError);
      }
      if (scWasLoading) {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }
      this.scLogCriticalError(scContextLabel, scError);
    }

    async scReplaceProductsInner(scNewProductsInnerElement) {
      const scProductListElement = this.scGetProductListElement();
      if (!scProductListElement) return;

      const scCurrentProductsInnerElement = scProductListElement.querySelector('[data-sc-products-inner]');
      if (!scCurrentProductsInnerElement) return;

      scCurrentProductsInnerElement.replaceWith(scNewProductsInnerElement);
      await this.scRefreshProductListState(scProductListElement);
    }

    async scRefreshProductListState(scProductListElement) {
      await scRefreshFeaturedCollectionProductListState(scProductListElement);
      this.scUpdateCollectionSwiper();
    }

    scCreateSectionRequestController() {
      if (this.scSectionRequestController) {
        this.scSectionRequestController.abort();
      }

      const scSectionRequestController = new AbortController();
      this.scSectionRequestController = scSectionRequestController;
      return scSectionRequestController;
    }

    async scBootstrapAjaxFilterIfNeeded() {
      if (this.scHasAjaxFilterData) return;

      const scProductListElement = this.scGetProductListElement();
      if (!scProductListElement || scProductListElement.hidden) return;

      const scSectionRenderingUrl = this.scBuildSectionRenderingUrl(false);
      if (!scSectionRenderingUrl) return;

      const scSectionRequestController = this.scCreateSectionRequestController();

      try {
        const scSectionHtml = await this.scFetchSectionHtml(scSectionRenderingUrl, scSectionRequestController.signal);
        if (this.scSectionRequestController !== scSectionRequestController) return;

        const scSectionState = this.scExtractSectionState(scSectionHtml);
        if (!scSectionState || !this.scHasUsableFilterValues(scSectionState.scFilterElement)) {
          await this.scActivateFallbackMode('bootstrap-empty-filter');
          return;
        }

        this.scReplaceFilterContent(scSectionState.scFilterElement);
        this.scSetAjaxFilterReadyState(true);
      } catch (scBootstrapError) {
        if (scBootstrapError.name !== 'AbortError') {
          await this.scActivateFallbackMode('bootstrap-request', scBootstrapError);
        }
      } finally {
        if (this.scSectionRequestController === scSectionRequestController) {
          this.scSectionRequestController = null;
        }
      }
    }

    scUpdateCollectionSwiper() {
      const scCollectionFilterElement = this.querySelector('sc-collection-filter-absorbancy[data-sc-collection-filter]');
      if (!scCollectionFilterElement || !scCollectionFilterElement.scSwiperInstance) return;
      scCollectionFilterElement.scSwiperInstance.update();
    }

    async scRenderProductsByFilter() {
      if (!this.scHasAjaxFilterData) return;

      const scSectionRenderingUrl = this.scBuildSectionRenderingUrl(true);
      if (!scSectionRenderingUrl) return;

      const scProductListElement = this.scGetProductListElement();
      const scFilterRequest = scCreateFeaturedCollectionFilterRequest(scProductListElement);
      if (!scFilterRequest) return;

      const { scRequestController: scSectionRequestController, scRequestId } = scFilterRequest;
      this.scSectionRequestController = scSectionRequestController;

      this.scSetLoadingState(true);
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      try {
        const scSectionHtml = await this.scFetchSectionHtml(scSectionRenderingUrl, scSectionRequestController.signal);
        if (!scIsFeaturedCollectionFilterRequestCurrent(scProductListElement, scRequestId, scSectionRequestController)) return;

        const scSectionState = this.scExtractSectionState(scSectionHtml);
        if (!scSectionState) {
          await this.scActivateFallbackMode('filter-missing-section-state', this.scCreateExpectedStateError('Missing section state in response.'));
          return;
        }
        if (!this.scHasUsableFilterValues(scSectionState.scFilterElement)) {
          await this.scActivateFallbackMode('filter-empty-filter-values');
          return;
        }
        if (!scSectionState.scProductsInnerElement) {
          await this.scActivateFallbackMode('filter-missing-products-inner', this.scCreateExpectedStateError('Missing products container in response.'));
          return;
        }

        await this.scReplaceProductsInner(scSectionState.scProductsInnerElement);
        scReplaceFeaturedCollectionFilterRoots(scProductListElement, scSectionState.scFilterRootElements);

      } catch (scRenderError) {
        if (scRenderError.name !== 'AbortError') {
          await this.scActivateFallbackMode('filter-request', scRenderError);
        }
      } finally {
        if (this.scSectionRequestController === scSectionRequestController) {
          this.scSectionRequestController = null;
        }

        if (scIsFeaturedCollectionFilterRequestCurrent(scProductListElement, scRequestId, scSectionRequestController)) {
          scFinalizeFeaturedCollectionFilterRequest(scProductListElement, scRequestId, scSectionRequestController);
          this.scSetLoadingState(false);
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        }
      }
    }
  }

  customElements.define('sc-featured-collection-absorbancy-filter', ScFeaturedCollectionAbsorbancyFilter);
}

if (!customElements.get('sc-featured-collection-tabs-filter')) {
  class ScFeaturedCollectionTabsFilter extends HTMLElement {
    connectedCallback() {
      if (this.scIsInitialized) return;
      this.scIsInitialized = true;
      this.scHasAjaxFilterData = false;
      this.scSectionRequestController = null;
      this.scFilterInputElements = [];
      this.scInitialProductsInnerElement = this.scCloneCurrentProductsInnerElement();
      this.scBoundFilterInputChangedHandler = this.scOnFilterInputChanged.bind(this);
      this.scBoundTabsChangedHandler = this.scOnTabsChanged.bind(this);
      this.scSetAjaxFilterReadyState(false);
      this.scBindTabsNavigationEvents();
      this.scBootstrapAjaxFilterIfNeeded();
    }

    disconnectedCallback() {
      if (this.scSectionRequestController) {
        this.scSectionRequestController.abort();
      }

      if (this.scTabsNavigationElement && this.scBoundTabsChangedHandler) {
        this.scTabsNavigationElement.removeEventListener('tabs-nav:changed', this.scBoundTabsChangedHandler);
      }

      this.scUnbindFilterInputEvents();
    }

    scOnTabsChanged() {
      this.scUpdateCollectionSwiper();
      requestAnimationFrame(() => {
        this.scBootstrapAjaxFilterIfNeeded();
      });
    }

    scSetAjaxFilterReadyState(scIsReady) {
      this.scHasAjaxFilterData = scIsReady;

      const scProductListElement = this.scGetProductListElement();
      if (scIsReady) {
        this.removeAttribute('hidden');
      } else {
        this.setAttribute('hidden', '');
      }

      if (scProductListElement) {
        const scHasAnyReadyFilter = scGetFeaturedCollectionFilterRootElements(scProductListElement).some((scFilterRootElement) => {
          return scFilterRootElement === this ? scIsReady : !scFilterRootElement.hasAttribute('hidden');
        });
        scProductListElement.dataset.scAjaxFilterReady = scHasAnyReadyFilter ? 'true' : 'false';
      }
    }

    scUnbindFilterInputEvents() {
      this.scFilterInputElements.forEach((scFilterInputElement) => {
        scFilterInputElement.removeEventListener('change', this.scBoundFilterInputChangedHandler);
      });
      this.scFilterInputElements = [];
    }

    scBindFilterInputEvents() {
      this.scUnbindFilterInputEvents();

      this.scFilterInputElements = Array.from(this.querySelectorAll('[data-sc-filter-input]'));
      this.scFilterInputElements.forEach((scFilterInputElement) => {
        scFilterInputElement.addEventListener('change', this.scBoundFilterInputChangedHandler);
      });
    }

    scBindTabsNavigationEvents() {
      const scParentSectionElement = this.closest('[id^="shopify-section-"]');
      if (!scParentSectionElement) return;

      this.scTabsNavigationElement = scParentSectionElement.querySelector('tabs-nav');
      if (!this.scTabsNavigationElement) return;

      this.scTabsNavigationElement.addEventListener('tabs-nav:changed', this.scBoundTabsChangedHandler);
    }

    async scOnFilterInputChanged() {
      if (!this.scHasAjaxFilterData) return;
      this.scSyncCheckedFilterItems();

      const scControllerElement = this.scGetCollectionControllerElement();
      if (scControllerElement && typeof scControllerElement.scIsLoadMoreEnabled === 'function' && scControllerElement.scIsLoadMoreEnabled()) {
        if (typeof scControllerElement.scOnTabsFilterChanged === 'function') {
          const scControllerHandledRequest = await scControllerElement.scOnTabsFilterChanged();
          if (scControllerHandledRequest) return;
        } else if (typeof scControllerElement.scOnAbsorbancyFilterChanged === 'function') {
          const scControllerHandledRequest = await scControllerElement.scOnAbsorbancyFilterChanged();
          if (scControllerHandledRequest) return;
        }
      }

      this.scRenderProductsByFilter();
    }

    scSyncCheckedFilterItems() {
      this.querySelectorAll('[data-sc-filter-input]').forEach((scFilterInputElement) => {
        if (!scFilterInputElement.id) return;

        const scFilterItemLabelElement = this.querySelector(`label[for="${scFilterInputElement.id}"] [data-sc-filter-item]`);
        if (!scFilterItemLabelElement) return;

        scFilterItemLabelElement.classList.toggle('checked', scFilterInputElement.checked);
      });
    }

    scGetProductListElement() {
      return this.closest('product-list[data-sc-featured-collection-block]');
    }

    scGetCollectionControllerElement() {
      return this.scGetProductListElement()?.querySelector('sc-featured-collection-controller');
    }

    scCloneCurrentProductsInnerElement() {
      const scProductListElement = this.scGetProductListElement();
      const scProductsInnerElement = scProductListElement?.querySelector('[data-sc-products-inner]');
      if (!scProductsInnerElement) return null;
      return scProductsInnerElement.cloneNode(true);
    }

    scGetCollectionBasePath() {
      const scProductListElement = this.scGetProductListElement();
      return this.dataset.scCollectionUrl || scProductListElement?.dataset.scCollectionUrl || window.location.pathname;
    }

    scSetLoadingState(scIsLoading) {
      const scProductListElement = this.scGetProductListElement();
      if (!scProductListElement) return;

      if (scIsLoading) {
        scProductListElement.setAttribute('data-sc-loading', 'true');
      } else {
        scProductListElement.removeAttribute('data-sc-loading');
      }

      scSetFeaturedCollectionFilterInputsLocked(scProductListElement, scIsLoading);
    }

    scBuildSectionRenderingUrl(scIncludeSelectedFilters = true) {
      const scSectionId = this.dataset.scSectionId;
      if (!scSectionId) return null;

      const scCollectionBasePath = this.scGetCollectionBasePath();
      const scSectionRenderingUrl = new URL(scCollectionBasePath, window.location.origin);
      scSectionRenderingUrl.searchParams.set('sections', scSectionId);
      this.scAppendRequestContextParameters(scSectionRenderingUrl);

      if (scIncludeSelectedFilters) {
        scGetFeaturedCollectionSelectedFilterInputs(this.scGetProductListElement()).forEach((scCheckedFilterInput) => {
          if (!scCheckedFilterInput.name) return;
          scSectionRenderingUrl.searchParams.append(scCheckedFilterInput.name, scCheckedFilterInput.value);
        });
      }

      return scSectionRenderingUrl;
    }

    scAppendRequestContextParameters(scSectionRenderingUrl) {
      const scCurrentRequestUrl = new URL(window.location.href);
      const scAllowedRequestParameterNames = ['preview_theme_id', 'pb', '_fd', 'sort_by'];

      scAllowedRequestParameterNames.forEach((scAllowedRequestParameterName) => {
        if (!scCurrentRequestUrl.searchParams.has(scAllowedRequestParameterName)) return;
        if (scSectionRenderingUrl.searchParams.has(scAllowedRequestParameterName)) return;

        scSectionRenderingUrl.searchParams.set(
          scAllowedRequestParameterName,
          scCurrentRequestUrl.searchParams.get(scAllowedRequestParameterName)
        );
      });
    }

    scCreateExpectedStateError(scMessage) {
      const scExpectedStateError = new Error(scMessage);
      scExpectedStateError.scIsExpectedState = true;
      return scExpectedStateError;
    }

    scIsCriticalError(scError) {
      if (!scError) return false;
      if (scError.name === 'AbortError') return false;
      if (scError.scIsExpectedState) return false;

      if (typeof scError.scHttpStatus === 'number') {
        return scError.scHttpStatus >= 500;
      }

      return true;
    }

    scLogCriticalError(scContextLabel, scError) {
      if (!this.scIsCriticalError(scError)) return;
      console.error(`[SC] Featured collection tabs filter critical error (${scContextLabel}).`, scError);
    }

    async scFetchSectionHtml(scSectionRenderingUrl, scAbortSignal) {
      const scFetchResponse = await fetch(scSectionRenderingUrl.toString(), {
        signal: scAbortSignal,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!scFetchResponse.ok) {
        const scResponseError = new Error(`Section rendering request failed with status ${scFetchResponse.status}.`);
        scResponseError.scHttpStatus = scFetchResponse.status;
        throw scResponseError;
      }

      const scResponseText = await scFetchResponse.text();
      const scSectionId = this.dataset.scSectionId;
      let scSectionHtml = '';

      try {
        const scResponseJson = JSON.parse(scResponseText);
        scSectionHtml = scResponseJson[scSectionId] || '';
      } catch {
        scSectionHtml = scResponseText;
      }

      if (!scSectionHtml) {
        throw this.scCreateExpectedStateError('No section HTML returned for requested section.');
      }

      return scSectionHtml;
    }

    scExtractSectionState(scSectionHtml) {
      const scInstanceId = this.dataset.scInstanceId;
      if (!scInstanceId) return null;

      const scHtmlParser = new DOMParser();
      const scSectionDocument = scHtmlParser.parseFromString(scSectionHtml, 'text/html');
      const scCandidateProductListElements = Array.from(scSectionDocument.querySelectorAll('product-list[data-sc-instance-id]'));
      const scTargetProductListElement = scCandidateProductListElements.find((scProductListElement) => {
        return scProductListElement.dataset.scInstanceId === scInstanceId;
      });

      if (!scTargetProductListElement) return null;

      const scProductsInnerElement = scTargetProductListElement.querySelector('[data-sc-products-inner]');
      const scFilterRootElement = scGetFeaturedCollectionFilterRootElementByTag(
        scTargetProductListElement,
        this.tagName.toLowerCase(),
        this.dataset.scFilterParam || ''
      );

      return {
        scProductsInnerElement,
        scFilterElement: scFilterRootElement,
        scFilterRootElements: scGetFeaturedCollectionFilterRootElements(scTargetProductListElement),
      };
    }

    scHasUsableFilterValues(scFilterElement) {
      if (!scFilterElement) return false;
      return !!scFilterElement.querySelector('[data-sc-filter-input]:not([disabled])');
    }

    scReplaceFilterContent(scNewFilterElement) {
      if (!scNewFilterElement) return;

      const scNextChildNodes = Array.from(scNewFilterElement.childNodes).map((scChildNode) => scChildNode.cloneNode(true));
      this.replaceChildren(...scNextChildNodes);

      this.scBindFilterInputEvents();
      this.scSyncCheckedFilterItems();
      this.scUpdateCollectionSwiper();
    }

    async scRestoreInitialProductsState() {
      if (!this.scInitialProductsInnerElement) return;

      const scProductListElement = this.scGetProductListElement();
      const scCurrentProductsInnerElement = scProductListElement?.querySelector('[data-sc-products-inner]');
      if (!scProductListElement || !scCurrentProductsInnerElement) return;

      scCurrentProductsInnerElement.replaceWith(this.scInitialProductsInnerElement.cloneNode(true));
      await this.scRefreshProductListState(scProductListElement);
    }

    async scActivateFallbackMode(scContextLabel, scError = null) {
      const scProductListElement = this.scGetProductListElement();
      const scWasLoading = !!scProductListElement?.hasAttribute('data-sc-loading');

      if (this.scSectionRequestController) {
        this.scSectionRequestController.abort();
        this.scSectionRequestController = null;
      }

      this.scSetAjaxFilterReadyState(false);
      this.scSetLoadingState(false);
      this.scUnbindFilterInputEvents();
      await this.scRestoreInitialProductsState();
      const scControllerElement = this.scGetCollectionControllerElement();
      if (scControllerElement && typeof scControllerElement.scDeactivateToDefaultMode === 'function') {
        scControllerElement.scDeactivateToDefaultMode('load-more-fallback-after-filter-error', scError);
      }
      if (scWasLoading) {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }
      this.scLogCriticalError(scContextLabel, scError);
    }

    async scReplaceProductsInner(scNewProductsInnerElement) {
      const scProductListElement = this.scGetProductListElement();
      if (!scProductListElement) return;

      const scCurrentProductsInnerElement = scProductListElement.querySelector('[data-sc-products-inner]');
      if (!scCurrentProductsInnerElement) return;

      scCurrentProductsInnerElement.replaceWith(scNewProductsInnerElement);
      await this.scRefreshProductListState(scProductListElement);
    }

    async scRefreshProductListState(scProductListElement) {
      await scRefreshFeaturedCollectionProductListState(scProductListElement);
      this.scUpdateCollectionSwiper();
    }

    scCreateSectionRequestController() {
      if (this.scSectionRequestController) {
        this.scSectionRequestController.abort();
      }

      const scSectionRequestController = new AbortController();
      this.scSectionRequestController = scSectionRequestController;
      return scSectionRequestController;
    }

    async scBootstrapAjaxFilterIfNeeded() {
      if (this.scHasAjaxFilterData) return;

      const scProductListElement = this.scGetProductListElement();
      if (!scProductListElement || scProductListElement.hidden) return;

      const scSectionRenderingUrl = this.scBuildSectionRenderingUrl(false);
      if (!scSectionRenderingUrl) return;

      const scSectionRequestController = this.scCreateSectionRequestController();

      try {
        const scSectionHtml = await this.scFetchSectionHtml(scSectionRenderingUrl, scSectionRequestController.signal);
        if (this.scSectionRequestController !== scSectionRequestController) return;

        const scSectionState = this.scExtractSectionState(scSectionHtml);
        if (!scSectionState || !this.scHasUsableFilterValues(scSectionState.scFilterElement)) {
          await this.scActivateFallbackMode('bootstrap-empty-filter');
          return;
        }

        this.scReplaceFilterContent(scSectionState.scFilterElement);
        this.scSetAjaxFilterReadyState(true);
      } catch (scBootstrapError) {
        if (scBootstrapError.name !== 'AbortError') {
          await this.scActivateFallbackMode('bootstrap-request', scBootstrapError);
        }
      } finally {
        if (this.scSectionRequestController === scSectionRequestController) {
          this.scSectionRequestController = null;
        }
      }
    }

    scUpdateCollectionSwiper() {
      this.querySelectorAll('sc-collection-filter-tabs[data-sc-collection-filter]').forEach((scCollectionFilterElement) => {
        if (!scCollectionFilterElement.scSwiperInstance) return;
        scCollectionFilterElement.scSwiperInstance.update();
      });
    }

    async scRenderProductsByFilter() {
      if (!this.scHasAjaxFilterData) return;

      const scSectionRenderingUrl = this.scBuildSectionRenderingUrl(true);
      if (!scSectionRenderingUrl) return;

      const scProductListElement = this.scGetProductListElement();
      const scFilterRequest = scCreateFeaturedCollectionFilterRequest(scProductListElement);
      if (!scFilterRequest) return;

      const { scRequestController: scSectionRequestController, scRequestId } = scFilterRequest;
      this.scSectionRequestController = scSectionRequestController;

      this.scSetLoadingState(true);
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      try {
        const scSectionHtml = await this.scFetchSectionHtml(scSectionRenderingUrl, scSectionRequestController.signal);
        if (!scIsFeaturedCollectionFilterRequestCurrent(scProductListElement, scRequestId, scSectionRequestController)) return;

        const scSectionState = this.scExtractSectionState(scSectionHtml);
        if (!scSectionState) {
          await this.scActivateFallbackMode('filter-missing-section-state', this.scCreateExpectedStateError('Missing section state in response.'));
          return;
        }
        if (!this.scHasUsableFilterValues(scSectionState.scFilterElement)) {
          await this.scActivateFallbackMode('filter-empty-filter-values');
          return;
        }
        if (!scSectionState.scProductsInnerElement) {
          await this.scActivateFallbackMode('filter-missing-products-inner', this.scCreateExpectedStateError('Missing products container in response.'));
          return;
        }

        await this.scReplaceProductsInner(scSectionState.scProductsInnerElement);
        scReplaceFeaturedCollectionFilterRoots(scProductListElement, scSectionState.scFilterRootElements);

      } catch (scRenderError) {
        if (scRenderError.name !== 'AbortError') {
          await this.scActivateFallbackMode('filter-request', scRenderError);
        }
      } finally {
        if (this.scSectionRequestController === scSectionRequestController) {
          this.scSectionRequestController = null;
        }

        if (scIsFeaturedCollectionFilterRequestCurrent(scProductListElement, scRequestId, scSectionRequestController)) {
          scFinalizeFeaturedCollectionFilterRequest(scProductListElement, scRequestId, scSectionRequestController);
          this.scSetLoadingState(false);
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        }
      }
    }
  }

  customElements.define('sc-featured-collection-tabs-filter', ScFeaturedCollectionTabsFilter);
}
/* SC Start: Discount Engine */
const scDefaultDiscountEngineConfig = {
  enabled: true,
  existingCodePolicy: 'skip',
  applyOncePerSession: false,
  autoRunOnLoad: true,
  requestSections: ['mini-cart'],
  debug: true,
  debugConsole: true,
  debugMaxEntries: 200,
  sessionStorageKey: 'sc-discount-engine:auto-applied',
  pageRules: [],
};

function scNormalizeDiscountEngineConfig(scConfigOverride = {}) {
  const scNormalizedBaseConfig = {
    ...scDefaultDiscountEngineConfig,
    ...scConfigOverride,
  };

  return {
    ...scNormalizedBaseConfig,
    requestSections: Array.isArray(scNormalizedBaseConfig.requestSections)
      ? scNormalizedBaseConfig.requestSections
      : scDefaultDiscountEngineConfig.requestSections.slice(),
    pageRules: (Array.isArray(scNormalizedBaseConfig.pageRules) ? scNormalizedBaseConfig.pageRules : []).map((scPageRule, scIndex) => ({
      id: typeof scPageRule?.id === 'string' && scPageRule.id.trim()
        ? scPageRule.id.trim()
        : `rule-${scIndex + 1}`,
      targetTemplateSuffixes: scNormalizeDiscountStringList(scPageRule?.targetTemplateSuffixes),
      targetTemplateNames: scNormalizeDiscountStringList(scPageRule?.targetTemplateNames).map((scName) => scName.toLowerCase()),
      targetPathnames: scNormalizeDiscountStringList(scPageRule?.targetPathnames),
      codeFromUrlParam: typeof scPageRule?.codeFromUrlParam === 'string' ? scPageRule.codeFromUrlParam.trim() : '',
      existingCodePolicy: ['skip', 'append', 'replace'].includes(scPageRule?.existingCodePolicy)
        ? scPageRule.existingCodePolicy
        : null,
      matchMode: typeof scPageRule?.matchMode === 'string' && scPageRule.matchMode.trim()
        ? scPageRule.matchMode.trim()
        : 'template_or_url',
      codesToApply: scNormalizeDiscountCodesList(
        scPageRule?.codesToApply ?? scPageRule?.targetCodes ?? scPageRule?.targetCode ?? []
      ),
      codesToRemove: scNormalizeDiscountCodesList(
        scPageRule?.codesToRemove ?? scPageRule?.removeCodes ?? []
      ),
      showcasePercentage: scNormalizeDiscountPercentage(
        scPageRule?.showcasePercentage ?? scPageRule?.targetPercentage ?? 0
      ),
    })),
  };
}

function scGetDiscountEngineConfig() {
  const scRuntimeConfig = window.scDiscountEngineConfig;
  if (!scRuntimeConfig || typeof scRuntimeConfig !== 'object') {
    return scNormalizeDiscountEngineConfig();
  }

  return scNormalizeDiscountEngineConfig(scRuntimeConfig);
}

function scCreateDiscountState(scPartialState = {}) {
  return {
    source: 'sc-discount-engine',
    status: 'idle',
    reason: null,
    updatedAt: null,
    targetCode: '',
    targetCodes: [],
    removeCodes: [],
    targetPercentage: 0,
    codes: [],
    primaryCode: null,
    hasAnyCode: false,
    hasTargetCode: false,
    cartLevelApplications: [],
    totalDiscount: 0,
    totalPrice: 0,
    originalTotalPrice: 0,
    itemsSubtotalPrice: 0,
    showcaseCode: null,
    showcasePercentage: 0,
    showcaseType: 'none',
    pageContext: null,
    ...scPartialState,
  };
}

function scNormalizeDiscountCode(scCode) {
  if (typeof scCode !== 'string') return '';
  return scCode.trim().toUpperCase();
}

function scNormalizeDiscountCodesList(scCodes) {
  if (Array.isArray(scCodes)) {
    return scCodes.map(scNormalizeDiscountCode).filter(Boolean);
  }

  const scNormalizedCode = scNormalizeDiscountCode(scCodes);
  return scNormalizedCode ? [scNormalizedCode] : [];
}

function scNormalizeDiscountStringList(scValues) {
  if (Array.isArray(scValues)) {
    return scValues
      .map((scValue) => (typeof scValue === 'string' ? scValue.trim() : ''))
      .filter(Boolean);
  }

  if (typeof scValues === 'string' && scValues.trim()) {
    return [scValues.trim()];
  }

  return [];
}

function scNormalizeDiscountPercentage(scValue) {
  const scNumber = Number(scValue);
  if (!Number.isFinite(scNumber) || scNumber <= 0) {
    return 0;
  }

  return scNumber;
}

function scExtractDiscountCodesFromCart(scCart) {
  if (!scCart || !Array.isArray(scCart.discount_codes)) return [];

  return scCart.discount_codes
    .map((scDiscountCodeEntry) => {
      if (typeof scDiscountCodeEntry === 'string') {
        return scNormalizeDiscountCode(scDiscountCodeEntry);
      }

      if (scDiscountCodeEntry && typeof scDiscountCodeEntry.code === 'string') {
        return scNormalizeDiscountCode(scDiscountCodeEntry.code);
      }

      return '';
    })
    .filter(Boolean);
}

function scUniqueDiscountCodes(scCodes) {
  return Array.from(new Set((scCodes || []).map(scNormalizeDiscountCode).filter(Boolean)));
}

function scSerializeDebugValue(scValue) {
  if (scValue instanceof Error) {
    return {
      name: scValue.name,
      message: scValue.message,
      stack: scValue.stack,
    };
  }

  if (typeof scValue === 'undefined') {
    return null;
  }

  try {
    return JSON.parse(JSON.stringify(scValue));
  } catch {
    return String(scValue);
  }
}

function scBuildCartDebugSummary(scCart) {
  if (!scCart || typeof scCart !== 'object') return null;

  return {
    token: scCart.token || null,
    item_count: Number.isFinite(scCart.item_count) ? scCart.item_count : 0,
    total_price: Number.isFinite(scCart.total_price) ? scCart.total_price : 0,
    original_total_price: Number.isFinite(scCart.original_total_price) ? scCart.original_total_price : 0,
    total_discount: Number.isFinite(scCart.total_discount) ? scCart.total_discount : 0,
    items_subtotal_price: Number.isFinite(scCart.items_subtotal_price) ? scCart.items_subtotal_price : 0,
    discount_codes: scExtractDiscountCodesFromCart(scCart),
    cart_level_discount_applications: Array.isArray(scCart.cart_level_discount_applications)
      ? scCart.cart_level_discount_applications.map((scDiscountApplication) => ({
        title: scDiscountApplication?.title || null,
        total_allocated_amount: Number.isFinite(scDiscountApplication?.total_allocated_amount)
          ? scDiscountApplication.total_allocated_amount
          : 0,
      }))
      : [],
  };
}

function scBuildStateDebugSummary(scState) {
  if (!scState || typeof scState !== 'object') return null;

  return {
    status: scState.status || null,
    reason: scState.reason || null,
    targetCode: scState.targetCode || null,
    targetPercentage: Number.isFinite(scState.targetPercentage) ? scState.targetPercentage : 0,
    codes: Array.isArray(scState.codes) ? scState.codes.slice() : [],
    hasAnyCode: !!scState.hasAnyCode,
    hasTargetCode: !!scState.hasTargetCode,
    showcaseCode: scState.showcaseCode || null,
    showcasePercentage: Number.isFinite(scState.showcasePercentage) ? scState.showcasePercentage : 0,
    totalDiscount: Number.isFinite(scState.totalDiscount) ? scState.totalDiscount : 0,
    totalPrice: Number.isFinite(scState.totalPrice) ? scState.totalPrice : 0,
    originalTotalPrice: Number.isFinite(scState.originalTotalPrice) ? scState.originalTotalPrice : 0,
    itemsSubtotalPrice: Number.isFinite(scState.itemsSubtotalPrice) ? scState.itemsSubtotalPrice : 0,
    pageContext: scSerializeDebugValue(scState.pageContext),
    updatedAt: scState.updatedAt || null,
  };
}

function scEnsureDiscountDebugLog() {
  if (!Array.isArray(window.__scDiscountDebugLog)) {
    window.__scDiscountDebugLog = [];
  }

  return window.__scDiscountDebugLog;
}

function scPushDiscountDebugLog(scConfig, scEntry) {
  const scLogStore = scEnsureDiscountDebugLog();
  scLogStore.push({
    timestamp: new Date().toISOString(),
    href: window.location.href,
    ...scEntry,
  });

  const scMaxEntries = Number.isFinite(scConfig?.debugMaxEntries) ? scConfig.debugMaxEntries : 200;
  if (scLogStore.length > scMaxEntries) {
    scLogStore.splice(0, scLogStore.length - scMaxEntries);
  }

  return scLogStore;
}

function scGetDiscountDebugDump() {
  return {
    generatedAt: new Date().toISOString(),
    href: window.location.href,
    state: scBuildStateDebugSummary(window.__scDiscountState),
    log: scEnsureDiscountDebugLog().slice(),
  };
}

function scGetDiscountDebugDumpString() {
  return JSON.stringify(scGetDiscountDebugDump(), null, 2);
}

class ScDiscountEngine extends HTMLElement {
  constructor() {
    super();
    this.scConfig = scGetDiscountEngineConfig();
    this.scBoundCartEventHandler = this.scHandleCartEvent.bind(this);
    this.scCartFetchPromise = null;
    this.scCurrentState = scCreateDiscountState();
    this.scApplyInFlight = false;
    scEnsureDiscountDebugLog();
  }

  connectedCallback() {
    if (this.scIsConnected) return;

    this.scIsConnected = true;
    window.__scDiscountState = this.scCurrentState;
    window.scDiscountEngine = this;
    window.scGetDiscountDebugDump = scGetDiscountDebugDump;
    window.scGetDiscountDebugDumpString = scGetDiscountDebugDumpString;
    window.scPrintDiscountDebugDump = function scPrintDiscountDebugDump() {
      const scDumpString = scGetDiscountDebugDumpString();
      console.log(scDumpString);
      return scDumpString;
    };
    window.scRemoveDiscountCode = async (scCodeToRemove = this.scGetPrimaryTargetCode()) => {
      const scLatestState = this.scCurrentState || window.__scDiscountState || scCreateDiscountState();
      return await this.scRemoveDiscountCode(scCodeToRemove, scLatestState);
    };

    document.documentElement.addEventListener('cart:updated', this.scBoundCartEventHandler);
    document.documentElement.addEventListener('cart:refresh', this.scBoundCartEventHandler);

    this.scLogDebug('engine-connected', {
      config: {
        existingCodePolicy: this.scConfig.existingCodePolicy,
        pageRules: this.scConfig.pageRules.map((scPageRule) => ({
          id: scPageRule.id,
          matchMode: scPageRule.matchMode,
          targetTemplateSuffixes: scPageRule.targetTemplateSuffixes,
          targetTemplateNames: scPageRule.targetTemplateNames,
          targetPathnames: scPageRule.targetPathnames,
          codesToApply: scPageRule.codesToApply,
          codesToRemove: scPageRule.codesToRemove,
          codeFromUrlParam: scPageRule.codeFromUrlParam,
          existingCodePolicy: scPageRule.existingCodePolicy,
        })),
      },
    });

    if (this.scConfig.autoRunOnLoad) {
      this.scInit().catch((scInitError) => {
        this.scLogDebug('init-error', {
          error: scInitError,
        });
        this.scDebugLog('Init failed', scInitError);
      });
    }
  }

  disconnectedCallback() {
    if (!this.scIsConnected) return;

    this.scIsConnected = false;
    document.documentElement.removeEventListener('cart:updated', this.scBoundCartEventHandler);
    document.documentElement.removeEventListener('cart:refresh', this.scBoundCartEventHandler);
  }

  scDebugLog(...scArgs) {
    if (!this.scConfig.debug) return;
    console.log('[SC Discount Engine]', ...scArgs);
  }

  async scInit() {
    this.scLogDebug('init-start');
    const scCart = await this.scGetCart();
    const scState = this.scBuildDiscountState(scCart);
    this.scSetState(scState);

    const scSyncPlan = this.scResolvePageDiscountSyncPlan(scState);
    this.scLogDebug('sync-plan', {
      syncPlan: scSerializeDebugValue(scSyncPlan),
    });

    if (scSyncPlan.shouldSync) {
      await this.scSyncPageDiscountCodes(scState, scSyncPlan);
    }

    const scUrlCode = scState.pageContext?.codeFromUrl;
    if (scUrlCode && scSyncPlan.reason !== 'url-code-not-in-cart') {
      this.scSetLastCleanedUrlCode(scUrlCode);
    }
  }

  scGetLastCleanedUrlCode() {
    try {
      return window.sessionStorage.getItem(`${this.scConfig.sessionStorageKey}:url-code-cleaned`);
    } catch {
      return null;
    }
  }

  scSetLastCleanedUrlCode(scCode) {
    try {
      window.sessionStorage.setItem(`${this.scConfig.sessionStorageKey}:url-code-cleaned`, scCode);
    } catch {
      // Storage unavailable (e.g. private mode): cleanup then simply runs on every landing
    }
  }

  scGetPageContext() {
    const scTemplateSuffix = typeof window._template?.suffix === 'string'
      ? window._template.suffix.trim()
      : '';
    const scTemplateName = typeof window._template?.name === 'string'
      ? window._template.name.trim().toLowerCase()
      : '';
    const scUrl = new URL(window.location.href);
    const scViewParam = scNormalizeDiscountCode(scUrl.searchParams.get('view') || '').toLowerCase();
    const scPathname = scUrl.pathname;
    const scNormalizedTemplateSuffix = scTemplateSuffix.toLowerCase();
    const scMatchedRule = this.scFindMatchingPageRule({
      scNormalizedTemplateSuffix,
      scTemplateName,
      scViewParam,
      scPathname,
    });
    // Rules with codeFromUrlParam take their code from the URL (e.g. influencer links) and only run when it is present
    const scUrlCode = scMatchedRule?.codeFromUrlParam
      ? scNormalizeDiscountCode(scUrl.searchParams.get(scMatchedRule.codeFromUrlParam) || '')
      : '';
    const scShouldRun = !!scMatchedRule && (!scMatchedRule.codeFromUrlParam || !!scUrlCode);

    return {
      pathname: scPathname,
      search: scUrl.search,
      templateSuffix: scTemplateSuffix,
      viewParam: scViewParam,
      isTargetTemplate: !!scMatchedRule?.targetTemplateSuffixes.map((scSuffix) => scSuffix.toLowerCase()).includes(scNormalizedTemplateSuffix),
      isTargetView: !!scMatchedRule?.targetTemplateSuffixes.map((scSuffix) => scSuffix.toLowerCase()).includes(scViewParam),
      isTargetPath: !!scMatchedRule?.targetPathnames.includes(scPathname),
      matchedRuleId: scMatchedRule?.id || null,
      codesToApply: scUrlCode ? [scUrlCode] : (scMatchedRule?.codesToApply || []),
      codesToRemove: scMatchedRule?.codesToRemove || [],
      codeFromUrl: scUrlCode || null,
      existingCodePolicy: scMatchedRule?.existingCodePolicy || null,
      showcasePercentage: scMatchedRule?.showcasePercentage || 0,
      shouldRun: scShouldRun,
    };
  }

  scGetExistingCodePolicy(scState = this.scCurrentState) {
    return scState?.pageContext?.existingCodePolicy || this.scConfig.existingCodePolicy;
  }

  scShouldRunForPage(scPageContextFlags, scMatchMode = 'template_or_url') {
    switch (scMatchMode) {
      case 'template':
        return !!scPageContextFlags.scIsTargetTemplate;
      case 'url':
        return !!(scPageContextFlags.scIsTargetView || scPageContextFlags.scIsTargetPath);
      case 'template_or_url':
      default:
        return !!(scPageContextFlags.scIsTargetTemplate || scPageContextFlags.scIsTargetView || scPageContextFlags.scIsTargetPath);
    }
  }

  scFindMatchingPageRule(scPageContextFlags) {
    return this.scConfig.pageRules.find((scPageRule) => {
      const scNormalizedRuleTemplateSuffixes = scPageRule.targetTemplateSuffixes.map((scSuffix) => scSuffix.toLowerCase());
      const scMatchesTemplateName = scPageRule.targetTemplateNames.length === 0
        || scPageRule.targetTemplateNames.includes(scPageContextFlags.scTemplateName);
      const scRuleFlags = {
        scIsTargetTemplate: scMatchesTemplateName
          && scNormalizedRuleTemplateSuffixes.includes(scPageContextFlags.scNormalizedTemplateSuffix),
        scIsTargetView: scNormalizedRuleTemplateSuffixes.includes(scPageContextFlags.scViewParam),
        scIsTargetPath: scPageRule.targetPathnames.includes(scPageContextFlags.scPathname),
      };

      return this.scShouldRunForPage(scRuleFlags, scPageRule.matchMode);
    }) || null;
  }

  async scGetCart() {
    if (this.scCartFetchPromise) {
      return this.scCartFetchPromise;
    }

    const scCartUrl = `${window.themeVariables?.routes?.cartUrl || '/cart'}.js`;

    this.scCartFetchPromise = (async () => {
      try {
        if (window.ShopifyData && typeof window.ShopifyData.getCart === 'function') {
          const scCart = await window.ShopifyData.getCart(false);
          this.scLogDebug('cart-fetched', {
            source: 'ShopifyData.getCart',
            cart: scBuildCartDebugSummary(scCart),
          });
          return scCart;
        }

        const scResponse = await fetch(scCartUrl, {
          credentials: 'same-origin',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (!scResponse.ok) {
          throw new Error(`Cart request failed with status ${scResponse.status}.`);
        }

        const scCart = await scResponse.json();
        this.scLogDebug('cart-fetched', {
          source: 'fetch-cart.js',
          cart: scBuildCartDebugSummary(scCart),
        });
        return scCart;
      } finally {
        this.scCartFetchPromise = null;
      }
    })();

    return this.scCartFetchPromise;
  }

  scBuildDiscountState(scCart) {
    const scPageContext = this.scGetPageContext();
    const scTargetCodes = scNormalizeDiscountCodesList(scPageContext.codesToApply);
    const scRemoveCodes = scNormalizeDiscountCodesList(scPageContext.codesToRemove);
    const scTargetCode = scTargetCodes[0] || '';
    const scTargetPercentage = scNormalizeDiscountPercentage(scPageContext.showcasePercentage);
    const scCodes = scExtractDiscountCodesFromCart(scCart);
    const scHasTargetCode = scTargetCodes.length > 0
      ? scTargetCodes.some((scCode) => scCodes.includes(scCode))
      : false;

    const scState = scCreateDiscountState({
      targetCode: scTargetCode,
      targetCodes: scTargetCodes,
      removeCodes: scRemoveCodes,
      targetPercentage: scTargetPercentage,
      codes: scCodes,
      primaryCode: scCodes[0] || null,
      hasAnyCode: scCodes.length > 0,
      hasTargetCode: scHasTargetCode,
      cartLevelApplications: Array.isArray(scCart?.cart_level_discount_applications)
        ? scCart.cart_level_discount_applications
        : [],
      totalDiscount: Number.isFinite(scCart?.total_discount) ? scCart.total_discount : 0,
      totalPrice: Number.isFinite(scCart?.total_price) ? scCart.total_price : 0,
      originalTotalPrice: Number.isFinite(scCart?.original_total_price) ? scCart.original_total_price : 0,
      itemsSubtotalPrice: Number.isFinite(scCart?.items_subtotal_price) ? scCart.items_subtotal_price : 0,
      showcaseCode: scHasTargetCode ? (scTargetCodes.find((scCode) => scCodes.includes(scCode)) || scTargetCode) : (scCodes[0] || null),
      showcasePercentage: scHasTargetCode ? scTargetPercentage : 0,
      showcaseType: scHasTargetCode ? 'target_code' : (scCodes[0] ? 'existing_code' : 'none'),
      pageContext: scPageContext,
      updatedAt: Date.now(),
    });

    this.scLogDebug('state-built', {
      cart: scBuildCartDebugSummary(scCart),
      state: scBuildStateDebugSummary(scState),
    });

    return scState;
  }

  scShouldApplyTargetCode(scState) {
    let scReason = 'ready-to-apply';

    if (!this.scConfig.enabled) {
      scReason = 'config-disabled';
      this.scLogDebug('apply-decision', {
        shouldApply: false,
        reason: scReason,
        state: scBuildStateDebugSummary(scState),
      });
      return false;
    }

    if (!scState?.pageContext?.shouldRun) {
      scReason = 'page-not-targeted';
      this.scLogDebug('apply-decision', {
        shouldApply: false,
        reason: scReason,
        state: scBuildStateDebugSummary(scState),
      });
      return false;
    }

    if (scState.hasTargetCode) {
      scReason = 'target-code-already-applied';
      this.scLogDebug('apply-decision', {
        shouldApply: false,
        reason: scReason,
        state: scBuildStateDebugSummary(scState),
      });
      return false;
    }

    if (this.scConfig.applyOncePerSession && this.scHasAppliedInSession()) {
      scReason = 'already-applied-in-session';
      this.scLogDebug('apply-decision', {
        shouldApply: false,
        reason: scReason,
        state: scBuildStateDebugSummary(scState),
      });
      return false;
    }

    if (scState.hasAnyCode && this.scGetExistingCodePolicy(scState) === 'skip') {
      scReason = 'existing-code-policy-skip';
      this.scLogDebug('apply-decision', {
        shouldApply: false,
        reason: scReason,
        state: scBuildStateDebugSummary(scState),
      });
      return false;
    }

    this.scLogDebug('apply-decision', {
      shouldApply: true,
      reason: scReason,
      state: scBuildStateDebugSummary(scState),
    });

    return true;
  }

  scResolvePageDiscountSyncPlan(scState = this.scCurrentState) {
    const scCurrentCodes = scUniqueDiscountCodes(scState?.codes || []);
    const scCodesToApply = scNormalizeDiscountCodesList(scState?.targetCodes || []);
    const scCodesToRemove = scNormalizeDiscountCodesList(scState?.removeCodes || []);
    const scFilteredCodes = scCurrentCodes.filter((scCode) => !scCodesToRemove.includes(scCode));
    let scDesiredCodes = scFilteredCodes.slice();
    let scReason = 'codes-already-in-desired-state';

    if (!this.scConfig.enabled) {
      return { shouldSync: false, reason: 'config-disabled', desiredCodes: scCurrentCodes, discountString: scCurrentCodes.join(',') };
    }

    if (!scState?.pageContext?.shouldRun) {
      return { shouldSync: false, reason: 'page-not-targeted', desiredCodes: scCurrentCodes, discountString: scCurrentCodes.join(',') };
    }

    if (this.scConfig.applyOncePerSession && this.scHasAppliedInSession()) {
      return { shouldSync: false, reason: 'already-applied-in-session', desiredCodes: scCurrentCodes, discountString: scCurrentCodes.join(',') };
    }

    const scExistingCodePolicy = this.scGetExistingCodePolicy(scState);
    const scUrlCode = scState?.pageContext?.codeFromUrl || null;

    if (scUrlCode) {
      // Only drop other codes once Shopify has accepted the URL code via /discount/CODE, otherwise the customer would end up without any code
      if (!scCurrentCodes.includes(scUrlCode)) {
        return { shouldSync: false, reason: 'url-code-not-in-cart', desiredCodes: scCurrentCodes, discountString: scCurrentCodes.join(',') };
      }

      // Clean up once per landing: a reload of the same LP must not remove a code the customer entered manually afterwards
      if (this.scGetLastCleanedUrlCode() === scUrlCode) {
        return { shouldSync: false, reason: 'url-code-already-cleaned', desiredCodes: scCurrentCodes, discountString: scCurrentCodes.join(',') };
      }
    }

    switch (scExistingCodePolicy) {
      case 'replace':
        scDesiredCodes = scCodesToApply.slice();
        break;
      case 'append':
        scDesiredCodes = scUniqueDiscountCodes([...scFilteredCodes, ...scCodesToApply]);
        break;
      case 'skip':
      default: {
        const scMissingCodesToApply = scCodesToApply.filter((scCode) => !scFilteredCodes.includes(scCode));

        if (scMissingCodesToApply.length > 0 && scFilteredCodes.length === 0) {
          scDesiredCodes = scUniqueDiscountCodes([...scFilteredCodes, ...scMissingCodesToApply]);
        } else {
          scDesiredCodes = scFilteredCodes.slice();
        }
        break;
      }
    }

    const scCurrentDiscountString = scCurrentCodes.join(',');
    const scDesiredDiscountString = scDesiredCodes.join(',');

    if (scCurrentDiscountString === scDesiredDiscountString) {
      if (scCodesToApply.some((scCode) => scCurrentCodes.includes(scCode))) {
        scReason = 'target-code-already-applied';
      } else if (
        scExistingCodePolicy === 'skip' &&
        scCodesToApply.some((scCode) => !scCurrentCodes.includes(scCode)) &&
        scFilteredCodes.length > 0
      ) {
        scReason = 'existing-code-policy-skip';
      }

      return {
        shouldSync: false,
        reason: scReason,
        desiredCodes: scDesiredCodes,
        discountString: scDesiredDiscountString,
      };
    }

    if (scCodesToRemove.some((scCode) => scCurrentCodes.includes(scCode))) {
      scReason = 'remove-codes-on-page-entry';
    }

    if (scCodesToApply.some((scCode) => !scCurrentCodes.includes(scCode)) && scDesiredCodes.some((scCode) => scCodesToApply.includes(scCode))) {
      scReason = 'apply-codes-on-page-entry';
    }

    return {
      shouldSync: true,
      reason: scReason,
      desiredCodes: scDesiredCodes,
      discountString: scDesiredDiscountString,
    };
  }

  scBuildNextDiscountString(scState) {
    const scTargetCode = scNormalizeDiscountCode(scState?.targetCode);
    const scExistingCodes = scUniqueDiscountCodes(scState?.codes || []);

    if (!scTargetCode) {
      return null;
    }

    if (scExistingCodes.includes(scTargetCode)) {
      return scExistingCodes.join(',');
    }

    switch (this.scGetExistingCodePolicy(scState)) {
      case 'replace':
        return scTargetCode;
      case 'append':
        return scUniqueDiscountCodes([...scExistingCodes, scTargetCode]).join(',');
      case 'skip':
      default:
        if (scExistingCodes.length > 0) {
          return null;
        }

        return scTargetCode;
    }
  }

  scBuildDiscountStringWithoutCode(scCodeToRemove, scState = this.scCurrentState) {
    const scNormalizedCodeToRemove = scNormalizeDiscountCode(scCodeToRemove);
    const scExistingCodes = scUniqueDiscountCodes(scState?.codes || []);

    if (!scNormalizedCodeToRemove) {
      return scExistingCodes.join(',');
    }

    return scExistingCodes
      .filter((scCode) => scCode !== scNormalizedCodeToRemove)
      .join(',');
  }

  scGetPrimaryTargetCode(scState = this.scCurrentState) {
    return scNormalizeDiscountCode(
      scState?.targetCode
      || scState?.targetCodes?.[0]
      || window.__scDiscountState?.targetCode
      || window.__scDiscountState?.targetCodes?.[0]
      || ''
    );
  }

  async scApplyTargetCode(scState = this.scCurrentState) {
    if (this.scApplyInFlight) return null;

    const scDiscountString = this.scBuildNextDiscountString(scState);
    if (!scDiscountString) {
      this.scSetState({
        ...scState,
        status: 'skipped',
        reason: 'existing-code-policy-skip',
        updatedAt: Date.now(),
      }, { scEventName: 'sc:discount-auto-apply:skip' });
      this.scLogDebug('apply-skipped', {
        reason: 'discount-string-empty',
        state: scBuildStateDebugSummary(scState),
      });
      return null;
    }

    this.scApplyInFlight = true;
    this.scSetState({
      ...scState,
      status: 'applying',
      reason: 'target-code-apply-start',
    });

    this.scEmit('sc:discount-auto-apply:start', {
      code: scState?.targetCode || null,
      discountString: scDiscountString,
      state: scState,
      pageContext: scState.pageContext,
    });

    try {
      const scCart = await this.scApplyDiscountString(scDiscountString);
      this.scMarkAppliedInSession();
      this.scHandleSuccessfulCartUpdate(scCart, {
        scReason: 'target-code-applied',
        scDiscountString: scDiscountString,
      });

      return scCart;
    } catch (scApplyError) {
      const scErroredState = {
        ...this.scBuildDiscountState(null),
        ...scState,
        status: 'error',
        reason: scApplyError.message,
        updatedAt: Date.now(),
      };

      this.scSetState(scErroredState, { scEventName: 'sc:discount-auto-apply:error' });
      this.scLogDebug('apply-error', {
        error: scApplyError,
        state: scBuildStateDebugSummary(scErroredState),
      });

      if (window.toastr && typeof window.toastr.error === 'function') {
        window.toastr.error('Discount code could not be applied automatically.');
      }

      this.scDebugLog('Apply failed', scApplyError);
      throw scApplyError;
    } finally {
      this.scApplyInFlight = false;
    }
  }

  async scRemoveDiscountCode(scCodeToRemove = this.scGetPrimaryTargetCode(), scState = this.scCurrentState) {
    if (this.scApplyInFlight) return null;

    const scNormalizedCodeToRemove = scNormalizeDiscountCode(scCodeToRemove);
    const scExistingCodes = scUniqueDiscountCodes(scState?.codes || []);

    if (!scNormalizedCodeToRemove || !scExistingCodes.includes(scNormalizedCodeToRemove)) {
      const scSkippedState = {
        ...scState,
        status: 'skipped',
        reason: 'remove-code-not-present',
        updatedAt: Date.now(),
      };

      this.scSetState(scSkippedState, { scEventName: 'sc:discount-remove:skip' });
      this.scLogDebug('remove-skipped', {
        code: scNormalizedCodeToRemove,
        state: scBuildStateDebugSummary(scState),
      });
      return null;
    }

    this.scApplyInFlight = true;
    this.scSetState({
      ...scState,
      status: 'applying',
      reason: 'target-code-remove-start',
    });

    const scDiscountString = this.scBuildDiscountStringWithoutCode(scNormalizedCodeToRemove, scState);
    this.scEmit('sc:discount-remove:start', {
      code: scNormalizedCodeToRemove,
      discountString: scDiscountString,
      state: scState,
      pageContext: scState.pageContext,
    });

    try {
      const scCart = await this.scApplyDiscountString(scDiscountString);
      const scReason = scDiscountString ? 'discount-code-removed' : 'discounts-cleared';

      this.scHandleSuccessfulCartUpdate(scCart, {
        scReason,
        scDiscountString: scDiscountString,
      });

      this.scEmit('sc:discount-remove:success', {
        code: scNormalizedCodeToRemove,
        discountString: scDiscountString,
        state: this.scCurrentState,
      });

      this.scLogDebug('remove-success', {
        code: scNormalizedCodeToRemove,
        discountString: scDiscountString,
        state: scBuildStateDebugSummary(this.scCurrentState),
      });

      return scCart;
    } catch (scRemoveError) {
      const scErroredState = {
        ...this.scBuildDiscountState(null),
        ...scState,
        status: 'error',
        reason: scRemoveError.message,
        updatedAt: Date.now(),
      };

      this.scSetState(scErroredState, { scEventName: 'sc:discount-remove:error' });
      this.scLogDebug('remove-error', {
        code: scNormalizedCodeToRemove,
        error: scRemoveError,
        state: scBuildStateDebugSummary(scErroredState),
      });

      if (window.toastr && typeof window.toastr.error === 'function') {
        window.toastr.error('Discount code could not be removed automatically.');
      }

      throw scRemoveError;
    } finally {
      this.scApplyInFlight = false;
    }
  }

  async scSyncPageDiscountCodes(scState = this.scCurrentState, scSyncPlan = this.scResolvePageDiscountSyncPlan(scState)) {
    if (this.scApplyInFlight) return null;

    if (!scSyncPlan?.shouldSync) {
      this.scSetState({
        ...scState,
        status: 'skipped',
        reason: scSyncPlan?.reason || 'sync-not-required',
        updatedAt: Date.now(),
      }, { scEventName: 'sc:discount-sync:skip' });

      this.scLogDebug('sync-skipped', {
        syncPlan: scSerializeDebugValue(scSyncPlan),
        state: scBuildStateDebugSummary(scState),
      });
      return null;
    }

    this.scApplyInFlight = true;
    this.scSetState({
      ...scState,
      status: 'applying',
      reason: scSyncPlan.reason || 'sync-start',
    });

    this.scEmit('sc:discount-sync:start', {
      discountString: scSyncPlan.discountString,
      desiredCodes: scSyncPlan.desiredCodes,
      reason: scSyncPlan.reason,
      state: scState,
      pageContext: scState.pageContext,
    });

    try {
      const scCart = await this.scApplyDiscountString(scSyncPlan.discountString);

      this.scHandleSuccessfulCartUpdate(scCart, {
        scReason: scSyncPlan.reason || 'discount-sync-success',
        scDiscountString: scSyncPlan.discountString,
      });

      this.scEmit('sc:discount-sync:success', {
        discountString: scSyncPlan.discountString,
        desiredCodes: scSyncPlan.desiredCodes,
        reason: scSyncPlan.reason,
        state: this.scCurrentState,
      });

      this.scLogDebug('sync-success', {
        syncPlan: scSerializeDebugValue(scSyncPlan),
        state: scBuildStateDebugSummary(this.scCurrentState),
      });

      return scCart;
    } catch (scSyncError) {
      const scErroredState = {
        ...this.scBuildDiscountState(null),
        ...scState,
        status: 'error',
        reason: scSyncError.message,
        updatedAt: Date.now(),
      };

      this.scSetState(scErroredState, { scEventName: 'sc:discount-sync:error' });
      this.scLogDebug('sync-error', {
        syncPlan: scSerializeDebugValue(scSyncPlan),
        error: scSyncError,
        state: scBuildStateDebugSummary(scErroredState),
      });

      if (window.toastr && typeof window.toastr.error === 'function') {
        window.toastr.error('Discount codes could not be synchronized automatically.');
      }

      throw scSyncError;
    } finally {
      this.scApplyInFlight = false;
    }
  }

  async scApplyDiscountString(scDiscountString) {
    const scRequestBody = {
      discount: scDiscountString,
    };

    if (this.scConfig.requestSections.length > 0) {
      scRequestBody.sections = this.scConfig.requestSections;
    }

    this.scLogDebug('cart-update-request', {
      requestBody: scRequestBody,
    });

    const scCartUpdateUrl = `${window.themeVariables?.routes?.cartUpdateUrl || '/cart/update'}.js`;
    const scResponse = await fetch(scCartUpdateUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(scRequestBody),
    });

    const scCart = await scResponse.json();
    if (!scResponse.ok) {
      const scErrorMessage = scCart?.description || scCart?.message || `Cart update failed with status ${scResponse.status}.`;
      throw new Error(scErrorMessage);
    }

    this.scLogDebug('cart-update-response', {
      cart: scBuildCartDebugSummary(scCart),
    });

    return scCart;
  }

  scHandleSuccessfulCartUpdate(scCart, { scReason = 'cart-updated', scDiscountString = null } = {}) {
    const scState = {
      ...this.scBuildDiscountState(scCart),
      status: 'applied',
      reason: scReason,
      updatedAt: Date.now(),
    };

    this.scSetState(scState, { scEventName: 'sc:discount-auto-apply:success' });
    this.scLogDebug('apply-success', {
      cart: scBuildCartDebugSummary(scCart),
      state: scBuildStateDebugSummary(scState),
    });

    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true,
      detail: {
        cart: scCart,
        source: 'sc-discount-engine',
      },
    }));

    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: {
        cart: scCart,
        source: 'sc-discount-engine',
      },
    }));

    this.scDebugLog('Discount update applied', {
      scDiscountString,
      scCodes: scState.codes,
    });
  }

  scHandleCartEvent(scEvent) {
    if (!scEvent?.detail?.cart) return;

    const scPreviousEvent = this.scLastPairedCartEvent;
    const scSource = scEvent.detail.source || null;
    if (scPreviousEvent?.type === 'cart:updated' && scEvent.type === 'cart:refresh'
      && scPreviousEvent.cart === scEvent.detail.cart
      && (scPreviousEvent.source === scSource || !scSource)) return;

    const scPairedEvent = { cart: scEvent.detail.cart, source: scSource, type: scEvent.type };
    this.scLastPairedCartEvent = scPairedEvent;
    Promise.resolve().then(() => {
      if (this.scLastPairedCartEvent === scPairedEvent) this.scLastPairedCartEvent = null;
    });

    const scDerivedState = this.scBuildDiscountState(scEvent.detail.cart);
    const scDerivedStatus = scEvent.detail.source === 'sc-discount-engine' && scDerivedState.hasTargetCode
      ? 'applied'
      : (this.scCurrentState.status === 'applying' ? 'applying' : 'idle');

    const scEventState = {
      ...scDerivedState,
      status: scDerivedStatus,
      reason: scEvent.detail.source || null,
      updatedAt: Date.now(),
    };

    window.__scDiscountState = scEventState;
    this.scCurrentState = scEventState;
    this.scLogDebug('cart-event-received', {
      source: scEvent.detail.source || 'cart-event',
      cart: scBuildCartDebugSummary(scEvent.detail.cart),
      state: scBuildStateDebugSummary(scEventState),
    });
    this.scEmit('sc:discount-state:changed', {
      cart: scEvent.detail.cart,
      state: scEventState,
      reason: scEvent.detail.source || 'cart-event',
    });
  }

  scSetState(scState, { scEventName = null } = {}) {
    const scNextState = scCreateDiscountState({
      ...this.scCurrentState,
      ...scState,
      updatedAt: Date.now(),
    });

    this.scCurrentState = scNextState;
    window.__scDiscountState = scNextState;
    this.scLogDebug('state-set', {
      eventName: scEventName,
      state: scBuildStateDebugSummary(scNextState),
    });

    this.scEmit('sc:discount-state:changed', {
      state: scNextState,
      reason: scNextState.reason,
    });

    if (scEventName) {
      this.scEmit(scEventName, {
        state: scNextState,
        reason: scNextState.reason,
      });
    }
  }

  scEmit(scEventName, scDetail = {}) {
    document.documentElement.dispatchEvent(new CustomEvent(scEventName, {
      bubbles: true,
      detail: {
        source: 'sc-discount-engine',
        ...scDetail,
      },
    }));
  }

  scHasAppliedInSession() {
    if (!this.scConfig.applyOncePerSession) return false;

    try {
      return sessionStorage.getItem(this.scConfig.sessionStorageKey) === '1';
    } catch {
      return false;
    }
  }

  scMarkAppliedInSession() {
    if (!this.scConfig.applyOncePerSession) return;

    try {
      sessionStorage.setItem(this.scConfig.sessionStorageKey, '1');
    } catch (scSessionStorageError) {
      this.scDebugLog('Session storage unavailable for apply marker', scSessionStorageError);
    }
  }

  async scRefresh() {
    this.scLogDebug('manual-refresh-start');
    const scCart = await this.scGetCart();
    const scState = this.scBuildDiscountState(scCart);
    this.scSetState({
      ...scState,
      status: 'idle',
      reason: 'manual-refresh',
    });

    const scSyncPlan = this.scResolvePageDiscountSyncPlan(scState);
    this.scLogDebug('sync-plan', {
      syncPlan: scSerializeDebugValue(scSyncPlan),
    });

    if (scSyncPlan.shouldSync) {
      await this.scSyncPageDiscountCodes(scState, scSyncPlan);
    }

    return this.scCurrentState;
  }

  scLogDebug(scEventName, scDetail = {}) {
    const scEntry = {
      event: scEventName,
      detail: {
        ...scSerializeDebugValue(scDetail),
      },
    };

    scPushDiscountDebugLog(this.scConfig, scEntry);

    if (this.scConfig.debugConsole) {
      console.log('[SC Discount Debug]', scEventName, scEntry.detail);
    }
  }
}

if (!customElements.get('sc-discount-engine')) {
  customElements.define('sc-discount-engine', ScDiscountEngine);
}

function scBootstrapDiscountEngine() {
  if (document.querySelector('sc-discount-engine')) {
    window.scDiscountEngine = document.querySelector('sc-discount-engine');
    return;
  }

  const scDiscountEngineElement = document.createElement('sc-discount-engine');
  document.body.appendChild(scDiscountEngineElement);
  window.scDiscountEngine = scDiscountEngineElement;
}

window.scRefreshDiscountEngine = async function scRefreshDiscountEngine() {
  if (!window.scDiscountEngine || typeof window.scDiscountEngine.scRefresh !== 'function') {
    return null;
  }

  return await window.scDiscountEngine.scRefresh();
};

window.scSyncPageDiscountCodes = async function scSyncPageDiscountCodes() {
  if (!window.scDiscountEngine || typeof window.scDiscountEngine.scSyncPageDiscountCodes !== 'function') {
    return null;
  }

  const scLatestState = window.scDiscountEngine.scCurrentState || window.__scDiscountState || scCreateDiscountState();
  const scSyncPlan = window.scDiscountEngine.scResolvePageDiscountSyncPlan(scLatestState);
  return await window.scDiscountEngine.scSyncPageDiscountCodes(scLatestState, scSyncPlan);
};

window.scRemoveTargetDiscountCode = async function scRemoveTargetDiscountCode() {
  if (!window.scDiscountEngine || typeof window.scDiscountEngine.scRemoveDiscountCode !== 'function') {
    return null;
  }

  return await window.scDiscountEngine.scRemoveDiscountCode(window.scDiscountEngine.scGetPrimaryTargetCode());
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scBootstrapDiscountEngine);
} else {
  scBootstrapDiscountEngine();
}
/* SC End: Discount Engine */
/* SC Start: Discount Showcase Bridge */
function scGetDiscountShowcaseAppliedCodes(scCart = null) {
  const scStateCodes = Array.isArray(window.__scDiscountState?.codes) ? window.__scDiscountState.codes : [];
  const scCartCodes = scExtractDiscountCodesFromCart(scCart);
  const scCodes = scUniqueDiscountCodes([...scStateCodes, ...scCartCodes]);

  // Prefer the codes Shopify actually applies; keep all codes when none applies (e.g. empty cart)
  const scApplicableCodes = Array.isArray(scCart?.discount_codes)
    ? scCart.discount_codes
      .filter((scDiscountCodeEntry) => scDiscountCodeEntry?.applicable === true && typeof scDiscountCodeEntry.code === 'string')
      .map((scDiscountCodeEntry) => scNormalizeDiscountCode(scDiscountCodeEntry.code))
    : [];

  if (scApplicableCodes.length > 0) {
    return scCodes.filter((scCode) => scApplicableCodes.includes(scCode));
  }

  return scCodes;
}

function scGetDiscountShowcaseProductCollections(scShowcaseElement) {
  const scProductCollectionsAttribute = scShowcaseElement?.getAttribute('data-product-collections');
  if (!scProductCollectionsAttribute) {
    return [];
  }

  try {
    const scProductCollections = JSON.parse(scProductCollectionsAttribute);
    return Array.isArray(scProductCollections) ? scProductCollections : [];
  } catch {
    return [];
  }
}

function scNormalizeDiscountShowcaseRule(scRule = {}) {
  const scDiscountType = typeof scRule?.discount_type === 'string' && scRule.discount_type.trim()
    ? scRule.discount_type.trim()
    : (typeof scRule?.type === 'string' && scRule.type.trim() ? scRule.type.trim() : 'percentage');
  const scPercentageValue = Number.isFinite(Number(scRule?.percentage)) ? Number(scRule.percentage) : 0;
  const scDiscountValue = Number.isFinite(Number(scRule?.discount_value))
    ? Number(scRule.discount_value)
    : (
      Number.isFinite(Number(scRule?.value))
        ? Number(scRule.value)
        : (
          Number.isFinite(Number(scRule?.fixed_amount))
            ? Number(scRule.fixed_amount)
            : scPercentageValue
        )
    );

  return {
    type: scDiscountType === 'fixed_amount' ? 'fixed_amount' : 'percentage',
    percentage: scPercentageValue,
    value: scDiscountValue,
    collection_based: !!scRule?.collection_based,
    discounted_collection_handle: scRule?.discounted_collection_handle || null,
    applies_once_per_order: !!scRule?.applies_once_per_order,
    priority: Number.isFinite(Number(scRule?.priority)) ? Number(scRule.priority) : 0,
    active: typeof scRule?.active === 'boolean' ? scRule.active : true,
  };
}

function scGetDiscountShowcaseConfiguredRules() {
  const scConfiguredRules = window.scDiscountEngineConfig?.discountRules;
  return scConfiguredRules && typeof scConfiguredRules === 'object' ? scConfiguredRules : {};
}

function scGetDiscountShowcaseConfiguredRule(scPrefix) {
  const scConfiguredRules = scGetDiscountShowcaseConfiguredRules();
  const scNormalizedPrefix = scNormalizeDiscountCode(scPrefix);
  const scNormalizedPrefixWithoutUnderscore = scNormalizedPrefix.replace('_', '');

  if (!scNormalizedPrefix) {
    return null;
  }

  const scRuleKeyCandidates = [
    scPrefix,
    scNormalizedPrefix,
    scNormalizedPrefixWithoutUnderscore,
    String(scPrefix || '').toLowerCase(),
    scNormalizedPrefix.toLowerCase(),
    scNormalizedPrefixWithoutUnderscore.toLowerCase(),
  ].filter(Boolean);

  for (const scRuleKey of scRuleKeyCandidates) {
    if (scConfiguredRules[scRuleKey]) {
      return scConfiguredRules[scRuleKey];
    }
  }

  const scMatchingRuleKey = Object.keys(scConfiguredRules).find((scRuleKey) => {
    const scNormalizedRuleKey = scNormalizeDiscountCode(scRuleKey);
    return scNormalizedRuleKey === scNormalizedPrefix
      || scNormalizedRuleKey.replace('_', '') === scNormalizedPrefixWithoutUnderscore;
  });

  return scMatchingRuleKey ? scConfiguredRules[scMatchingRuleKey] : null;
}

function scBuildDiscountShowcaseSyntheticRule(scCode) {
  const scNormalizedCode = scNormalizeDiscountCode(scCode);
  const scState = window.__scDiscountState;

  if (!scState || !scNormalizedCode || scState.targetCode !== scNormalizedCode || !(scState.targetPercentage > 0)) {
    return null;
  }

  return scNormalizeDiscountShowcaseRule({
    type: 'percentage',
    percentage: scState.targetPercentage,
    value: scState.targetPercentage,
    collection_based: false,
    discounted_collection_handle: null,
  });
}

async function scResolveDiscountShowcaseRule(scInstance, scCode) {
  if (!scInstance || !scCode) {
    return null;
  }

  // A rule for the full code (e.g. TFC_ADA25) wins; the prefix rule (e.g. TFC_) is the fallback
  const scFullCodeRule = scGetDiscountShowcaseConfiguredRule(scCode);

  if (scFullCodeRule) {
    return scNormalizeDiscountShowcaseRule(scFullCodeRule);
  }

  const scPrefix = typeof scInstance.extractPrefix === 'function'
    ? scInstance.extractPrefix(scCode)
    : scCode;
  const scConfiguredRule = scGetDiscountShowcaseConfiguredRule(scPrefix);

  if (scConfiguredRule) {
    return scNormalizeDiscountShowcaseRule(scConfiguredRule);
  }

  const scStructuredRule = typeof scInstance.getRuleForPrefix === 'function'
    ? await scInstance.getRuleForPrefix(scPrefix)
    : null;

  if (scStructuredRule) {
    return scNormalizeDiscountShowcaseRule(scStructuredRule);
  }

  if (typeof scInstance.getDiscountPercentageForPrefix === 'function') {
    const scFallbackPercentage = await scInstance.getDiscountPercentageForPrefix(scPrefix);
    if (scFallbackPercentage > 0) {
      return scNormalizeDiscountShowcaseRule({
        type: 'percentage',
        percentage: scFallbackPercentage,
        value: scFallbackPercentage,
      });
    }
  }

  return scBuildDiscountShowcaseSyntheticRule(scCode);
}

function scIsDiscountShowcaseRuleApplicable(scShowcaseElement, scRule) {
  if (!scShowcaseElement || !scRule || !scRule.active) {
    return false;
  }

  if (!scRule.collection_based) {
    return true;
  }

  const scTargetCollectionHandle = scRule.discounted_collection_handle;
  if (!scTargetCollectionHandle) {
    return true;
  }

  const scProductCollections = scGetDiscountShowcaseProductCollections(scShowcaseElement);
  return scProductCollections.includes(scTargetCollectionHandle);
}

function scCalculateDiscountShowcaseAmount(scProductPrice, scRule) {
  if (!(scProductPrice > 0) || !scRule) {
    return 0;
  }

  let scDiscountAmount = 0;

  if (scRule.type === 'fixed_amount') {
    if (scRule.applies_once_per_order) {
      return 0;
    }

    scDiscountAmount = scRule.value;
  } else {
    const scPercentage = scRule.percentage > 0 ? scRule.percentage : scRule.value;
    scDiscountAmount = Math.round(scProductPrice * scPercentage / 100);
  }

  if (!Number.isFinite(scDiscountAmount) || scDiscountAmount <= 0) {
    return 0;
  }

  return Math.min(scProductPrice, scDiscountAmount);
}

async function scResolveBestDiscountShowcaseCandidate(scInstance, scShowcaseElement, scDiscountCodes) {
  const scProductPrice = parseFloat(scShowcaseElement?.getAttribute('data-product-price')) || 0;
  if (!(scProductPrice > 0)) {
    return null;
  }

  let scBestCandidate = null;

  for (const scCode of scDiscountCodes) {
    const scNormalizedCode = scNormalizeDiscountCode(scCode);
    if (!scNormalizedCode) continue;

    const scRule = await scResolveDiscountShowcaseRule(scInstance, scNormalizedCode);
    if (!scRule || !scIsDiscountShowcaseRuleApplicable(scShowcaseElement, scRule)) {
      continue;
    }

    const scDiscountAmount = scCalculateDiscountShowcaseAmount(scProductPrice, scRule);
    if (!(scDiscountAmount > 0)) {
      continue;
    }

    const scCandidate = {
      code: scNormalizedCode,
      rule: scRule,
      discountAmount: scDiscountAmount,
      discountedPrice: Math.max(0, Math.round(scProductPrice - scDiscountAmount)),
      savingsPercentage: scRule.type === 'percentage'
        ? Math.round(scRule.percentage > 0 ? scRule.percentage : scRule.value)
        : Math.round((scDiscountAmount / scProductPrice) * 100),
    };

    if (!scBestCandidate || scCandidate.discountAmount > scBestCandidate.discountAmount) {
      scBestCandidate = scCandidate;
      continue;
    }

    if (
      scCandidate.discountAmount === scBestCandidate.discountAmount
      && scCandidate.rule.priority > scBestCandidate.rule.priority
    ) {
      scBestCandidate = scCandidate;
    }
  }

  return scBestCandidate;
}

function scRenderDiscountShowcaseCandidate(scInstance, scShowcaseElement, scCandidate, scAppliedCodes) {
  if (!scInstance || !scShowcaseElement || !scCandidate) {
    return;
  }

  const scFormattedPrice = typeof scInstance.formatMoney === 'function'
    ? scInstance.formatMoney(scCandidate.discountedPrice)
    : String(scCandidate.discountedPrice);
  const scPriceElement = scShowcaseElement.querySelector('[data-discount-price]');
  const scTextElement = scShowcaseElement.querySelector('.discount-showcase__text');
  const scBadgeElement = scShowcaseElement.querySelector('[data-discount-badge]');
  const scBadgeTextElement = scShowcaseElement.querySelector('[data-discount-badge-text]');
  const scIsPdp = scShowcaseElement.classList.contains('discount-showcase--pdp');
  const scUseNewDiscountText = scShowcaseElement.classList.contains('sc-discount-showcase')
    || scShowcaseElement.getAttribute('data-new-text') === 'true';
  const scWithCodeText = scUseNewDiscountText
    ? window.themeVariables?.strings?.discounNewtWithCode
    : window.themeVariables?.strings?.discountWithCode;
  const scExtraSaveText = scUseNewDiscountText
    ? window.themeVariables?.strings?.discounNewExtraSave
    : window.themeVariables?.strings?.discountExtraSave;
  const scResolvedWithCodeText = scWithCodeText || 'with your code';
  const scResolvedExtraSaveText = scExtraSaveText || 'Extra save {{ percentage }}%';

  if (scPriceElement) {
    scPriceElement.textContent = scFormattedPrice;
  }

  if (!scIsPdp) {
    const scMobilePriceElement = scShowcaseElement.querySelector('[data-discount-price-mobile]');
    const scMobilePrefixElement = scShowcaseElement.querySelector('[data-discount-text-prefix-mobile]');
    const scMobileCodeElement = scShowcaseElement.querySelector('[data-discount-code-mobile]');

    if (scMobilePriceElement) {
      scMobilePriceElement.textContent = scFormattedPrice;
    }

    if (scMobilePrefixElement) {
      scMobilePrefixElement.textContent = scResolvedWithCodeText;
    }

    if (scMobileCodeElement) {
      scMobileCodeElement.textContent = scCandidate.code;
    }
  }

  if (scTextElement) {
    const scTextPrefixElement = scTextElement.querySelector('[data-discount-text-prefix]');
    const scCodeElement = scTextElement.querySelector('[data-discount-code]');
    const scCodeButton = scShowcaseElement.querySelector('[data-discount-code-button]');
    const scCopyIcon = scShowcaseElement.querySelector('[data-discount-copy-icon]');
    const scAppliedIcon = scShowcaseElement.querySelector('[data-discount-applied-icon]');

    if (scTextPrefixElement && scCodeElement) {
      scTextPrefixElement.textContent = `${scResolvedWithCodeText} `;
      scCodeElement.textContent = scCandidate.code;

      if (scIsPdp && scCodeButton) {
        const scIsCodeApplied = Array.isArray(scAppliedCodes) && scAppliedCodes.includes(scCandidate.code);

        if (scIsCodeApplied) {
          scCodeButton.classList.add('is-applied');
          if (scCopyIcon) scCopyIcon.style.display = 'none';
          if (scAppliedIcon) scAppliedIcon.style.display = 'inline-flex';
          scCodeButton.style.cursor = 'default';
          scCodeButton.setAttribute('aria-label', 'Discount code applied');
        } else {
          scCodeButton.classList.remove('is-applied');
          if (scCopyIcon) scCopyIcon.style.display = 'inline-flex';
          if (scAppliedIcon) scAppliedIcon.style.display = 'none';
          scCodeButton.style.cursor = 'pointer';
          scCodeButton.setAttribute('aria-label', 'Copy discount code');

          const scNewButton = scCodeButton.cloneNode(true);
          scCodeButton.parentNode.replaceChild(scNewButton, scCodeButton);
          scNewButton.addEventListener('click', (scEvent) => {
            scEvent.preventDefault();
            scEvent.stopPropagation();
            if (typeof scInstance.copyCodeToClipboard === 'function') {
              scInstance.copyCodeToClipboard(scCandidate.code);
            }
          });
        }
      }
    } else {
      scTextElement.textContent = `${scResolvedWithCodeText} ${scCandidate.code}`;
    }
  }

  if (
    scBadgeElement
    && scBadgeTextElement
    && scShowcaseElement.getAttribute('data-show-second-badge') === 'true'
    && scCandidate.rule.type === 'percentage'
    && scCandidate.savingsPercentage > 0
  ) {
    scBadgeTextElement.textContent = scResolvedExtraSaveText
      .replace('{{ percentage }}', scCandidate.savingsPercentage)
      .replace('{{percentage}}', scCandidate.savingsPercentage)
      .replace('@percentage@', scCandidate.savingsPercentage);
    scBadgeElement.style.display = '';
  } else if (scBadgeElement) {
    scBadgeElement.style.display = 'none';
  }

  if (typeof scInstance.showShowcase === 'function') {
    scInstance.showShowcase(scShowcaseElement);
  }
}

function scPatchDiscountShowcaseInstance(scDiscountShowcaseInstance) {
  if (!scDiscountShowcaseInstance || scDiscountShowcaseInstance.__scDiscountBridgePatched) {
    return false;
  }

  const scOriginalUpdateAllShowcases = scDiscountShowcaseInstance.updateAllShowcases.bind(scDiscountShowcaseInstance);
  const scOriginalUpdateShowcase = scDiscountShowcaseInstance.updateShowcase.bind(scDiscountShowcaseInstance);

  scDiscountShowcaseInstance.updateAllShowcases = function scUpdateAllShowcasesWithBridge(scEvent = null) {
    if (typeof this.scAcceptCartSnapshot === 'function') this.scAcceptCartSnapshot(scEvent?.detail?.cart);
    this.scShowcaseUpdateVersion = (this.scShowcaseUpdateVersion || 0) + 1;
    this.scShowcaseUpdateRequested = true;
    if (this.scShowcaseUpdatePromise) return this.scShowcaseUpdatePromise;

    // Cart events and the discount-state bridge share one pass over the latest DOM/cart.
    this.scShowcaseUpdatePromise = Promise.resolve().then(async () => {
      while (this.scShowcaseUpdateRequested) {
        this.scShowcaseUpdateRequested = false;
        const scVersion = this.scShowcaseUpdateVersion;
        const scIsCurrent = () => scVersion === this.scShowcaseUpdateVersion;
        const scShowcases = document.querySelectorAll('[data-discount-showcase]');
        if (scShowcases.length === 0) continue;
        let scCart = null;

        try {
          scCart = await this.getCachedCart();
          if (!scIsCurrent()) continue;
          const scDiscountCodes = scGetDiscountShowcaseAppliedCodes(scCart);
          await Promise.all(Array.from(scShowcases).map((scShowcaseElement) =>
            this.updateShowcase(scShowcaseElement, scDiscountCodes, scIsCurrent)));
        } catch (scDiscountShowcaseBridgeError) {
          if (!scIsCurrent()) continue;
          console.warn('[SC Discount Showcase Bridge] Could not update showcases:', scDiscountShowcaseBridgeError);
          const scFallbackCode = scGetDiscountShowcaseAppliedCodes(scCart)[0] || null;
          await Promise.all(Array.from(scShowcases).map((scShowcaseElement) =>
            scOriginalUpdateShowcase(scShowcaseElement, scFallbackCode, scIsCurrent)));
        }
      }
    }).finally(() => {
      this.scShowcaseUpdatePromise = null;
      if (this.scShowcaseUpdateRequested) return this.updateAllShowcases();
    });
    return this.scShowcaseUpdatePromise;
  };

  scDiscountShowcaseInstance.updateShowcase = async function scUpdateShowcaseWithBridge(scShowcaseElement, scDiscountCodes, scIsCurrent = null) {
    const scNormalizedDiscountCodes = Array.isArray(scDiscountCodes)
      ? scUniqueDiscountCodes(scDiscountCodes)
      : scGetDiscountShowcaseAppliedCodes();

    const scBestCandidate = await scResolveBestDiscountShowcaseCandidate(this, scShowcaseElement, scNormalizedDiscountCodes);
    if (scIsCurrent && !scIsCurrent()) return;

    if (!scBestCandidate) {
      // A code with its own full-code rule that doesn't cover this product must not fall back to the broader prefix rule.
      // Show the theme's fallback code instead (e.g. the sale code); without a fallback the showcase is hidden.
      const scHasFullCodeRule = scNormalizedDiscountCodes.some((scCode) => !!scGetDiscountShowcaseConfiguredRule(scCode));
      if (scHasFullCodeRule) {
        return await scOriginalUpdateShowcase(scShowcaseElement, null);
      }

      const scFallbackCode = scNormalizedDiscountCodes[0] || null;
      return await scOriginalUpdateShowcase(scShowcaseElement, scFallbackCode, scIsCurrent);
    }

    scRenderDiscountShowcaseCandidate(this, scShowcaseElement, scBestCandidate, scNormalizedDiscountCodes);
  };

  scDiscountShowcaseInstance.__scDiscountBridgePatched = true;
  scDiscountShowcaseInstance.__scOriginalUpdateAllShowcases = scOriginalUpdateAllShowcases;
  scDiscountShowcaseInstance.__scOriginalUpdateShowcase = scOriginalUpdateShowcase;
  window.scDiscountShowcaseBridge = scDiscountShowcaseInstance;
  return true;
}

function scSetupDiscountShowcaseBridge() {
  if (window.discountShowcaseInstance) {
    const scWasPatched = scPatchDiscountShowcaseInstance(window.discountShowcaseInstance);
    if (scWasPatched && typeof window.discountShowcaseInstance.updateAllShowcases === 'function') {
      Promise.resolve(window.discountShowcaseInstance.updateAllShowcases()).catch((scDiscountShowcaseBridgeError) => {
        console.error('[SC Discount Showcase Bridge] Initial refresh failed.', scDiscountShowcaseBridgeError);
      });
    }
    return true;
  }

  return false;
}

document.documentElement.addEventListener('sc:discount-state:changed', (scEvent) => {
  scSetupDiscountShowcaseBridge();
  if (window.discountShowcaseInstance) {
    Promise.resolve(window.discountShowcaseInstance.updateAllShowcases(scEvent)).catch((scDiscountShowcaseError) => {
      console.error('[SC] Discount showcase refresh failed.', scDiscountShowcaseError);
    });
  } else {
    scRefreshDiscountShowcases();
  }
});

if (!window.customElements.get('sc-promo-collection-video')) {
  window.customElements.define('sc-promo-collection-video', class ScPromoCollectionVideo extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        return;
      }

      this.initialized = true;
      this.nativeVideo = this.querySelector('native-video');
      this.preview = this.querySelector('[data-sc-promo-video-preview]');
      this.video = this.querySelector('video');

      this.hidePreview = () => {
        this.classList.add('is-playing');
      };
      this.playVideo = () => {
        this.nativeVideo?.play();
      };

      this.addEventListener('video:played', this.hidePreview);
      this.video?.addEventListener('play', this.hidePreview);
      this.preview?.addEventListener('click', this.playVideo);

      if (this.video && !this.video.paused && !this.video.ended) {
        this.hidePreview();
      }
    }

    disconnectedCallback() {
      this.removeEventListener('video:played', this.hidePreview);
      this.video?.removeEventListener('play', this.hidePreview);
      this.preview?.removeEventListener('click', this.playVideo);
      this.initialized = false;
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (scSetupDiscountShowcaseBridge()) {
      return;
    }

    let scDiscountShowcaseBridgeAttempts = 0;
    const scDiscountShowcaseBridgeInterval = window.setInterval(() => {
      scDiscountShowcaseBridgeAttempts += 1;

      if (scSetupDiscountShowcaseBridge() || scDiscountShowcaseBridgeAttempts >= 40) {
        window.clearInterval(scDiscountShowcaseBridgeInterval);
      }
    }, 250);
  });
} else {
  scSetupDiscountShowcaseBridge();
}
/* SC End: Discount Showcase Bridge */

/* SC Start: Discount Showcase In Quick Buy */
// The quick buy drawer/popover is fetched when it opens, so its showcase has to be rendered after loading (DEV-4698).
document.addEventListener('openable-element:open', (scEvent) => {
  const scTarget = scEvent.target;
  if (!scTarget || !scTarget.matches || !scTarget.matches('quick-buy-drawer, quick-buy-popover')) return;
  if (!scTarget.querySelector('[data-discount-showcase]')) return;

  scRefreshDiscountShowcases();
});
/* SC End: Discount Showcase In Quick Buy */

/* SC Start: Add to Cart Price */
(() => {
  const scAddToCartButtonSelector = '[data-sc-addtocart-price-button]';
  const scAddToCartPriceSelector = '[data-sc-addtocart-price]';

  function scGetCurrencyFormat() {
    const scSettings = window.themeVariables && window.themeVariables.settings;

    if (!scSettings) {
      return '';
    }

    return scSettings.currencyCodeEnabled
      ? scSettings.moneyWithCurrencyFormat
      : scSettings.moneyFormat;
  }

  function scFormatAddToCartPrice(scPrice) {
    if (typeof window.formatMoney === 'function') {
      return window.formatMoney(scPrice, scGetCurrencyFormat());
    }

    return String(scPrice);
  }

  function scGetButtonTextContainer(scButtonElement) {
    if (!scButtonElement) {
      return null;
    }

    if (scButtonElement.getAttribute('is') === 'loader-button') {
      return scButtonElement.querySelector('.loader-button__text') || scButtonElement.firstElementChild;
    }

    return scButtonElement;
  }

  function scUpdateAddToCartPriceButton(scButtonElement, scVariant) {
    const scTextContainer = scGetButtonTextContainer(scButtonElement);

    if (!scTextContainer) {
      return;
    }

    const scExistingPriceElement = scTextContainer.querySelector(scAddToCartPriceSelector);
    const scShouldShowPrice = Boolean(scVariant && scVariant.available && !scButtonElement.disabled && !scButtonElement.hidden);

    if (!scShouldShowPrice) {
      if (scExistingPriceElement) {
        scExistingPriceElement.remove();
      }

      return;
    }

    const scPriceText = ` - ${scFormatAddToCartPrice(scVariant.price)}`;
    const scPriceElement = scExistingPriceElement || document.createElement('span');

    scPriceElement.className = 'sc-button-addtocart-price';
    scPriceElement.setAttribute('data-sc-addtocart-price', '');

    if (scPriceElement.textContent !== scPriceText) {
      scPriceElement.textContent = scPriceText;
    }

    if (!scExistingPriceElement) {
      scTextContainer.appendChild(scPriceElement);
    }
  }

  function scUpdateAddToCartPriceForForm(scFormElement, scVariant) {
    if (!scFormElement) {
      return;
    }

    scFormElement.querySelectorAll(scAddToCartButtonSelector).forEach((scButtonElement) => {
      scUpdateAddToCartPriceButton(scButtonElement, scVariant);
    });
  }

  document.addEventListener('variant:changed', (scEvent) => {
    const scVariant = scEvent.detail && scEvent.detail.variant;
    const scFormElement = scEvent.target;

    window.requestAnimationFrame(() => {
      scUpdateAddToCartPriceForForm(scFormElement, scVariant);
    });
  });

  const scInitAddToCartPrices = () => {
    document.querySelectorAll(scAddToCartButtonSelector).forEach((scButtonElement) => {
      const scFormElement = scButtonElement.closest('form');
      const scVariantInput = scFormElement && scFormElement.querySelector('[data-product-form-variant-input]');
      const scPriceElement = scButtonElement.querySelector(scAddToCartPriceSelector);
      const scInitialPrice = scPriceElement ? scPriceElement.textContent.trim() : '';

      if (!scVariantInput || !scInitialPrice || scButtonElement.disabled || scButtonElement.hidden) {
        return;
      }

      scButtonElement.dataset.scAddtocartInitialVariantId = scVariantInput.value;
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scInitAddToCartPrices);
  } else {
    scInitAddToCartPrices();
  }
})();
/* SC End: Add to Cart Price */

/* SC Start: Complete the Look Slider */
if (!customElements.get('complete-the-look-slider')) {
  class ScCompleteTheLookSlider extends HTMLElement {
    connectedCallback() {
      if (this.scIsInitialized) return;

      this.scIsInitialized = true;
      this.scBoundResizeHandler = this.scScheduleSync.bind(this);
      this.scSyncRequested = false;
      this.scSwiperInstance = null;

      window.addEventListener('resize', this.scBoundResizeHandler);
      this.scSync();
    }

    disconnectedCallback() {
      if (this.scBoundResizeHandler) {
        window.removeEventListener('resize', this.scBoundResizeHandler);
      }

      this.scDestroySwiper();
    }

    get scSwiperContainer() {
      return this.querySelector('[data-sc-ctl-swiper]');
    }

    get scWrapper() {
      return this.querySelector('[data-sc-ctl-wrapper]');
    }

    get scSlides() {
      return Array.from(this.querySelectorAll('[data-sc-ctl-slide]'));
    }

    get scNavigation() {
      return this.querySelector('[data-sc-ctl-navigation]');
    }

    get scNextButton() {
      return this.querySelector('[data-sc-ctl-nav-next]');
    }

    get scPreviousButton() {
      return this.querySelector('[data-sc-ctl-nav-prev]');
    }

    scScheduleSync() {
      if (this.scSyncRequested) return;

      this.scSyncRequested = true;

      requestAnimationFrame(() => {
        this.scSyncRequested = false;
        if (this.scSwiperInstance) {
          this.scSwiperInstance.update();
          this.scUpdateSliderState();
        } else {
          this.scSync();
        }
      });
    }

    scSetNavigationVisible(scIsVisible) {
      if (!this.scNavigation) return;

      this.scNavigation.hidden = !scIsVisible;
    }

    scGetGapWidth() {
      const scComputedStyle = window.getComputedStyle(this);
      const scGap = parseFloat(scComputedStyle.getPropertyValue('--sc-ctl-slide-gap'));

      return Number.isFinite(scGap) ? scGap : 8;
    }

    scHasOverflow() {
      if (!this.scSwiperContainer || !this.scWrapper) return false;

      const scOverflowThreshold = 2;
      const scWrapperWidth = this.scSlides.reduce((scTotalWidth, scSlideElement) => {
        return scTotalWidth + scSlideElement.getBoundingClientRect().width;
      }, 0);
      const scGapsWidth = Math.max(this.scSlides.length - 1, 0) * this.scGetGapWidth();

      return scWrapperWidth + scGapsWidth > this.scSwiperContainer.clientWidth + scOverflowThreshold;
    }

    scHasSwiperOverflow() {
      if (!this.scSwiperInstance) return this.scHasOverflow();

      const scOverflowThreshold = 2;
      return this.scSwiperInstance.virtualSize > this.scSwiperInstance.width + scOverflowThreshold;
    }

    scSyncSlideHeight() {
      this.style.setProperty('--sc-ctl-slide-equal-height', 'auto');

      if (this.scSlides.length <= 1) return;

      const scMaximumSlideHeight = this.scSlides.reduce((scMaxHeight, scSlideElement) => {
        return Math.max(scMaxHeight, scSlideElement.scrollHeight);
      }, 0);

      if (scMaximumSlideHeight > 0) {
        this.style.setProperty('--sc-ctl-slide-equal-height', `${Math.ceil(scMaximumSlideHeight)}px`);
      }
    }

    scSync() {
      const scSlides = this.scSlides;

      this.scSyncSlideHeight();

      if (!this.scSwiperContainer || !this.scWrapper || scSlides.length <= 1) {
        this.dataset.scCtlState = 'single';
        this.scSetNavigationVisible(false);
        this.scDestroySwiper();
        return;
      }

      if (!this.scHasOverflow()) {
        this.dataset.scCtlState = 'no-overflow';
        this.scSetNavigationVisible(false);
        this.scDestroySwiper();
        return;
      }

      this.dataset.scCtlState = 'slider';
      this.scInitializeSwiper();
    }

    scInitializeSwiper() {
      this.scEnsureSwiperReady().then(() => {
        if (!window.Swiper || this.scSwiperInstance || !this.scSwiperContainer) return;

        this.scSwiperInstance = new window.Swiper(this.scSwiperContainer, {
          slidesPerView: 'auto',
          spaceBetween: this.scGetGapWidth(),
          allowTouchMove: true,
          grabCursor: true,
          threshold: 5,
          touchStartPreventDefault: false,
          touchMoveStopPropagation: false,
          watchOverflow: false,
          on: {
            afterInit: () => this.scUpdateSliderState(),
            fromEdge: () => this.scUpdateSliderState(),
            lock: () => this.scUpdateSliderState(),
            reachBeginning: () => this.scUpdateSliderState(),
            reachEnd: () => this.scUpdateSliderState(),
            resize: () => this.scUpdateSliderState(),
            slideChange: () => this.scUpdateSliderState(),
            unlock: () => this.scUpdateSliderState(),
            update: () => this.scUpdateSliderState(),
          },
          navigation: {
            nextEl: this.scNextButton,
            prevEl: this.scPreviousButton,
          },
        });

        this.scUpdateSliderState();
      });
    }

    scUpdateSliderState() {
      if (!this.scSwiperInstance) return;

      this.scSyncSlideHeight();

      const scHasOverflow = this.scHasSwiperOverflow();

      this.scSwiperInstance.allowTouchMove = scHasOverflow;
      this.scSwiperInstance.allowSlideNext = scHasOverflow;
      this.scSwiperInstance.allowSlidePrev = scHasOverflow;

      if (!scHasOverflow) {
        this.scSetNavigationVisible(false);
        return;
      }

      this.dataset.scCtlState = 'slider';
      this.scSetNavigationVisible(true);
    }

    scDestroySwiper() {
      if (!this.scSwiperInstance || typeof this.scSwiperInstance.destroy !== 'function') return;

      this.scSwiperInstance.destroy(true, true);
      this.scSwiperInstance = null;
    }

    scEnsureSwiperReady() {
      if (window.Swiper) return Promise.resolve();
      if (ScCompleteTheLookSlider.scSwiperReadyPromise) return ScCompleteTheLookSlider.scSwiperReadyPromise;

      ScCompleteTheLookSlider.scSwiperReadyPromise = new Promise((resolve) => {
        const scReadyCheckStartAt = Date.now();
        const scMaximumWaitInMilliseconds = 5000;

        const scWaitForSwiper = () => {
          if (window.Swiper) return resolve();
          if (Date.now() - scReadyCheckStartAt > scMaximumWaitInMilliseconds) return resolve();
          requestAnimationFrame(scWaitForSwiper);
        };

        scWaitForSwiper();
      });

      return ScCompleteTheLookSlider.scSwiperReadyPromise;
    }
  }

  customElements.define('complete-the-look-slider', ScCompleteTheLookSlider);
}
/* SC End: Complete the Look Slider */

/* SC Start: Product Tabs */
if (!customElements.get('sc-product-tabs')) {
  class ScProductTabs extends HTMLElement {
    connectedCallback() {
      if (this.scIsInitialized) return;

      this.scIsInitialized = true;
      this.scActiveIndex = 0;
      this.scBoundClickHandler = this.scOnClick.bind(this);
      this.scBoundKeydownHandler = this.scOnKeydown.bind(this);
      this.scBoundResizeHandler = this.scOnResize.bind(this);
      this.scBoundScrollHandler = this.scUpdateAccent.bind(this);
      this.scBoundShopifyBlockSelectHandler = this.scOnShopifyBlockSelect.bind(this);
      this.scBoundPointerDownHandler = this.scOnPointerDown.bind(this);
      this.scBoundPointerMoveHandler = this.scOnPointerMove.bind(this);
      this.scBoundPointerUpHandler = this.scOnPointerUp.bind(this);
      this.scBoundPointerCancelHandler = this.scOnPointerCancel.bind(this);
      this.scResizeObserver = null;
      this.scResizeAnimationFrame = null;
      this.scSwipeState = null;
      this.scMobileSwipeMediaQuery = window.matchMedia('(max-width: 740px)');

      this.addEventListener('click', this.scBoundClickHandler);
      this.addEventListener('keydown', this.scBoundKeydownHandler);
      window.addEventListener('resize', this.scBoundResizeHandler);
      this.scNavigationElement?.addEventListener('scroll', this.scBoundScrollHandler, { passive: true });
      this.scViewportElement?.addEventListener('pointerdown', this.scBoundPointerDownHandler);
      this.scViewportElement?.addEventListener('pointermove', this.scBoundPointerMoveHandler);
      this.scViewportElement?.addEventListener('pointerup', this.scBoundPointerUpHandler);
      this.scViewportElement?.addEventListener('pointercancel', this.scBoundPointerCancelHandler);
      if (window.Shopify && window.Shopify.designMode) {
        this.addEventListener('shopify:block:select', this.scBoundShopifyBlockSelectHandler);
      }

      this.scWrapRichTextTables();
      this.scObserveLayoutChanges();
      this.scBindMediaLoadEvents();
      this.scActivate(0, { shouldFocus: false, shouldScroll: false });
      this.scUpdateLayout();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.scBoundClickHandler);
      this.removeEventListener('keydown', this.scBoundKeydownHandler);
      this.removeEventListener('shopify:block:select', this.scBoundShopifyBlockSelectHandler);
      window.removeEventListener('resize', this.scBoundResizeHandler);
      this.scNavigationElement?.removeEventListener('scroll', this.scBoundScrollHandler);
      this.scViewportElement?.removeEventListener('pointerdown', this.scBoundPointerDownHandler);
      this.scViewportElement?.removeEventListener('pointermove', this.scBoundPointerMoveHandler);
      this.scViewportElement?.removeEventListener('pointerup', this.scBoundPointerUpHandler);
      this.scViewportElement?.removeEventListener('pointercancel', this.scBoundPointerCancelHandler);

      if (this.scResizeObserver) {
        this.scResizeObserver.disconnect();
        this.scResizeObserver = null;
      }

      if (this.scResizeAnimationFrame) {
        window.cancelAnimationFrame(this.scResizeAnimationFrame);
        this.scResizeAnimationFrame = null;
      }
    }

    get scNavigationElement() {
      return this.querySelector('[data-sc-product-tabs-navigation]');
    }

    get scViewportElement() {
      return this.querySelector('[data-sc-product-tabs-viewport]');
    }

    get scTrackElement() {
      return this.querySelector('[data-sc-product-tabs-track]');
    }

    get scButtonElements() {
      return Array.from(this.querySelectorAll('[data-sc-product-tabs-button]'));
    }

    get scPanelElements() {
      return Array.from(this.querySelectorAll('[data-sc-product-tabs-panel]'));
    }

    get scContentElements() {
      return Array.from(this.querySelectorAll('[data-sc-product-tabs-content]'));
    }

    get scActiveButtonElement() {
      return this.scButtonElements[this.scActiveIndex] || null;
    }

    get scActivePanelElement() {
      return this.scPanelElements[this.scActiveIndex] || null;
    }

    scOnClick(event) {
      const scButtonElement = event.target.closest('[data-sc-product-tabs-button]');
      if (!scButtonElement || !this.contains(scButtonElement)) return;

      this.scActivate(this.scGetIndex(scButtonElement), { shouldFocus: false, shouldScroll: true });
    }

    scOnKeydown(event) {
      const scButtonElement = event.target.closest('[data-sc-product-tabs-button]');
      if (!scButtonElement || !this.contains(scButtonElement)) return;

      const scButtonElements = this.scButtonElements;
      if (!scButtonElements.length) return;

      let scNextIndex = this.scGetIndex(scButtonElement);

      if (event.key === 'ArrowRight') {
        scNextIndex = (scNextIndex + 1) % scButtonElements.length;
      } else if (event.key === 'ArrowLeft') {
        scNextIndex = (scNextIndex - 1 + scButtonElements.length) % scButtonElements.length;
      } else if (event.key === 'Home') {
        scNextIndex = 0;
      } else if (event.key === 'End') {
        scNextIndex = scButtonElements.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      this.scActivate(scNextIndex, { shouldFocus: true, shouldScroll: true });
    }

    scOnShopifyBlockSelect(event) {
      const scPanelElement = event.target.closest('[data-sc-product-tabs-panel]');
      if (!scPanelElement || !this.contains(scPanelElement)) return;

      this.scActivate(this.scGetIndex(scPanelElement), {
        shouldFocus: false,
        shouldScroll: !event.detail?.load,
      });
    }

    scOnPointerDown(event) {
      if (!this.scMobileSwipeMediaQuery.matches || !event.isPrimary || event.button !== 0) return;
      if (this.scPanelElements.length <= 1 || !this.scTrackElement || !this.scViewportElement) return;

      this.scSwipeState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        currentX: event.clientX,
        isDragging: false,
        viewportWidth: this.scViewportElement.clientWidth,
      };
    }

    scOnPointerMove(event) {
      if (!this.scSwipeState || event.pointerId !== this.scSwipeState.pointerId) return;

      const scDeltaX = event.clientX - this.scSwipeState.startX;
      const scDeltaY = event.clientY - this.scSwipeState.startY;
      const scAbsoluteDeltaX = Math.abs(scDeltaX);
      const scAbsoluteDeltaY = Math.abs(scDeltaY);

      if (!this.scSwipeState.isDragging) {
        if (scAbsoluteDeltaX < 8 && scAbsoluteDeltaY < 8) return;
        if (scAbsoluteDeltaY > scAbsoluteDeltaX) {
          this.scResetSwipeState();
          return;
        }

        this.scSwipeState.isDragging = true;
        this.scViewportElement?.setPointerCapture?.(event.pointerId);
      }

      event.preventDefault();
      this.scSwipeState.currentX = event.clientX;
      this.scApplySwipeTransform(scDeltaX);
    }

    scOnPointerUp(event) {
      if (!this.scSwipeState || event.pointerId !== this.scSwipeState.pointerId) return;

      const scDeltaX = event.clientX - this.scSwipeState.startX;
      const scSwipeThreshold = Math.min(Math.max(this.scSwipeState.viewportWidth * 0.18, 48), 110);
      let scNextIndex = this.scActiveIndex;

      if (Math.abs(scDeltaX) >= scSwipeThreshold) {
        scNextIndex = scDeltaX < 0 ? this.scActiveIndex + 1 : this.scActiveIndex - 1;
      }

      scNextIndex = Math.max(0, Math.min(scNextIndex, this.scButtonElements.length - 1));
      this.scFinishSwipe(scNextIndex);
    }

    scOnPointerCancel(event) {
      if (!this.scSwipeState || event.pointerId !== this.scSwipeState.pointerId) return;

      this.scFinishSwipe(this.scActiveIndex);
    }

    scApplySwipeTransform(scDeltaX) {
      const scTrackElement = this.scTrackElement;
      if (!scTrackElement || !this.scSwipeState) return;

      const scIsAtFirstSlide = this.scActiveIndex === 0;
      const scIsAtLastSlide = this.scActiveIndex === this.scButtonElements.length - 1;
      let scConstrainedDeltaX = scDeltaX;

      if ((scIsAtFirstSlide && scDeltaX > 0) || (scIsAtLastSlide && scDeltaX < 0)) {
        scConstrainedDeltaX = scDeltaX * 0.28;
      }

      scTrackElement.style.transition = 'none';
      scTrackElement.style.transform = `translateX(calc(${this.scActiveIndex} * -100% + ${Math.round(scConstrainedDeltaX)}px))`;
    }

    scFinishSwipe(scNextIndex) {
      const scTrackElement = this.scTrackElement;

      if (scTrackElement) {
        scTrackElement.style.transition = '';
        scTrackElement.style.transform = '';
      }

      this.scResetSwipeState();
      this.scActivate(scNextIndex, {
        shouldFocus: false,
        shouldScroll: true,
        shouldCenterNavigation: true,
      });
    }

    scResetSwipeState() {
      this.scSwipeState = null;
    }

    scWrapRichTextTables() {
      this.scContentElements.forEach((scContentElement) => {
        this.scWrapTables(scContentElement);
      });
    }

    scWrapTables(scContentElement) {
      scContentElement.querySelectorAll('table').forEach((scTableElement) => {
        if (scTableElement.parentElement?.classList.contains('table-wrapper')) return;

        const scTableWrapperElement = document.createElement('div');
        scTableWrapperElement.className = 'table-wrapper';
        scTableElement.parentNode.insertBefore(scTableWrapperElement, scTableElement);
        scTableWrapperElement.appendChild(scTableElement);
      });
    }

    scActivate(scIndex, options = {}) {
      const scButtonElements = this.scButtonElements;
      const scPanelElements = this.scPanelElements;
      if (!scButtonElements.length || !scPanelElements.length) return;

      const scSafeIndex = Math.max(0, Math.min(scIndex, scButtonElements.length - 1));
      this.scActiveIndex = scSafeIndex;
      this.style.setProperty('--sc-product-tabs-active-index', scSafeIndex);

      scButtonElements.forEach((scButtonElement, scButtonIndex) => {
        const scIsActive = scButtonIndex === scSafeIndex;
        scButtonElement.dataset.scProductTabsActive = scIsActive ? 'true' : 'false';
        scButtonElement.setAttribute('aria-selected', scIsActive ? 'true' : 'false');
        scButtonElement.tabIndex = scIsActive ? 0 : -1;
      });

      scPanelElements.forEach((scPanelElement, scPanelIndex) => {
        const scIsActive = scPanelIndex === scSafeIndex;
        scPanelElement.dataset.scProductTabsActive = scIsActive ? 'true' : 'false';
        scPanelElement.setAttribute('aria-hidden', scIsActive ? 'false' : 'true');

        if ('inert' in scPanelElement) {
          scPanelElement.inert = !scIsActive;
        }
      });

      if (options.shouldFocus) {
        this.scActiveButtonElement?.focus({ preventScroll: true });
      }

      if (options.shouldScroll) {
        if (options.shouldCenterNavigation) {
          this.scScrollActiveButtonToCenter();
        } else {
          this.scActiveButtonElement?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
          });
        }
      }

      this.scUpdateLayout();
    }

    scScrollActiveButtonToCenter() {
      const scButtonElement = this.scActiveButtonElement;
      const scNavigationElement = this.scNavigationElement;
      if (!scButtonElement || !scNavigationElement) return;

      const scMaximumScrollLeft = scNavigationElement.scrollWidth - scNavigationElement.clientWidth;
      if (scMaximumScrollLeft <= 0) return;

      const scButtonRect = scButtonElement.getBoundingClientRect();
      const scNavigationRect = scNavigationElement.getBoundingClientRect();
      const scButtonLeft = scButtonRect.left - scNavigationRect.left + scNavigationElement.scrollLeft;
      const scTargetScrollLeft = scButtonLeft - (scNavigationElement.clientWidth - scButtonRect.width) / 2;
      const scClampedScrollLeft = Math.max(0, Math.min(scTargetScrollLeft, scMaximumScrollLeft));

      scNavigationElement.scrollTo({
        left: scClampedScrollLeft,
        behavior: 'smooth',
      });
    }

    scGetIndex(scElement) {
      const scIndex = Number(scElement?.dataset.scProductTabsIndex);
      return Number.isNaN(scIndex) ? 0 : scIndex;
    }

    scOnResize() {
      if (this.scResizeAnimationFrame) {
        window.cancelAnimationFrame(this.scResizeAnimationFrame);
      }

      this.scResizeAnimationFrame = window.requestAnimationFrame(() => {
        this.scResizeAnimationFrame = null;
        this.scUpdateLayout();
      });
    }

    scUpdateLayout() {
      this.scUpdateViewportHeight();
      this.scUpdateNavigationLineWidth();
      this.scUpdateAccent();
    }

    scUpdateViewportHeight() {
      const scViewportElement = this.scViewportElement;
      const scActivePanelElement = this.scActivePanelElement;
      if (!scViewportElement || !scActivePanelElement) return;

      scViewportElement.style.height = `${scActivePanelElement.offsetHeight}px`;
    }

    scUpdateAccent() {
      const scButtonElement = this.scActiveButtonElement;
      const scNavigationElement = this.scNavigationElement;
      if (!scButtonElement || !scNavigationElement) return;

      const scButtonRect = scButtonElement.getBoundingClientRect();
      const scNavigationRect = scNavigationElement.getBoundingClientRect();
      const scAccentLeft = scButtonRect.left - scNavigationRect.left + scNavigationElement.scrollLeft;

      this.style.setProperty('--sc-product-tabs-accent-width', `${Math.round(scButtonRect.width)}px`);
      this.style.setProperty('--sc-product-tabs-accent-left', `${Math.round(scAccentLeft)}px`);
    }

    scUpdateNavigationLineWidth() {
      const scNavigationElement = this.scNavigationElement;
      if (!scNavigationElement) return;

      const scButtonElements = this.scButtonElements;
      const scNavigationStyles = window.getComputedStyle(scNavigationElement);
      const scNavigationGap = parseFloat(scNavigationStyles.columnGap || scNavigationStyles.gap) || 0;
      const scButtonsWidth = scButtonElements.reduce((scTotalWidth, scButtonElement) => {
        return scTotalWidth + scButtonElement.getBoundingClientRect().width;
      }, 0);
      const scNavigationContentWidth = scButtonsWidth + Math.max(scButtonElements.length - 1, 0) * scNavigationGap;
      const scIsScrollable = scNavigationContentWidth > scNavigationElement.clientWidth + 1;
      const scLineWidth = scIsScrollable ? scNavigationContentWidth : scNavigationElement.clientWidth;

      scNavigationElement.dataset.scProductTabsScrollable = scIsScrollable ? 'true' : 'false';
      this.style.setProperty('--sc-product-tabs-line-width', `${Math.ceil(scLineWidth)}px`);
    }

    scObserveLayoutChanges() {
      if (!window.ResizeObserver) return;

      this.scResizeObserver = new ResizeObserver(() => this.scUpdateLayout());
      if (this.scNavigationElement) {
        this.scResizeObserver.observe(this.scNavigationElement);
      }
      this.scButtonElements.forEach((scButtonElement) => this.scResizeObserver.observe(scButtonElement));
      this.scPanelElements.forEach((scPanelElement) => this.scResizeObserver.observe(scPanelElement));
    }

    scBindMediaLoadEvents() {
      this.querySelectorAll('[data-sc-product-tabs-panel] img').forEach((scImageElement) => {
        if (scImageElement.complete) return;
        scImageElement.addEventListener('load', this.scBoundResizeHandler, { once: true });
      });

      if (document.fonts && typeof document.fonts.ready?.then === 'function') {
        document.fonts.ready.then(() => this.scUpdateLayout());
      }
    }
  }

  customElements.define('sc-product-tabs', ScProductTabs);
}
/* SC End: Product Tabs */

/* SC Start: Product Comparison */
if (!customElements.get('sc-product-comparison')) {
  class ScProductComparison extends HTMLElement {
    connectedCallback() {
      this.scSelectElement = this.querySelector('[data-sc-product-comparison-select]');
      this.scButtonElement = this.querySelector('[data-sc-product-comparison-button]');
      this.scInfoElement = this.querySelector('[data-sc-product-comparison-info]');
      this.scJsonElement = this.querySelector('[data-sc-product-comparison-json]');
      this.scProducts = this.scParseProducts();
      this.scBoundChangeHandler = this.scOnProductChange.bind(this);

      if (this.scSelectElement) {
        this.scSelectElement.addEventListener('change', this.scBoundChangeHandler);
        this.scUpdateCompareProduct(this.scSelectElement.value);
      }
    }

    disconnectedCallback() {
      if (this.scSelectElement && this.scBoundChangeHandler) {
        this.scSelectElement.removeEventListener('change', this.scBoundChangeHandler);
      }
    }

    scParseProducts() {
      if (!this.scJsonElement?.textContent) return [];

      try {
        const scProducts = JSON.parse(this.scJsonElement.textContent);
        return Array.isArray(scProducts) ? scProducts : [];
      } catch {
        return [];
      }
    }

    scOnProductChange(scEvent) {
      this.scUpdateCompareProduct(scEvent.target.value);
    }

    scUpdateCompareProduct(scProductId) {
      const scProduct = this.scProducts.find((scProductItem) => String(scProductItem.id) === String(scProductId));
      if (!scProduct) return;

      this.scUpdateButton(scProduct);
      this.scUpdateInfo(scProduct);
      this.scUpdateCharacteristics(scProduct);

      this.dispatchEvent(new CustomEvent('sc-product-comparison:change', {
        bubbles: true,
        detail: { product: scProduct },
      }));
    }

    scUpdateButton(scProduct) {
      if (!this.scButtonElement || !scProduct.url) return;

      this.scButtonElement.setAttribute('href', scProduct.url);
    }

    scUpdateInfo(scProduct) {
      if (!this.scInfoElement || typeof scProduct.infoText !== 'string') return;

      this.scInfoElement.textContent = scProduct.infoText;
    }

    scUpdateCharacteristics(scProduct) {
      if (!scProduct.characteristics || typeof scProduct.characteristics !== 'object') return;

      Object.entries(scProduct.characteristics).forEach(([scKey, scValue]) => {
        const scCharacteristicElement = this.querySelector(`[data-sc-product-comparison-characteristic="${scKey}"]`);
        if (!scCharacteristicElement) return;

        scCharacteristicElement.textContent = scValue ?? '';
      });
    }
  }

  customElements.define('sc-product-comparison', ScProductComparison);
}
/* SC End: Product Comparison */

/* SC Start: Mini Cart Items */
if (!customElements.get('mini-cart-items')) {
  const scQuantityDebounceMs = 20;

  class ScMiniCartItems extends HTMLElement {
    connectedCallback() {
      if (this.scListeners) return;
      this.scStates ||= new Map();
      const scDrawer = this.closest('cart-drawer');
      if (scDrawer) {
        ScMiniCartItems.scRememberTree(scDrawer);
        scDrawer.scItemsMembership ??= ScMiniCartItems.scMembership(scDrawer);
      }
      this.scListeners = new AbortController();
      const scOptions = { signal: this.scListeners.signal };
      this.addEventListener('click', this.scOnClick.bind(this), scOptions);
      // InputNumber dispatches a non-bubbling change when typing or using arrow keys.
      this.addEventListener('change', this.scOnChange.bind(this), { ...scOptions, capture: true });
      this.addEventListener('keydown', (scEvent) => {
        if (!scEvent.target.matches('[data-sc-cart-input]')) return;
        this.scArrowInput = ['ArrowUp', 'ArrowDown'].includes(scEvent.key) ? scEvent.target : null;
        queueMicrotask(() => { this.scArrowInput = null; });
      }, { ...scOptions, capture: true });
      this.closest('form')?.addEventListener('submit', this.scOnSubmit.bind(this), scOptions);
    }

    disconnectedCallback() {
      this.scListeners?.abort();
      this.scListeners = null;
    }

    static scSignature(scRow) {
      let scProperties;
      try {
        scProperties = JSON.parse(scRow.dataset.scCartProperties || '{}') || {};
      } catch {
        return null;
      }
      // Intelligems may change this property when recalculating discounts.
      const scEntries = Object.entries(scProperties)
        .filter(([scName]) => scName !== '_igLineItemDiscount')
        .sort(([scLeft], [scRight]) => scLeft.localeCompare(scRight));
      return JSON.stringify([scRow.dataset.scCartVariant, scRow.dataset.scCartPlan || '', scEntries]);
    }

    scState(scRow) {
      if (!this.scStates.has(scRow)) {
        const scQuantity = Number(scRow.querySelector('[data-sc-cart-input]')?.getAttribute('value') || 0);
        this.scStates.set(scRow, { key: scRow.dataset.scCartKey, confirmed: scQuantity, desired: scQuantity, revision: 0 });
      }
      return this.scStates.get(scRow);
    }

    scOnClick(scEvent) {
      const scControl = scEvent.target.closest('[data-sc-cart-decrease], [data-sc-cart-increase], [data-sc-cart-remove]');
      if (!scControl || scControl.closest('mini-cart-items') !== this) return;
      scEvent.preventDefault();
      if (!scControl.matches('a[href], button:not(:disabled)') || scControl.getAttribute('aria-disabled') === 'true') return;
      const scRow = scControl.closest('[data-sc-cart-key]');
      if (!scRow) return;
      const scState = this.scState(scRow);
      if (scState.desired === 0) return;
      const scQuantity = scControl.hasAttribute('data-sc-cart-remove') ? 0
        : scState.desired + (scControl.hasAttribute('data-sc-cart-increase') ? 1 : -1);
      this.scEnqueue(scRow, scQuantity, !scControl.hasAttribute('data-sc-cart-remove'));
    }

    scOnChange(scEvent) {
      if (!scEvent.target.matches('[data-sc-cart-input]')) return;
      const scRow = scEvent.target.closest('[data-sc-cart-key]');
      const scQuantity = Number(scEvent.target.value);
      if (scRow && scEvent.target.value.trim() && Number.isInteger(scQuantity)) this.scEnqueue(scRow, scQuantity, this.scArrowInput === scEvent.target);
      this.scArrowInput = null;
    }

    scEnqueue(scRow, scQuantity, scRelative = false) {
      const scState = this.scState(scRow);
      const scInput = scRow.querySelector('[data-sc-cart-input]');
      const scMax = scInput?.hasAttribute('max') ? Number(scInput.max) : Infinity;
      scQuantity = Math.max(0, scQuantity);
      if (scQuantity > scState.desired && scMax > 0) scQuantity = Math.min(scQuantity, scMax);
      // Only the counter is optimistic; prices and availability still come from Liquid.
      if (scInput) {
        if (scInput.value !== String(scQuantity)) scInput.value = String(scQuantity);
        scInput.size = Math.max(String(scQuantity).length, 2);
      }
      if (scQuantity === scState.desired) return;
      if (!scRelative) scState.relative = false;
      else if (scState.desired === scState.confirmed) scState.relative = true;
      scState.desired = scQuantity;
      scState.readyAt = performance.now() + scQuantityDebounceMs;
      scState.revision += 1;
      this.scFailed = false;
      this.scSetPending(scRow, true);
      this.scStartQueue();
    }

    scStartQueue() {
      if (this.scProcessing) return;
      const scDrawer = this.closest("cart-drawer");
      this.scProcessing = true;
      this.scWork = Promise.resolve().then(() => this.scRunQueue()).finally(async () => {
        this.scProcessing = false;
        this.scStates.forEach((scValue, scElement) => this.scSetPending(scElement, !this.scRecoveryBlocked && scValue.desired !== scValue.confirmed));
        await ScMiniCartItems.scFlushDeferred(scDrawer);
        if (this.isConnected && !this.scRecoveryBlocked && [...this.scStates].some(([scRow, scState]) => scRow.isConnected && scState.desired !== scState.confirmed)) {
          this.scStartQueue();
        }
      });
    }

    static scCoordinate(scTask) {
      ScMiniCartItems.scPendingMutations = (ScMiniCartItems.scPendingMutations || 0) + 1;
      const scWork = (ScMiniCartItems.scMutationTail || Promise.resolve()).then(scTask).finally(async () => {
        ScMiniCartItems.scPendingMutations -= 1;
        if (!ScMiniCartItems.scPendingMutations) await ScMiniCartItems.scFlushDeferred();
      });
      ScMiniCartItems.scMutationTail = scWork.catch(() => undefined);
      return scWork;
    }

    static async scFlushDeferred(scOnlyDrawer) {
      const scDrawers = scOnlyDrawer ? [scOnlyDrawer] : [...document.querySelectorAll("cart-drawer")];
      for (const scDrawer of scDrawers) {
        if (ScMiniCartItems.scPendingMutations || scDrawer.scItemsRecoveryBlocked || scDrawer.querySelector("mini-cart-items")?.scProcessing || !scDrawer.scDeferredItemsRefresh) continue;
        const scDetail = scDrawer.scDeferredItemsRefresh;
        delete scDrawer.scDeferredItemsRefresh;
        await ScMiniCartItems.renderDrawer(scDrawer, { detail: scDetail });
      }
    }

    static scPublish(scCart) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: scCart, source: 'mini-cart-items' } }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: scCart, scItemsApplied: true } }));
    }

    scRecover(scDrawer) {
      return ScMiniCartItems.scRecoverDrawer(scDrawer, { scItemsSource: this, scRebaseItems: !!scDrawer.scItemsRecoveryRebase });
    }

    static async scRecoverDrawer(scDrawer, scOptions = {}) {
      scDrawer.scItemsRecoveryBlocked = true;
      scDrawer.scItemsRecoveryRebase = !!scOptions.scRebaseItems;
      try {
        const scResponse = await fetch(`${window.themeVariables.routes.cartUrl}.js`, { cache: 'no-store', credentials: 'same-origin' });
        if (!scResponse.ok) return false;
        const scCart = await scResponse.json();
        if (!Array.isArray(scCart.items)) return false;
        if (!await ScMiniCartItems.renderDrawer(scDrawer, { detail: { ...scOptions, cart: scCart, scManagedMutation: true, scForceFetch: true } })) return false;
        ScMiniCartItems.scPublish(scCart);
        scDrawer.scItemsRecoveryBlocked = false;
        scDrawer.scItemsRecoveryRebase = false;
        const scItems = scDrawer.querySelector('mini-cart-items');
        if (scItems) scItems.scRecoveryBlocked = false;
        return true;
      } catch {
        return false;
      }
    }

    async scRunQueue() {
      const scDrawer = this.closest('cart-drawer');
      if (this.scRecoveryBlocked || scDrawer.scItemsRecoveryBlocked) {
        this.scRecoveryBlocked = !await ScMiniCartItems.scCoordinate(() => this.scRecover(scDrawer));
        if (this.scRecoveryBlocked) return;
      }
      while (this.isConnected) {
        const scPending = [...this.scStates].filter(([scRow, scState]) => scRow.isConnected && scState.desired !== scState.confirmed);
        if (!scPending.length) break;
        const scNow = performance.now();
        const scEntry = scPending.find(([, scState]) => (scState.readyAt || 0) <= scNow);
        if (!scEntry) {
          const scDelay = Math.min(...scPending.map(([, scState]) => scState.readyAt)) - scNow;
          await new Promise((scResolve) => setTimeout(scResolve, Math.max(1, scDelay)));
          continue;
        }
        const [scRow, scState] = scEntry;
        await ScMiniCartItems.scCoordinate(async () => {
          // A preceding add or recovery may have changed the key, quantity or row.
          if (this.scRecoveryBlocked || scDrawer.scItemsRecoveryBlocked) {
            this.scRecoveryBlocked = !await this.scRecover(scDrawer);
            if (this.scRecoveryBlocked) return;
          }
          if (!scRow.isConnected || scState.desired === scState.confirmed || scState.readyAt > performance.now()) return;
          const scQuantity = scState.desired;
          const scRevision = scState.revision;
          scDrawer.scItemsHaveMutated = true;
          scDrawer.scItemsRenderVersion = (scDrawer.scItemsRenderVersion || 0) + 1;
          try {
            const scResponse = await fetch(`${window.themeVariables.routes.cartChangeUrl}.js`, {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: JSON.stringify({ id: scState.key, quantity: scQuantity, sections: ['mini-cart'] }),
            });
            const scCart = await scResponse.json();
            if (!scResponse.ok || !Array.isArray(scCart.items)) throw new Error(scCart.description || scCart.message || 'The cart could not be updated. Please try again.');
            const scSignature = ScMiniCartItems.scSignature(scRow);
            let scItem = scCart.items.find((scCandidate) => scCandidate.key === scState.key);
            if (!scItem && scQuantity !== 0) {
              const scMatches = scCart.items.filter((scCandidate) => ScMiniCartItems.scSignature({ dataset: {
                scCartVariant: String(scCandidate.variant_id),
                scCartPlan: String(scCandidate.selling_plan_allocation?.selling_plan?.id || ''),
                scCartProperties: JSON.stringify(scCandidate.properties || {}),
              } }) === scSignature);
              const scOriginalMatches = [...this.querySelectorAll('line-item[data-sc-cart-key]')]
                .filter((scCandidate) => ScMiniCartItems.scSignature(scCandidate) === scSignature);
              if (scMatches.length !== 1 || scOriginalMatches.length !== 1) throw new Error('The cart has changed. Please check the quantities and try again.');
              scItem = scMatches[0];
            }
            scState.confirmed = scItem?.quantity || 0;
            if (scRevision === scState.revision) scState.desired = scState.confirmed;
            if (scItem) scState.key = scItem.key;
            if (!await ScMiniCartItems.renderDrawer(scDrawer, { detail: { cart: scCart, scItemsSource: this, scManagedMutation: true } })) throw new Error('The cart could not be updated. Please try again.');
            ScMiniCartItems.scPublish(scCart);
            this.scSetPending(scRow, scState.desired !== scState.confirmed);
          } catch (scError) {
            this.scFailed = true;
            ScMiniCartItems.scFailureVersion = (ScMiniCartItems.scFailureVersion || 0) + 1;
            // Cancel only this failed intent. Other rows and newer revisions survive.
            if (scRevision === scState.revision) {
              scState.desired = scState.confirmed;
              scState.relative = false;
            }
            const scInput = scRow.querySelector('[data-sc-cart-input]');
            if (scInput) {
              scInput.value = String(scState.desired);
              scInput.size = Math.max(scInput.value.length, 2);
            }
            this.scShowError(scError);
            // Read back an uncertain POST before sending another mutation.
            scDrawer.scItemsRecoveryRebase = false;
            this.scRecoveryBlocked = !await this.scRecover(scDrawer);
          }
        });
        if (this.scRecoveryBlocked) break;
      }
    }

    static async submitProductForm(scForm, scEvent) {
      scEvent.preventDefault();
      if (scForm.scCartSubmitting) return;
      if (!scForm.checkValidity()) {
        scForm.reportValidity();
        return;
      }
      const scDrawer = document.querySelector('cart-drawer');
      const scInsideDrawer = !!scForm.closest('.drawer');
      const scButtons = [...scForm.elements].filter((scButton) => scButton.type === 'submit');
      const scData = new FormData(scForm);
      scData.set('sections', 'mini-cart');
      ['option1', 'option2', 'option3'].forEach((scName) => scData.delete(scName));
      scForm.scCartSubmitting = true;
      scButtons.forEach((scButton) => {
        scButton.disabled = true;
        scButton.setAttribute('aria-busy', 'true');
      });
      try {
        await ScMiniCartItems.scCoordinate(async () => {
          let scAdded = false;
          let scAddAttempted = false;
          let scResponseJson;
          let scErrorMessage = '';
          scDrawer.scItemsHaveMutated = true;
          scDrawer.scItemsRenderVersion = (scDrawer.scItemsRenderVersion || 0) + 1;
          try {
            if (scDrawer.scItemsRecoveryBlocked && !await ScMiniCartItems.scRecoverDrawer(scDrawer, { scRebaseItems: !!scDrawer.scItemsRecoveryRebase })) {
              throw new Error("The cart could not be updated. Please try again.");
            }
            scAddAttempted = true;
            const scResponse = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
              method: 'POST', body: scData, headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });
            scResponseJson = await scResponse.json();
            if (!scResponse.ok) throw new Error(scResponseJson.description || scResponseJson.message || 'The cart could not be updated. Please try again.');
            scAdded = true;
            scForm.dispatchEvent(new CustomEvent('variant:added', {
              bubbles: true,
              detail: { variant: Object.prototype.hasOwnProperty.call(scResponseJson, 'items') ? scResponseJson.items[0] : scResponseJson },
            }));
            (scForm.onSuccessHandler || []).forEach((scHandler) => {
              try { scHandler(); } catch (scCallbackError) { console.error('[MiniCart] Product success callback failed', scCallbackError); }
            });
            // add.js returns added lines, so read the full cart once for existing subscribers.
            const scCartResponse = await fetch(`${window.themeVariables.routes.cartUrl}.js`, { cache: 'no-store', credentials: 'same-origin' });
            if (!scCartResponse.ok) throw new Error('The cart could not be updated. Please try again.');
            const scCart = await scCartResponse.json();
            if (!Array.isArray(scCart.items)) throw new Error('The cart could not be updated. Please try again.');
            scCart.sections = scResponseJson.sections;
            const scDetail = {
              cart: scCart, scManagedMutation: true, scRebaseItems: true,
              upsellPopupAvailable: window.themeVariables.settings.upsellPopupAvailable,
              openMiniCart: !scInsideDrawer && !window.themeVariables.settings.upsellPopupAvailable,
            };
            if (!await ScMiniCartItems.renderDrawer(scDrawer, { detail: scDetail })) throw new Error('The cart could not be updated. Please try again.');
            ScMiniCartItems.scPublish(scCart);
          } catch (scError) {
            scErrorMessage = scError.message;
            ScMiniCartItems.scFailureVersion = (ScMiniCartItems.scFailureVersion || 0) + 1;
            const scItems = scDrawer.querySelector('mini-cart-items');
            if (scItems) scItems.scShowError(scError);
            else if (window.toastr) window.toastr.error(scErrorMessage);
            // The add may already have committed. Never automatically repeat it.
            if (scAdded && scResponseJson?.sections?.['mini-cart']) {
              await ScMiniCartItems.renderDrawer(scDrawer, { detail: { cart: { sections: scResponseJson.sections }, scManagedMutation: true, scRebaseItems: true } });
            }
            const scRecovered = scAddAttempted && await ScMiniCartItems.scRecoverDrawer(scDrawer, { scRebaseItems: true });
            const scCurrentItems = scDrawer.querySelector("mini-cart-items");
            if (scCurrentItems) scCurrentItems.scRecoveryBlocked = !scRecovered;
          }
          scForm.dispatchEvent(new CustomEvent('cart-notification:show', {
            bubbles: true, cancelable: true, detail: { status: scAdded ? 'success' : 'error', error: scErrorMessage },
          }));
        });
      } finally {
        scForm.scCartSubmitting = false;
        scButtons.forEach((scButton) => {
          scButton.disabled = false;
          scButton.removeAttribute('aria-busy');
        });
      }
    }

    async scOnSubmit(scEvent) {
      const scDrawer = this.closest("cart-drawer");
      const scBlocked = this.scRecoveryBlocked || scDrawer.scItemsRecoveryBlocked;
      if (!this.scProcessing && !ScMiniCartItems.scPendingMutations && !scBlocked) return;
      scEvent.preventDefault();
      if (this.scSubmitting) return;
      this.scSubmitting = true;
      const scFailureVersion = ScMiniCartItems.scFailureVersion || 0;
      if (scBlocked && !this.scProcessing) this.scStartQueue();
      try {
        let scItems;
        do {
          scItems = scDrawer.querySelector('mini-cart-items') || this;
          await Promise.all([scItems.scWork, ScMiniCartItems.scMutationTail]);
        } while (scDrawer.querySelector('mini-cart-items')?.scProcessing || ScMiniCartItems.scPendingMutations);
        if (scItems.scFailed || scItems.scRecoveryBlocked || scDrawer.scItemsRecoveryBlocked || (ScMiniCartItems.scFailureVersion || 0) !== scFailureVersion) return;
        const scForm = scDrawer.querySelector('#mini-cart-form');
        const scButton = scDrawer.querySelector('[type="submit"][name="checkout"]');
        if (scForm) scForm.requestSubmit(scButton?.form === scForm ? scButton : undefined);
      } finally {
        this.scSubmitting = false;
      }
    }

    scSetPending(scRow, scPending) {
      scRow.toggleAttribute('data-sc-cart-pending', scPending);
      if (scPending) {
        if (scRow.getAttribute('aria-busy') !== 'true') scRow.setAttribute('aria-busy', 'true');
      } else scRow.removeAttribute('aria-busy');
      const scLoader = scRow.querySelector('.line-item__loader');
      if (!scLoader) return;
      if (scLoader.hidden !== !scPending) scLoader.hidden = !scPending;
      if (scLoader.firstElementChild && scLoader.firstElementChild.hidden !== !scPending) scLoader.firstElementChild.hidden = !scPending;
      if (scLoader.lastElementChild && !scLoader.lastElementChild.hidden) scLoader.lastElementChild.hidden = true;
    }

    scShowError(scError) {
      const scMessage = scError.message || 'The cart could not be updated. Please try again.';
      if (window.toastr) window.toastr.error(scMessage);
      else {
        let scAlert = this.querySelector('[data-sc-cart-error]');
        if (!scAlert) {
          scAlert = document.createElement('p');
          scAlert.setAttribute('data-sc-cart-error', '');
          scAlert.setAttribute('role', 'alert');
          this.prepend(scAlert);
        }
        scAlert.textContent = scMessage;
      }
    }

    static scSyncAttributes(scCurrent, scIncoming, scExcluded = []) {
      [...scCurrent.attributes].forEach((scAttribute) => {
        if (!scExcluded.includes(scAttribute.name) && !scIncoming.hasAttribute(scAttribute.name)) scCurrent.removeAttribute(scAttribute.name);
      });
      [...scIncoming.attributes].forEach((scAttribute) => {
        if (!scExcluded.includes(scAttribute.name) && scCurrent.getAttribute(scAttribute.name) !== scAttribute.value) scCurrent.setAttribute(scAttribute.name, scAttribute.value);
      });
    }

    scPatchRow(scRow, scIncoming, scRebase = false) {
      const scState = this.scState(scRow);
      const scServerQuantity = Number(scIncoming.querySelector('[data-sc-cart-input]')?.value || 0);
      if (scState.desired === scState.confirmed) scState.desired = scServerQuantity;
      else if (scRebase && scState.relative && scState.desired !== 0) {
        scState.desired = Math.max(0, scServerQuantity + scState.desired - scState.confirmed);
      }
      scState.confirmed = scServerQuantity;
      scState.key = scIncoming.dataset.scCartKey;
      ScMiniCartItems.scSyncAttributes(scRow, scIncoming, ['aria-busy', 'data-sc-cart-pending', 'style']);
      const scImage = scRow.querySelector('.line-item__image-wrapper');
      const scNextImage = scIncoming.querySelector('.line-item__image-wrapper');
      if (scImage && scNextImage) {
        ScMiniCartItems.scSyncAttributes(scImage, scNextImage);
        const scPicture = scImage.querySelector('.line-item__image');
        const scNextPicture = scNextImage.querySelector('.line-item__image');
        if (scPicture && scNextPicture && !scPicture.isEqualNode(scNextPicture)) scPicture.replaceWith(scNextPicture.cloneNode(true));
      }
      ['meta', 'price', 'discounts'].forEach((scName) => {
        const scCurrentBlock = scRow.querySelector(`[data-sc-cart-block="${scName}"]`);
        const scNewBlock = scIncoming.querySelector(`[data-sc-cart-block="${scName}"]`);
        if (scCurrentBlock && scNewBlock) {
          ScMiniCartItems.scPatchNode(scCurrentBlock, scNewBlock);
        } else if (scCurrentBlock) scCurrentBlock.remove();
        else if (scNewBlock) scRow.querySelector('[data-sc-cart-controls]').before(scNewBlock.cloneNode(true));
      });
      const scInput = scRow.querySelector('[data-sc-cart-input]');
      const scNextInput = scIncoming.querySelector('[data-sc-cart-input]');
      if (scInput && scNextInput) {
        const scKeepValue = scState.desired !== scState.confirmed;
        const scFocused = document.activeElement === scInput;
        const scStart = scInput.selectionStart, scEnd = scInput.selectionEnd;
        ScMiniCartItems.scSyncAttributes(scInput, scNextInput, ['value']);
        if (scInput.getAttribute('value') !== scNextInput.getAttribute('value')) scInput.setAttribute('value', scNextInput.getAttribute('value'));
        const scValue = scKeepValue ? String(scState.desired) : scNextInput.value;
        if (scInput.value !== scValue) scInput.value = scValue;
        const scSize = Math.max(scValue.length, 2);
        if (scKeepValue && scInput.size !== scSize) scInput.size = scSize;
        if (scFocused) scInput.setSelectionRange(scStart, scEnd);
      }
      ['decrease', 'increase', 'remove'].forEach((scName) => {
        const scControl = scRow.querySelector(`[data-sc-cart-${scName}]`);
        const scNext = scIncoming.querySelector(`[data-sc-cart-${scName}]`);
        if (!scControl || !scNext) return;
        if (scControl.tagName !== scNext.tagName) scControl.replaceWith(scNext.cloneNode(true));
        else {
          ScMiniCartItems.scSyncAttributes(scControl, scNext);
          if (scControl.innerHTML !== scNext.innerHTML) scControl.innerHTML = scNext.innerHTML;
        }
      });
      this.scSetPending(scRow, scState.desired !== scState.confirmed);
    }

    scApplyItems(scIncoming, scRebase = false) {
      const scOldRows = [...this.querySelectorAll('line-item[data-sc-cart-key]')];
      const scNewRows = [...scIncoming.querySelectorAll('line-item[data-sc-cart-key]')];
      const scUsed = new Set();
      const scRows = new Map();
      scNewRows.forEach((scNext) => {
        let scMatch = scOldRows.find((scRow) => !scUsed.has(scRow) && scRow.dataset.scCartKey === scNext.dataset.scCartKey);
        if (!scMatch) {
          const scSignature = ScMiniCartItems.scSignature(scNext);
          const scCandidates = scOldRows.filter((scRow) => !scUsed.has(scRow) && ScMiniCartItems.scSignature(scRow) === scSignature);
          const scIncomingMatches = scNewRows.filter((scRow) => ScMiniCartItems.scSignature(scRow) === scSignature);
          if (scSignature && scCandidates.length === 1 && scIncomingMatches.length === 1) scMatch = scCandidates[0];
        }
        if (scMatch) {
          scUsed.add(scMatch);
          this.scPatchRow(scMatch, scNext, scRebase);
          scRows.set(scNext, scMatch);
        } else scRows.set(scNext, scNext.cloneNode(true));
      });
      scOldRows.forEach((scRow) => {
        if (scUsed.has(scRow)) return;
        const scState = this.scStates.get(scRow);
        if (scState && scState.desired !== scState.confirmed) {
          this.scFailed = true;
          this.scShowError(new Error('The cart has changed. Please check the quantities and try again.'));
        }
        this.scStates.delete(scRow);
      });
      // Walk in server order. Existing rows stay connected when already in place.
      let scCursor = this.firstChild;
      [...scIncoming.childNodes].forEach((scNext) => {
        const scDesired = scRows.get(scNext);
        if (scDesired) {
          while (scCursor && scCursor !== scDesired && !scUsed.has(scCursor)) {
            const scRemove = scCursor; scCursor = scCursor.nextSibling; scRemove.remove();
          }
          if (scCursor === scDesired) scCursor = scCursor.nextSibling;
          else this.insertBefore(scDesired, scCursor);
        } else if (scCursor && !scUsed.has(scCursor) && scCursor.isEqualNode(scNext)) scCursor = scCursor.nextSibling;
        else this.insertBefore(scNext.cloneNode(true), scCursor);
      });
      while (scCursor) {
        const scRemove = scCursor; scCursor = scCursor.nextSibling; scRemove.remove();
      }
    }

    static scRememberTree(scNode, scFromServer = false) {
      ScMiniCartItems.scServerNodes ||= new WeakMap();
      if (ScMiniCartItems.scServerNodes.has(scNode)) return;
      const scState = { text: scNode.nodeValue, fromServer: scFromServer };
      if (scNode.nodeType === Node.ELEMENT_NODE) {
        scState.attributes = new Map([...scNode.attributes].map(scAttribute => [scAttribute.name, scAttribute.value]));
        // KEK.saveXtampons owns this title marker together with its injected info block.
        // It may run before our first snapshot; never treat its marker as Liquid markup.
        if (scNode.matches('.product-item-meta__title.kk-sp9-checked') && scNode.closest('line-item[data-sc-cart-key]')) {
          const scClasses = (scState.attributes.get('class') || '').split(/\s+/).filter(scClass => scClass && scClass !== 'kk-sp9-checked').join(' ');
          if (scClasses) scState.attributes.set('class', scClasses);
          else scState.attributes.delete('class');
        }
        if (scNode.localName.includes('-') || scNode.hasAttribute('is')) scState.template = scNode.outerHTML;
      }
      ScMiniCartItems.scServerNodes.set(scNode, scState);
      [...scNode.childNodes].forEach(scChild => ScMiniCartItems.scRememberTree(scChild, scFromServer));
    }

    static scProgressTemplate(scNode) {
      const scClone = scNode.cloneNode(true);
      const scElements = [scClone, ...scClone.querySelectorAll('*')];
      scElements.forEach(scElement => {
        ['completed', 'in-progress', 'd-none', 'upselling-progress--all-bars-completed'].forEach(scClass => scElement.classList.remove(scClass));
        if (!scElement.getAttribute('class')) scElement.removeAttribute('class');
        scElement.style?.removeProperty('--progress-step-fill-percentage');
        if (scElement.hasAttribute('style')) {
          const scStyle = scElement.style.cssText;
          if (scStyle) scElement.setAttribute('style', scStyle);
          else scElement.removeAttribute('style');
        }
        [...scElement.attributes].forEach(scAttribute => {
          if (/^(?:data-ly-|ly-)/.test(scAttribute.name)) scElement.removeAttribute(scAttribute.name);
        });
        if (scElement.matches('cart-total-progress, panty-bulk-discount-progress, panty-bulk-discount')) scElement.removeAttribute('hidden');
        if (scElement.matches('[data-progress-step-message]')) scElement.textContent = '';
      });
      scClone.removeAttribute('hidden');
      return scClone.outerHTML;
    }

    static scInitialComponentMatches(scCurrent, scIncoming) {
      if (scCurrent.matches('cart-total-progress, panty-bulk-discount-progress, panty-bulk-discount, upselling-progress')) {
        return ScMiniCartItems.scProgressTemplate(scCurrent) === ScMiniCartItems.scProgressTemplate(scIncoming);
      }
      if (scCurrent.localName === 'cart-drawer-recommendations') {
        return ['product-id', 'section-id'].every(scName => scCurrent.getAttribute(scName) === scIncoming.getAttribute(scName));
      }
      if (scCurrent.localName.startsWith('ig-')) return true;
      return false;
    }

    static scNodeKey(scNode) {
      if (scNode.nodeType !== Node.ELEMENT_NODE) return null;
      if (scNode.id) return `${scNode.localName}#${scNode.id}`;
      if (scNode.localName.includes('-') || scNode.hasAttribute('is')) return `${scNode.localName}:${scNode.getAttribute('is') || ''}`;
      const scAttributes = ScMiniCartItems.scServerNodes?.get(scNode)?.attributes;
      const scClass = (scAttributes ? scAttributes.get('class') || '' : scNode.getAttribute('class') || '').split(/\s+/)[0];
      return `${scNode.localName}:${scNode.getAttribute('is') || ''}:${scNode.getAttribute('data-sc-cart-block') || scClass}`;
    }

    static scPatchAttributes(scCurrent, scIncoming) {
      const scPrevious = ScMiniCartItems.scServerNodes.get(scCurrent);
      const scNext = new Map([...scIncoming.attributes].map(scAttribute => [scAttribute.name, scAttribute.value]));
      // Compare Liquid snapshots, so runtime classes, translations and widget state survive.
      for (const [scName, scValue] of scPrevious.attributes) {
        if (!scNext.has(scName) && scCurrent.getAttribute(scName) === scValue) scCurrent.removeAttribute(scName);
      }
      for (const [scName, scValue] of scNext) {
        if (scPrevious.attributes.get(scName) === scValue) continue;
        if (scName === 'class') {
          const scOldClasses = new Set((scPrevious.attributes.get('class') || '').split(/\s+/).filter(Boolean));
          const scNewClasses = new Set(scValue.split(/\s+/).filter(Boolean));
          scOldClasses.forEach(scClass => { if (!scNewClasses.has(scClass)) scCurrent.classList.remove(scClass); });
          scNewClasses.forEach(scClass => { if (!scCurrent.classList.contains(scClass)) scCurrent.classList.add(scClass); });
        } else if (scCurrent.getAttribute(scName) !== scValue) scCurrent.setAttribute(scName, scValue);
      }
      scPrevious.attributes = scNext;
    }

    static scInvalidateRecommendations(scCurrent, scIncoming) {
      const scCache = customElements.get('cart-drawer-recommendations')?.recommendationsCache || scCurrent.constructor.recommendationsCache;
      if (!scCache) return;
      for (const scUrl of Object.keys(scCache)) {
        const scParams = new URL(scUrl, window.location.href).searchParams;
        if (scParams.get('product_id') === scIncoming.getAttribute('product-id') && scParams.get('section_id') === scIncoming.getAttribute('section-id')) delete scCache[scUrl];
      }
    }

    static scIsCartItemClientNode(scNode) {
      return scNode.nodeType === Node.ELEMENT_NODE
        && scNode.matches('.kk-sp9-cartitem-info, #kk-sp9-cartitem-info, kk-sp9-cartitem-info')
        && !!scNode.closest('line-item[data-sc-cart-key]');
    }

    static scPatchNode(scCurrent, scIncoming, scOptions = {}) {
      // This product-info widget is injected by an integration, outside Liquid's markup.
      if (ScMiniCartItems.scIsCartItemClientNode(scCurrent)) return scCurrent;
      ScMiniCartItems.scRememberTree(scCurrent);
      const scPrevious = ScMiniCartItems.scServerNodes.get(scCurrent);
      if (scCurrent.nodeType !== Node.ELEMENT_NODE) {
        if (scPrevious.text !== scIncoming.nodeValue && scCurrent.nodeValue !== scIncoming.nodeValue) scCurrent.nodeValue = scIncoming.nodeValue;
        scPrevious.text = scIncoming.nodeValue;
        return scCurrent;
      }
      if (scPrevious.template !== undefined) {
        const scTemplate = scIncoming.outerHTML;
        const scRecommendations = scCurrent.localName === 'cart-drawer-recommendations';
        const scInitialMatch = !scPrevious.fromServer && ScMiniCartItems.scInitialComponentMatches(scCurrent, scIncoming);
        if ((scPrevious.template !== scTemplate && !scInitialMatch) || (scRecommendations && scOptions.membershipChanged)) {
          if (scRecommendations) ScMiniCartItems.scInvalidateRecommendations(scCurrent, scIncoming);
          const scReplacement = scIncoming.cloneNode(true);
          ScMiniCartItems.scRememberTree(scReplacement, true);
          scCurrent.replaceWith(scReplacement);
          return scReplacement;
        }
        scPrevious.template = scTemplate;
        scPrevious.fromServer = true;
        return scCurrent;
      }
      ScMiniCartItems.scPatchAttributes(scCurrent, scIncoming);
      ScMiniCartItems.scPatchChildren(scCurrent, scIncoming, scOptions);
      return scCurrent;
    }

    static scPatchChildren(scCurrent, scIncoming, scOptions = {}, scPreserved, scNextPreserved) {
      const scOldChildren = [...scCurrent.childNodes];
      const scClientNodes = new Set(scOldChildren.filter(scNode => ScMiniCartItems.scIsCartItemClientNode(scNode)));
      const scUsed = new Set();
      const scPlan = [...scIncoming.childNodes].map(scNext => {
        const scKey = ScMiniCartItems.scNodeKey(scNext);
        const scMatch = scNext === scNextPreserved ? scPreserved : scOldChildren.find(scChild =>
          scChild !== scPreserved && !scUsed.has(scChild)
          && (!scClientNodes.has(scChild) || ScMiniCartItems.scIsCartItemClientNode(scNext))
          && scChild.nodeType === scNext.nodeType && scChild.nodeName === scNext.nodeName && ScMiniCartItems.scNodeKey(scChild) === scKey
        );
        if (scMatch) scUsed.add(scMatch);
        return { incoming: scNext, current: scMatch };
      });
      scOldChildren.forEach(scChild => { if (!scUsed.has(scChild) && !scClientNodes.has(scChild)) scChild.remove(); });
      let scCursor = scCurrent.firstChild;
      for (const scEntry of scPlan) {
        // Keep the widget in place between the existing Liquid siblings, without reconnecting it.
        while (scClientNodes.has(scCursor) && scCursor !== scEntry.current) scCursor = scCursor.nextSibling;
        let scNode = scEntry.current;
        if (!scNode) {
          scNode = scEntry.incoming.cloneNode(true);
          ScMiniCartItems.scRememberTree(scNode, true);
        }
        if (scNode !== scCursor) scCurrent.insertBefore(scNode, scCursor);
        if (scEntry.current && scNode !== scPreserved) scNode = ScMiniCartItems.scPatchNode(scNode, scEntry.incoming, scOptions);
        scCursor = scNode.nextSibling;
      }
    }

    static scReplaceAround(scCurrent, scIncoming, scPreserved, scNextPreserved, scOptions = {}) {
      ScMiniCartItems.scRememberTree(scCurrent);
      ScMiniCartItems.scPatchAttributes(scCurrent, scIncoming);
      ScMiniCartItems.scPatchChildren(scCurrent, scIncoming, scOptions, scPreserved, scNextPreserved);
    }

    static scMembership(scDrawer) {
      return JSON.stringify([...new Set([...scDrawer.querySelectorAll('line-item[data-sc-cart-variant]')].map(scRow => scRow.dataset.scCartVariant))].sort());
    }

    static scProductMembership(scCart) {
      if (!Array.isArray(scCart?.items)) return null;
      return JSON.stringify([...new Set(scCart.items.map(scItem => String(scItem.product_id || scItem.variant_id || scItem.id)))].sort());
    }

    static async renderDrawer(scDrawer, scEvent = {}) {
      const scDetail = scEvent.detail || {};
      if (scDetail.scItemsApplied) return true;
      const scItems = scDrawer.querySelector('mini-cart-items');
      const scOwn = scDetail.scManagedMutation || scDetail.scItemsSource === scItems;
      if ((scItems?.scProcessing || ScMiniCartItems.scPendingMutations || scDrawer.scItemsRecoveryBlocked) && !scOwn) {
        scDrawer.scDeferredItemsRefresh = { ...scDrawer.scDeferredItemsRefresh, ...scDetail };
        return true;
      }
      const scVersion = scDrawer.scItemsRenderVersion = (scDrawer.scItemsRenderVersion || 0) + 1;
      try {
        let scHtml = scDetail.cart?.sections?.['mini-cart'];
        // ProductForm can publish an old section a second after adding an item.
        // After local edits, external refreshes must read current HTML instead.
        if (!scHtml || scDetail.scForceFetch || (!scOwn && scDrawer.scItemsHaveMutated)) {
          const scResponse = await fetch(`${window.themeVariables.routes.cartUrl}?section_id=${encodeURIComponent(scDrawer.getAttribute('section') || 'mini-cart')}`, { cache: 'no-store', credentials: 'same-origin' });
          if (!scResponse.ok) throw new Error('The cart could not be updated. Please try again.');
          scHtml = await scResponse.text();
        }
        if (scVersion !== scDrawer.scItemsRenderVersion) return false;
        if ((scItems?.scProcessing || ScMiniCartItems.scPendingMutations || scDrawer.scItemsRecoveryBlocked) && !scOwn) {
          scDrawer.scDeferredItemsRefresh = { ...scDrawer.scDeferredItemsRefresh, ...scDetail };
          return true;
        }
        const scDocument = new DOMParser().parseFromString(scHtml, 'text/html');
        const scIncoming = scDocument.querySelector('cart-drawer');
        const scContent = scDrawer.querySelector('.drawer__content');
        const scNextContent = scIncoming?.querySelector('.drawer__content');
        if (!scIncoming || !scContent || !scNextContent) throw new Error('The cart could not be updated. Please try again.');
        const scForm = scDrawer.querySelector('#mini-cart-form');
        const scNextForm = scIncoming.querySelector('#mini-cart-form');
        const scNextItems = scIncoming.querySelector('mini-cart-items');
        const scScroll = scContent.scrollTop;
        const scMembership = ScMiniCartItems.scMembership(scIncoming);
        const scProductMembership = ScMiniCartItems.scProductMembership(scDetail.cart);
        const scPreviousProducts = scDrawer.scItemsProductMembership ?? ScMiniCartItems.scProductMembership(window.ShopifyCart?._current);
        const scPatchOptions = { membershipChanged: scDrawer.scItemsMembership !== scMembership
          || (scProductMembership !== null && scPreviousProducts !== null && scProductMembership !== scPreviousProducts) };
        if (scItems && scNextItems && scForm && scNextForm) {
          scItems.scApplyItems(scNextItems, scDetail.scRebaseItems);
          ScMiniCartItems.scReplaceAround(scForm, scNextForm, scItems, scNextItems, scPatchOptions);
          ScMiniCartItems.scReplaceAround(scContent, scNextContent, scForm, scNextForm, scPatchOptions);
          ScMiniCartItems.scReplaceAround(scDrawer, scIncoming, scContent, scNextContent, scPatchOptions);
          scContent.scrollTop = scScroll;
        } else {
          // Empty/non-empty is a structural transition, with no rows to preserve.
          const scChildren = [...scIncoming.childNodes].map(scNode => scNode.cloneNode(true));
          if (scPatchOptions.membershipChanged) {
            scChildren.forEach(scNode => {
              if (scNode.nodeType !== Node.ELEMENT_NODE) return;
              const scRecommendations = [...scNode.querySelectorAll('cart-drawer-recommendations')];
              if (scNode.matches('cart-drawer-recommendations')) scRecommendations.push(scNode);
              scRecommendations.forEach(scElement => ScMiniCartItems.scInvalidateRecommendations(scElement, scElement));
            });
          }
          scChildren.forEach(scNode => ScMiniCartItems.scRememberTree(scNode, true));
          scDrawer.replaceChildren(...scChildren);
        }
        scDrawer.scItemsMembership = scMembership;
        if (scProductMembership !== null) scDrawer.scItemsProductMembership = scProductMembership;
        scDrawer.nextReplacementDelay = 0;
        if (scDetail.openMiniCart) scDrawer.open = true;
        if (scDetail.upsellPopupAvailable) document.documentElement.dispatchEvent(new CustomEvent('upsell-popup:open'));
        return true;
      } catch (scError) {
        if (scVersion === scDrawer.scItemsRenderVersion) {
          if (scItems) scItems.scShowError(scError);
          else if (window.toastr) window.toastr.error(scError.message);
        }
        return false;
      }
    }
  }

  customElements.define('mini-cart-items', ScMiniCartItems);
  window.ScMiniCartItems = ScMiniCartItems;
}
/* SC End: Mini Cart Items */

/* SC End */

/* SC Start: Keep Influencer Params On Filtering */
// Focal rebuilds the URL from the filter form on filtering/sorting/paging, which drops the influencer
// parameters (code block, heading, tracking). Capture runs before Focal's handler and adds them back.
(function () {
  const scKeepParamKeys = ['view', 'discount_code', 'discount_amount', 'inf_name', 'image_url'];
  const scKeepParamPrefixes = ['utm_', 'trc_inf_'];

  function scShouldKeepParam(scKey) {
    return scKeepParamKeys.includes(scKey) || scKeepParamPrefixes.some((scPrefix) => scKey.startsWith(scPrefix));
  }

  function scKeepInfluencerParams(scEvent) {
    if (!scEvent.detail || typeof scEvent.detail.url !== 'string') return;

    const scCurrentParams = new URL(window.location.href).searchParams;
    const scNextUrl = new URL(scEvent.detail.url, window.location.href);
    let scChanged = false;

    scCurrentParams.forEach((scValue, scKey) => {
      if (scShouldKeepParam(scKey) && !scNextUrl.searchParams.has(scKey)) {
        scNextUrl.searchParams.set(scKey, scValue);
        scChanged = true;
      }
    });

    if (scChanged) {
      scEvent.detail.url = scNextUrl.toString();
    }
  }

  ['facet:criteria-changed', 'pagination:page-changed'].forEach((scEventName) => {
    document.addEventListener(scEventName, scKeepInfluencerParams, true);
  });
})();
/* SC End: Keep Influencer Params On Filtering */
